import React, { useState, useEffect } from 'react';
import { BillingReadinessItem, Claim, Payment, RemittanceArtifact, UserRole, Client } from '../types';
import { BillingHoldResolvePanel } from './BillingHoldResolvePanel';
import { readDisplaySync, SAVED_DISPLAY_WARNING } from '../savedDisplaySync';
import { billingRateSubtitle } from '../utils/billingRateDisplay';
import {
  CircleDollarSign,
  CheckCircle2,
  AlertTriangle,
  FileCheck,
  RefreshCw,
  Eye,
  CreditCard,
  ShieldCheck,
  ShieldAlert,
  X,
  Clock,
  ArrowRight,
  Download,
  UploadCloud,
  FileText,
  Layers,
  FileSpreadsheet,
  Hash,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  Filter,
  Info,
  Users,
  Link2,
  Unlink,
  ExternalLink,
  Scissors,
  Plus,
  Search,
  FileUp,
  AlertCircle,
  Archive,
  Database,
  Lock,
  Sparkles
} from 'lucide-react';
import { SecondaryCobPreviewModal, SecondaryCobPreviewData } from './SecondaryCobPreviewModal';

export interface EobSourceBatch {
  id: string;
  filename: string;
  mime_type: string;
  byte_size: number;
  file_hash: string;
  uploaded_by: string;
  uploaded_at: string;
  received_date: string;
  source: string;
  page_count: number;
  processing_status: 'UPLOADED' | 'REVIEW_PENDING' | 'REVIEWED' | 'PARTIALLY_PROCESSED' | 'COMPLETE';
  payer_hint?: string;
  payment_date_hint?: string;
  total_payment_hint?: number;
  trace_number_hint?: string;
  notes?: string;
  derived_eob_count: number;
}

export interface AttachedClient {
  clientId: string;
  clientName: string;
  ahcccsId?: string;
  policyId?: string;
  attachedAt: string;
  attachedBy: string;
}

export interface CanonicalEob {
  id: string;
  source_batch_id?: string;
  source_page_start: number;
  source_page_end: number;
  payer_name: string;
  received_date: string;
  payment_date?: string;
  total_payment_amount: number;
  check_or_eft_number?: string;
  filename: string;
  mime_type: string;
  file_size: number;
  file_hash: string;
  uploaded_by: string;
  created_at: string;
  notes?: string;
  source: string;
  reconciliation_status: 'UNMATCHED' | 'PARTIAL' | 'RECONCILED';
  review_status: 'NEEDS_REVIEW' | 'VERIFIED';
  payment_header_id?: string;
  source_batch_filename?: string;
  source_batch_page_count?: number;
  payment_header_amount?: number;
  payment_header_check_number?: string;
  payment_header_method?: string;
  payment_header_status?: string;
  payment_header_date?: string;
  adjudications_count?: number;
  attached_clients: AttachedClient[];
}

export interface ClaimAdjudication {
  id: string;
  claim_id: string;
  canonical_eob_id: string;
  payment_header_id?: string;
  service_id: string;
  service_line_number: number;
  cpt_code?: string;
  dos?: string;
  payer_name: string;
  payer_sequence: 'PRIMARY' | 'SECONDARY' | 'TERTIARY';
  adjudication_date: string;
  adjudication_outcome: 'PAID' | 'PARTIAL' | 'DENIED' | 'REVERSED' | 'CORRECTION';
  payer_control_number?: string;
  billed_amount: number;
  allowed_amount: number;
  paid_amount: number;
  contractual_adjustment: number;
  patient_responsibility: number;
  remaining_secondary_balance: number;
  group_code?: string;
  reason_code?: string;
  carc_code?: string;
  rarc_code?: string;
  denial_reason?: string;
  tpl_evidence_status: 'PENDING' | 'VALID_PAYMENT' | 'VALID_DENIAL' | 'INSUFFICIENT_EVIDENCE';
  reversal_of_adjudication_id?: string;
  notes?: string;
  created_by: string;
  created_at: string;
  // Joins
  claim_number?: string;
  claim_billed_amount?: number;
  claim_status?: string;
  billing_route?: string;
  first_name?: string;
  last_name?: string;
  ahcccs_id?: string;
  policy_id?: string;
  eob_filename?: string;
  eob_payer?: string;
  eob_check_number?: string;
  eob_review_status?: string;
  source_page_start?: number;
  source_page_end?: number;
  payment_header_check?: string;
  payment_header_total?: number;
  payment_header_method?: string;
}

export interface PaymentHeader {
  id: string;
  payer_name: string;
  payment_date: string;
  total_payment_amount: number;
  check_or_eft_number?: string;
  payment_method: 'EFT' | 'CHECK' | 'VIRTUAL_CARD' | 'ACH' | 'OTHER';
  status: 'POSTED' | 'UNALLOCATED' | 'PARTIALLY_ALLOCATED' | 'FULLY_ALLOCATED';
  notes?: string;
  created_by: string;
  created_at: string;
  linked_eob_count: number;
}

interface BillingBatch {
  id: string;
  batch_number: string;
  billing_route: string;
  filename: string;
  file_hash: string;
  claim_count: number;
  total_charges: number;
  dos_start: string;
  dos_end: string;
  edi_837_content: string;
  status: string;
  sender_id: string;
  payer_id: string;
  payer_name: string;
  generated_at: string;
}

interface BillingConsoleViewProps {
  userRole: UserRole;
  initialTab?: 'Readiness' | 'Claims' | 'Payments' | 'Remittances' | 'HistoricalVault';
  onRefreshStats: () => void;
  onSelectClient: (clientId: string) => void;
  onNavigateTab?: (tab: string, subTab?: string) => void;
  batchCacheStaleWarning?: string | null;
  onBatchCacheStaleWarning?: (warning: string) => void;
}

export const BillingConsoleView: React.FC<BillingConsoleViewProps> = ({
  userRole,
  initialTab = 'Readiness',
  onRefreshStats,
  onSelectClient,
  onNavigateTab,
  batchCacheStaleWarning = null,
  onBatchCacheStaleWarning
}) => {
  const [readinessItems, setReadinessItems] = useState<BillingReadinessItem[]>([]);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [artifacts, setArtifacts] = useState<RemittanceArtifact[]>([]);
  const [batches, setBatches] = useState<BillingBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'Readiness' | 'Claims' | 'Payments' | 'Remittances' | 'HistoricalVault'>(initialTab);
  const [historicalVaultSummary, setHistoricalVaultSummary] = useState<{
    artifactsCount: number;
    retrievalsCount: number;
    candidateAnchorsCount: number;
    transportAcksCount: number;
    lineageAssertionsCount: number;
    assertionsByStatus: Record<string, number>;
  }>({
    artifactsCount: 0,
    retrievalsCount: 0,
    candidateAnchorsCount: 0,
    transportAcksCount: 0,
    lineageAssertionsCount: 0,
    assertionsByStatus: {}
  });
  const [historicalSubTab, setHistoricalSubTab] = useState<'SUMMARY' | 'EDI_PREVIEW' | 'ARTIFACTS' | 'ANCHORS' | 'TRANSPORT' | 'ASSERTIONS'>('SUMMARY');
  const [ediPreviewLoading, setEdiPreviewLoading] = useState(false);
  const [ediPreviewResult, setEdiPreviewResult] = useState<any | null>(null);
  const [ediInput837, setEdiInput837] = useState('');
  const [ediInputTa1, setEdiInputTa1] = useState('');
  const [ediInput999, setEdiInput999] = useState('');
  const [activeEdiTab, setActiveEdiTab] = useState<'837' | 'TA1' | '999'>('837');
  const [showRawInputs, setShowRawInputs] = useState(false);
  const [sampleNotes, setSampleNotes] = useState<string | null>(null);

  const handleLoadSampleTestCase = async () => {
    setEdiPreviewLoading(true);
    try {
      const res = await fetch('/api/historical-vault/edi-sample');
      if (!res.ok) throw new Error('Failed to load sample');
      const data = await res.json();
      setEdiInput837(data.raw837 || '');
      setEdiInputTa1(data.rawTa1 || '');
      setEdiInput999(data.raw999 || '');
      setSampleNotes(data.description || 'Authorized WellSky Historical Test Case');
      await runEdiPreview({
        raw837Text: data.raw837,
        rawTa1Text: data.rawTa1,
        raw999Text: data.raw999
      });
    } catch (err: any) {
      console.error('Error loading sample:', err);
    } finally {
      setEdiPreviewLoading(false);
    }
  };

  const runEdiPreview = async (overridePayload?: any) => {
    setEdiPreviewLoading(true);
    try {
      const payload = overridePayload || {
        raw837Text: ediInput837 || undefined,
        rawTa1Text: ediInputTa1 || undefined,
        raw999Text: ediInput999 || undefined
      };
      const res = await fetch('/api/historical-vault/edi-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setEdiPreviewResult(data.preview);
    } catch (err: any) {
      console.error('Failed to run EDI preview:', err);
    } finally {
      setEdiPreviewLoading(false);
    }
  };

  // Filter and selection state for Billing Readiness Working Queue
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [routeFilter, setRouteFilter] = useState<'ALL' | 'WELLSKY_DDD' | 'CLAIMMD_COMMERCIAL' | 'CLAIMMD_PRIMARY_DDD_SECONDARY'>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'READY' | 'HOLD' | 'WELLSKY_REVIEW' | 'CLOSED'>('ALL');
  const [resolveServiceId, setResolveServiceId] = useState<string | null>(null);

  // Filter state for Claims & 837 EDI History
  const [batchRouteFilter, setBatchRouteFilter] = useState<'ALL' | 'WELLSKY_DDD' | 'CLAIMMD_COMMERCIAL' | 'CLAIMMD_PRIMARY_DDD_SECONDARY'>('ALL');
  const [expandedBatchIds, setExpandedBatchIds] = useState<string[]>([]);

  // Modals
  const [inspectClaim, setInspectClaim] = useState<Claim | null>(null);
  const [inspectBatch, setInspectBatch] = useState<BillingBatch | null>(null);
  const [copiedHash, setCopiedHash] = useState(false);

  // Batch Generation Preview & Confirmation Modal
  const [batchPreview, setBatchPreview] = useState<{
    batchId: string;
    batchNumber: string;
    billingRoute: string;
    claimCount: number;
    clients: string[];
    dosRange: string;
    totalCharges: number;
    sender: string;
    payer: string;
    filename: string;
    generatedAt: string;
    fileHash: string;
    ediContent: string;
  } | null>(null);

  // Replacement Claim Modal (Frequency 7)
  const [showReplacementModal, setShowReplacementModal] = useState(false);
  const [selectedClaimForReplacement, setSelectedClaimForReplacement] = useState<Claim | null>(null);
  const [replacementForm, setReplacementForm] = useState({
    payerControlNumber: '',
    resubmissionReason: 'Corrected CPT/Modifier',
    claimFrequencyCode: '7'
  });

  // Post Payment / EOB Modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedClaimForPayment, setSelectedClaimForPayment] = useState<Claim | null>(null);
  const [selectedServiceForPayment, setSelectedServiceForPayment] = useState<BillingReadinessItem | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    claimId: '',
    serviceId: '',
    payerName: 'Arizona DDD',
    clientName: '',
    dos: '',
    serviceCode: '92507',
    amountBilled: 65.0,
    amountPaid: 55.25,
    allowedAmount: 55.25,
    patientResp: 0.0,
    deductible: 0.0,
    coinsurance: 0.0,
    copay: 0.0,
    adjustment: 9.75,
    paymentDate: new Date().toISOString().split('T')[0],
    checkNumber: 'EFT-883921',
    eraRef: 'ERA-835-AZ-9921',
    eobRef: 'EOB-88219',
    denialReason: '',
    notes: ''
  });

  // Multi-Client EOB / Remittance Artifact Upload Modal
  const [showArtifactModal, setShowArtifactModal] = useState(false);
  const [artifactForm, setArtifactForm] = useState({
    filename: '',
    payerName: 'Arizona DDD',
    checkOrEftNumber: '',
    adjudicationDate: new Date().toISOString().split('T')[0],
    totalPaidAmount: 0.0,
    totalAllowedAmount: 0.0,
    totalBilledAmount: 0.0,
    totalAdjustmentAmount: 0.0,
    totalPatientResp: 0.0,
    notes: '',
    fileContent: ''
  });
  const [artifactLines, setArtifactLines] = useState<Array<{
    claimId: string;
    amountBilled: number;
    amountPaid: number;
    allowedAmount: number;
    adjustment: number;
    patientResp: number;
    denialReason: string;
    notes: string;
  }>>([]);

  // EOB Source Batches & Canonical Remittances State
  const [eobBatches, setEobBatches] = useState<EobSourceBatch[]>([]);
  const [canonicalEobs, setCanonicalEobs] = useState<CanonicalEob[]>([]);
  const [paymentHeaders, setPaymentHeaders] = useState<PaymentHeader[]>([]);
  const [clientsList, setClientsList] = useState<Client[]>([]);
  const [remittanceSubTab, setRemittanceSubTab] = useState<'CANONICAL' | 'BATCHES'>('CANONICAL');

  // Modals for EOB Batches and Canonical EOBs
  const [showUploadBatchModal, setShowUploadBatchModal] = useState(false);
  const [uploadBatchForm, setUploadBatchForm] = useState({
    filename: '',
    receivedDate: new Date().toISOString().split('T')[0],
    payerHint: '',
    paymentDateHint: '',
    totalPaymentHint: '',
    traceNumberHint: '',
    notes: '',
    fileContent: '',
    mimeType: 'application/pdf'
  });
  const [batchUploadError, setBatchUploadError] = useState<string | null>(null);
  const [batchUploading, setBatchUploading] = useState(false);

  const [showCreateCanonicalModal, setShowCreateCanonicalModal] = useState(false);
  const [canonicalForm, setCanonicalForm] = useState<{
    sourceBatchId: string;
    sourcePageStart: number;
    sourcePageEnd: number;
    payerName: string;
    receivedDate: string;
    paymentDate: string;
    totalPaymentAmount: string;
    checkOrEftNumber: string;
    filename: string;
    notes: string;
    reviewStatus: 'NEEDS_REVIEW' | 'VERIFIED';
    paymentHeaderOption: 'NONE' | 'EXISTING' | 'NEW';
    paymentHeaderId: string;
    newHeaderPayer: string;
    newHeaderDate: string;
    newHeaderAmount: string;
    newHeaderCheckNumber: string;
    newHeaderMethod: 'EFT' | 'CHECK' | 'VIRTUAL_CARD' | 'ACH' | 'OTHER';
    selectedClientIds: string[];
  }>({
    sourceBatchId: '',
    sourcePageStart: 1,
    sourcePageEnd: 1,
    payerName: '',
    receivedDate: new Date().toISOString().split('T')[0],
    paymentDate: '',
    totalPaymentAmount: '',
    checkOrEftNumber: '',
    filename: '',
    notes: '',
    reviewStatus: 'NEEDS_REVIEW',
    paymentHeaderOption: 'NONE',
    paymentHeaderId: '',
    newHeaderPayer: '',
    newHeaderDate: new Date().toISOString().split('T')[0],
    newHeaderAmount: '',
    newHeaderCheckNumber: '',
    newHeaderMethod: 'EFT',
    selectedClientIds: []
  });
  const [canonicalSubmitting, setCanonicalSubmitting] = useState(false);
  const [canonicalError, setCanonicalError] = useState<string | null>(null);

  // Client Attach/Detach Modal
  const [activeEobForClients, setActiveEobForClients] = useState<CanonicalEob | null>(null);
  const [selectedClientIdToAttach, setSelectedClientIdToAttach] = useState<string>('');
  const [clientSearchQuery, setClientSearchQuery] = useState<string>('');

  // Payment Header Link Modal
  const [activeEobForPaymentHeader, setActiveEobForPaymentHeader] = useState<CanonicalEob | null>(null);
  const [selectedPaymentHeaderIdToLink, setSelectedPaymentHeaderIdToLink] = useState<string>('');
  const [showCreatePaymentHeaderModal, setShowCreatePaymentHeaderModal] = useState(false);
  const [paymentHeaderForm, setPaymentHeaderForm] = useState<{
    payerName: string;
    paymentDate: string;
    totalPaymentAmount: string;
    checkOrEftNumber: string;
    paymentMethod: 'EFT' | 'CHECK' | 'VIRTUAL_CARD' | 'ACH' | 'OTHER';
    notes: string;
    linkEobId?: string;
  }>({
    payerName: '',
    paymentDate: new Date().toISOString().split('T')[0],
    totalPaymentAmount: '',
    checkOrEftNumber: '',
    paymentMethod: 'EFT',
    notes: ''
  });

  // Claim Adjudication Bridge State (Phase 1)
  const [showAdjudicateModal, setShowAdjudicateModal] = useState(false);
  const [activeEobForAdjudication, setActiveEobForAdjudication] = useState<CanonicalEob | null>(null);
  const [eligibleClaimsForEob, setEligibleClaimsForEob] = useState<any[]>([]);
  const [loadingEligibleClaims, setLoadingEligibleClaims] = useState(false);
  const [claimSearchFilter, setClaimSearchFilter] = useState('');
  const [adjudicateForm, setAdjudicateForm] = useState<{
    claimId: string;
    payerName: string;
    payerSequence: 'PRIMARY' | 'SECONDARY';
    adjudicationDate: string;
    adjudicationOutcome: 'PAID' | 'PARTIAL' | 'DENIED' | 'REVERSED' | 'CORRECTION';
    payerControlNumber: string;
    billedAmount: string;
    allowedAmount: string;
    paidAmount: string;
    contractualAdjustment: string;
    patientResponsibility: string;
    remainingSecondaryBalance: string;
    groupCode: string;
    reasonCode: string;
    carcCode: string;
    rarcCode: string;
    denialReason: string;
    tplEvidenceStatus: 'PENDING' | 'VALID_PAYMENT' | 'VALID_DENIAL' | 'INSUFFICIENT_EVIDENCE';
    paymentHeaderId: string;
    notes: string;
  }>({
    claimId: '',
    payerName: '',
    payerSequence: 'PRIMARY',
    adjudicationDate: new Date().toISOString().split('T')[0],
    adjudicationOutcome: 'PAID',
    payerControlNumber: '',
    billedAmount: '',
    allowedAmount: '',
    paidAmount: '',
    contractualAdjustment: '',
    patientResponsibility: '',
    remainingSecondaryBalance: '',
    groupCode: '',
    reasonCode: '',
    carcCode: '',
    rarcCode: '',
    denialReason: '',
    tplEvidenceStatus: 'PENDING',
    paymentHeaderId: '',
    notes: ''
  });
  const [adjudicateSubmitting, setAdjudicateSubmitting] = useState(false);
  const [adjudicateError, setAdjudicateError] = useState<string | null>(null);

  // EOB Adjudications View Modal
  const [showEobAdjudicationsModal, setShowEobAdjudicationsModal] = useState(false);
  const [activeEobForViewingAdjudications, setActiveEobForViewingAdjudications] = useState<CanonicalEob | null>(null);
  const [eobAdjudicationsList, setEobAdjudicationsList] = useState<ClaimAdjudication[]>([]);
  const [loadingEobAdjudications, setLoadingEobAdjudications] = useState(false);

  // Claim Adjudications History View Modal
  const [showClaimAdjudicationsModal, setShowClaimAdjudicationsModal] = useState(false);
  const [activeClaimForAdjudications, setActiveClaimForAdjudications] = useState<Claim | any | null>(null);
  const [claimAdjudicationsList, setClaimAdjudicationsList] = useState<ClaimAdjudication[]>([]);
  const [loadingClaimAdjudications, setLoadingClaimAdjudications] = useState(false);

  // Reversal / Correction State
  const [reversingAdjudicationId, setReversingAdjudicationId] = useState<string | null>(null);
  const [reversalNotes, setReversalNotes] = useState('');
  const [reversalSubmitting, setReversalSubmitting] = useState(false);

  // Inline Document Viewer Modal
  const [inlineDocViewer, setInlineDocViewer] = useState<{
    title: string;
    url: string;
    filename: string;
  } | null>(null);

  // Secondary COB Packet Preview Modal (Phase 2B)
  const [showCobPreviewModal, setShowCobPreviewModal] = useState(false);
  const [cobPreviewData, setCobPreviewData] = useState<SecondaryCobPreviewData | null>(null);
  const [loadingCobPreview, setLoadingCobPreview] = useState(false);
  const [cobPreviewError, setCobPreviewError] = useState<string | null>(null);
  const [generatingSecondaryServiceId, setGeneratingSecondaryServiceId] = useState<string | null>(null);
  const [secondaryGenerateError, setSecondaryGenerateError] = useState<{ serviceId: string; message: string } | null>(null);

  const handleOpenCobPreview = async (serviceId: string) => {
    setShowCobPreviewModal(true);
    setLoadingCobPreview(true);
    setCobPreviewError(null);
    setCobPreviewData(null);
    try {
      const res = await fetch(`/api/billing/cob-preview/${serviceId}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed to load preview' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setCobPreviewData(data);
    } catch (err: any) {
      setCobPreviewError(err.message || 'Error generating secondary COB packet preview.');
    } finally {
      setLoadingCobPreview(false);
    }
  };

  const handleGenerateDddSecondary = async (serviceId: string) => {
    setGeneratingSecondaryServiceId(serviceId);
    setSecondaryGenerateError(null);
    try {
      const res = await fetch('/api/billing/claims/generate-ddd-secondary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ serviceId })
      });
      const data = await res.json().catch(() => ({ error: 'DDD secondary file was not created.' }));
      if (!res.ok) {
        throw new Error(data.error || 'DDD secondary file was not created.');
      }
      setShowCobPreviewModal(false);
      setCobPreviewData(null);
      await fetchData();
      onRefreshStats();
    } catch (err: any) {
      setSecondaryGenerateError({ serviceId, message: err.message || 'DDD secondary file was not created.' });
    } finally {
      setGeneratingSecondaryServiceId(null);
    }
  };

  // Sync initial tab when changed from outer navigation
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [readRes, claimRes, pmtRes, artRes, batchRes, eobBatchRes, canEobRes, pmtHdrRes, clientsRes, histRes] = await Promise.all([
        fetch('/api/billing/readiness'),
        fetch('/api/billing/claims'),
        fetch('/api/billing/payments'),
        fetch('/api/billing/remittance-artifacts'),
        fetch('/api/billing/batches'),
        fetch('/api/billing/eob-batches'),
        fetch('/api/billing/canonical-eobs'),
        fetch('/api/billing/payment-headers'),
        fetch('/api/clients'),
        fetch('/api/historical-vault/summary')
      ]);
      if (readRes.ok) setReadinessItems(await readRes.json());
      if (claimRes.ok) setClaims(await claimRes.json());
      if (pmtRes.ok) setPayments(await pmtRes.json());
      if (artRes.ok) setArtifacts(await artRes.json());
      if (batchRes.ok) setBatches(await batchRes.json());
      if (eobBatchRes.ok) setEobBatches(await eobBatchRes.json());
      if (canEobRes.ok) setCanonicalEobs(await canEobRes.json());
      if (pmtHdrRes.ok) setPaymentHeaders(await pmtHdrRes.json());
      if (clientsRes.ok) setClientsList(await clientsRes.json());
      if (histRes.ok) setHistoricalVaultSummary(await histRes.json());
    } catch (err) {
      console.error('Error fetching billing console data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleReevaluateAll = async () => {
    try {
      const res = await fetch('/api/billing/evaluate-all', { method: 'POST' });
      if (res.ok) {
        await fetchData();
        onRefreshStats();
      }
    } catch (err) {
      console.error('Re-evaluate failed:', err);
    }
  };

  // Generate Batch Action (WellSky or Claim.MD)
  const handleGenerateBatch = async (route: 'WELLSKY_DDD' | 'CLAIMMD_COMMERCIAL') => {
    if (batchCacheStaleWarning) return;

    const targetIds = route === 'WELLSKY_DDD'
      ? selectedWellSkyReady.map((i) => i.service_id)
      : selectedClaimMdReady.map((i) => i.service_id);

    if (targetIds.length === 0) return;

    try {
      const res = await fetch('/api/billing/claims/generate-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceIds: targetIds,
          route
        })
      });
      if (res.ok) {
        const data = await res.json();
        const sync = readDisplaySync(data);
        if (sync.displayOutOfSync) {
          onBatchCacheStaleWarning?.(sync.warning || SAVED_DISPLAY_WARNING);
          return;
        }
        setBatchPreview({
          ...data.batch,
          batchNumber: data.batch?.batchNumber || data.batch?.batchId
        });
        setSelectedServiceIds((prev) => prev.filter((id) => !targetIds.includes(id)));
        setActiveTab('Claims');

        if (data.runtime_cache_synced === false) {
          onBatchCacheStaleWarning?.(
            data.runtime_cache_warning || 'The operation was saved. Server cache recovery is required before this view can be trusted. Do not retry this action.'
          );
        }

        await fetchData();
        onRefreshStats();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to generate batch');
      }
    } catch (err) {
      console.error('Batch generation failed:', err);
      alert(err instanceof Error ? err.message : 'Failed to generate batch');
    }
  };

  const handleDownloadBatchContent = (filename: string, content: string) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadBatchById = async (batchId: string, filename: string) => {
    try {
      const res = await fetch(`/api/billing/batches/${batchId}/download`);
      if (!res.ok) {
        alert('Failed to download batch file');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const handleDownload837 = async (claimId: string, filename?: string) => {
    try {
      const res = await fetch(`/api/billing/claims/${claimId}/download-837`);
      if (!res.ok) {
        alert('Failed to download 837 file');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `ALLEGIANT_837P_${claimId}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const handleDownloadArtifact = async (artifactId: string, filename: string) => {
    try {
      const res = await fetch(`/api/billing/remittance-artifacts/${artifactId}/download`);
      if (!res.ok) {
        alert('Failed to download artifact');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const handleCreateReplacement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClaimForReplacement) return;
    try {
      const res = await fetch('/api/billing/claims/create-replacement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          originalClaimId: selectedClaimForReplacement.id,
          payerControlNumber: replacementForm.payerControlNumber,
          resubmissionReason: replacementForm.resubmissionReason,
          claimFrequencyCode: replacementForm.claimFrequencyCode
        })
      });
      if (res.ok) {
        setShowReplacementModal(false);
        setSelectedClaimForReplacement(null);
        await fetchData();
        onRefreshStats();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to create replacement claim');
      }
    } catch (err) {
      console.error('Error creating replacement claim:', err);
    }
  };

  const handlePostPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/billing/payments/post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paymentForm)
      });
      if (res.ok) {
        setShowPaymentModal(false);
        setSelectedClaimForPayment(null);
        setSelectedServiceForPayment(null);
        await fetchData();
        onRefreshStats();
        setActiveTab('Payments');
      } else {
        const err = await res.json();
        alert(err.error || 'Payment posting failed');
      }
    } catch (err) {
      console.error('Payment post error:', err);
    }
  };

  const openPaymentModalForClaim = (c: Claim) => {
    setSelectedClaimForPayment(c);
    setSelectedServiceForPayment(null);
    setPaymentForm({
      claimId: c.id,
      serviceId: c.service_id || '',
      payerName: c.payer_name || 'Commercial Payer',
      clientName: `${c.last_name}, ${c.first_name}`,
      dos: c.dos || '',
      serviceCode: '92507',
      amountBilled: c.billed_amount || 65.0,
      amountPaid: c.billed_amount ? Math.max(0, c.billed_amount - 9.75) : 55.25,
      allowedAmount: c.billed_amount ? Math.max(0, c.billed_amount - 9.75) : 55.25,
      patientResp: 0.0,
      deductible: 0.0,
      coinsurance: 0.0,
      copay: 0.0,
      adjustment: 9.75,
      paymentDate: new Date().toISOString().split('T')[0],
      checkNumber: 'EFT-883921',
      eraRef: 'ERA-835-AZ-9921',
      eobRef: 'EOB-88219',
      denialReason: '',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const openPaymentModalForService = (item: BillingReadinessItem) => {
    setSelectedServiceForPayment(item);
    setSelectedClaimForPayment(null);
    setPaymentForm({
      claimId: '',
      serviceId: item.service_id,
      payerName: item.primary_payer_name || 'Commercial Primary',
      clientName: `${item.last_name}, ${item.first_name}`,
      dos: item.dos || '',
      serviceCode: item.service_code || '92507',
      amountBilled: Number(item.calculated_charge || 65.0),
      amountPaid: Number(item.calculated_charge ? item.calculated_charge - 9.75 : 55.25),
      allowedAmount: Number(item.calculated_charge ? item.calculated_charge - 9.75 : 55.25),
      patientResp: 0.0,
      deductible: 0.0,
      coinsurance: 0.0,
      copay: 0.0,
      adjustment: 9.75,
      paymentDate: new Date().toISOString().split('T')[0],
      checkNumber: 'EFT-PRIMARY-001',
      eraRef: 'ERA-835-PRIMARY',
      eobRef: 'EOB-COMMERCIAL-PRIMARY',
      denialReason: '',
      notes: 'Primary commercial adjudication recorded to clear Route C for secondary DDD billing'
    });
    setShowPaymentModal(true);
  };

  const handleUploadArtifact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!artifactForm.filename) {
      alert('Filename is required');
      return;
    }
    try {
      const res = await fetch('/api/billing/remittance-artifacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...artifactForm,
          paymentLines: artifactLines
        })
      });
      if (res.ok) {
        setShowArtifactModal(false);
        setArtifactForm({
          filename: '',
          payerName: 'Arizona DDD',
          checkOrEftNumber: '',
          adjudicationDate: new Date().toISOString().split('T')[0],
          totalPaidAmount: 0.0,
          totalAllowedAmount: 0.0,
          totalBilledAmount: 0.0,
          totalAdjustmentAmount: 0.0,
          totalPatientResp: 0.0,
          notes: '',
          fileContent: ''
        });
        setArtifactLines([]);
        await fetchData();
        onRefreshStats();
        setActiveTab('Remittances');
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to save remittance artifact');
      }
    } catch (err) {
      console.error('Artifact upload error:', err);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setArtifactForm((prev) => ({ ...prev, filename: file.name }));
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setArtifactForm((prev) => ({ ...prev, fileContent: content }));
    };
    reader.readAsText(file);
  };

  const addArtifactLine = () => {
    if (claims.length === 0) return;
    const defaultClaim = claims.find((c) => c.status !== 'PAID') || claims[0];
    setArtifactLines((prev) => [
      ...prev,
      {
        claimId: defaultClaim.id,
        amountBilled: defaultClaim.billed_amount || 65.0,
        amountPaid: defaultClaim.billed_amount ? defaultClaim.billed_amount - 9.75 : 55.25,
        allowedAmount: defaultClaim.billed_amount ? defaultClaim.billed_amount - 9.75 : 55.25,
        adjustment: 9.75,
        patientResp: 0.0,
        denialReason: '',
        notes: ''
      }
    ]);
  };

  // --- SCANNED EOB BATCH & CANONICAL REMITTANCE HANDLERS ---

  const handleBatchFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const dataUrl = evt.target?.result as string;
      setUploadBatchForm((prev) => ({
        ...prev,
        filename: file.name,
        mimeType: file.type || 'application/pdf',
        fileContent: dataUrl
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleUploadBatchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadBatchForm.filename || !uploadBatchForm.fileContent || !uploadBatchForm.receivedDate) {
      setBatchUploadError('File, filename, and received date are required.');
      return;
    }
    try {
      setBatchUploading(true);
      setBatchUploadError(null);
      const res = await fetch('/api/billing/eob-batches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(uploadBatchForm)
      });
      if (res.ok) {
        setShowUploadBatchModal(false);
        setUploadBatchForm({
          filename: '',
          receivedDate: new Date().toISOString().split('T')[0],
          payerHint: '',
          paymentDateHint: '',
          totalPaymentHint: '',
          traceNumberHint: '',
          notes: '',
          fileContent: '',
          mimeType: 'application/pdf'
        });
        await fetchData();
        onRefreshStats();
      } else {
        const err = await res.json();
        setBatchUploadError(err.error || 'Failed to upload EOB batch');
      }
    } catch (err: any) {
      setBatchUploadError(err.message || 'Error uploading EOB batch');
    } finally {
      setBatchUploading(false);
    }
  };

  const handleCreateCanonicalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canonicalForm.payerName || !canonicalForm.receivedDate) {
      setCanonicalError('Payer Name and Received Date are required.');
      return;
    }
    try {
      setCanonicalSubmitting(true);
      setCanonicalError(null);

      let targetHeaderId = canonicalForm.paymentHeaderOption === 'EXISTING' ? canonicalForm.paymentHeaderId : undefined;

      // If user wants to create a new payment header simultaneously:
      if (canonicalForm.paymentHeaderOption === 'NEW' && canonicalForm.newHeaderPayer && canonicalForm.newHeaderDate) {
        const pmtRes = await fetch('/api/billing/payment-headers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            payerName: canonicalForm.newHeaderPayer,
            paymentDate: canonicalForm.newHeaderDate,
            totalPaymentAmount: parseFloat(canonicalForm.newHeaderAmount || canonicalForm.totalPaymentAmount) || 0.0,
            checkOrEftNumber: canonicalForm.newHeaderCheckNumber || canonicalForm.checkOrEftNumber,
            paymentMethod: canonicalForm.newHeaderMethod,
            notes: 'Generated from Canonical EOB intake'
          })
        });
        if (pmtRes.ok) {
          const pmtData = await pmtRes.json();
          targetHeaderId = pmtData.paymentHeaderId;
        }
      }

      const payload = {
        sourceBatchId: canonicalForm.sourceBatchId || undefined,
        sourcePageStart: Number(canonicalForm.sourcePageStart) || 1,
        sourcePageEnd: Number(canonicalForm.sourcePageEnd) || 1,
        payerName: canonicalForm.payerName,
        receivedDate: canonicalForm.receivedDate,
        paymentDate: canonicalForm.paymentDate || undefined,
        totalPaymentAmount: parseFloat(canonicalForm.totalPaymentAmount) || 0.0,
        checkOrEftNumber: canonicalForm.checkOrEftNumber || undefined,
        filename: canonicalForm.filename || undefined,
        notes: canonicalForm.notes || undefined,
        reviewStatus: canonicalForm.reviewStatus,
        paymentHeaderId: targetHeaderId,
        clientIds: canonicalForm.selectedClientIds
      };

      const res = await fetch('/api/billing/canonical-eobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setShowCreateCanonicalModal(false);
        setCanonicalForm({
          sourceBatchId: '',
          sourcePageStart: 1,
          sourcePageEnd: 1,
          payerName: '',
          receivedDate: new Date().toISOString().split('T')[0],
          paymentDate: '',
          totalPaymentAmount: '',
          checkOrEftNumber: '',
          filename: '',
          notes: '',
          reviewStatus: 'NEEDS_REVIEW',
          paymentHeaderOption: 'NONE',
          paymentHeaderId: '',
          newHeaderPayer: '',
          newHeaderDate: new Date().toISOString().split('T')[0],
          newHeaderAmount: '',
          newHeaderCheckNumber: '',
          newHeaderMethod: 'EFT',
          selectedClientIds: []
        });
        await fetchData();
        onRefreshStats();
      } else {
        const err = await res.json();
        setCanonicalError(err.error || 'Failed to create canonical EOB');
      }
    } catch (err: any) {
      setCanonicalError(err.message || 'Error creating canonical EOB');
    } finally {
      setCanonicalSubmitting(false);
    }
  };

  const handleToggleVerifyCanonical = async (eobId: string) => {
    try {
      const res = await fetch(`/api/billing/canonical-eobs/${eobId}/verify`, { method: 'PUT' });
      if (res.ok) {
        await fetchData();
        onRefreshStats();
      }
    } catch (err) {
      console.error('Error toggling canonical review status:', err);
    }
  };

  const handleAttachClientToEob = async (eobId: string, clientId: string) => {
    if (!clientId) return;
    try {
      const res = await fetch(`/api/billing/canonical-eobs/${eobId}/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
      });
      if (res.ok) {
        setSelectedClientIdToAttach('');
        await fetchData();
        // Update the active modal's state if open
        const updated = await fetch('/api/billing/canonical-eobs').then(r => r.json());
        setCanonicalEobs(updated);
        const match = updated.find((e: CanonicalEob) => e.id === eobId);
        if (match) setActiveEobForClients(match);
      }
    } catch (err) {
      console.error('Error attaching client to EOB:', err);
    }
  };

  const handleDetachClientFromEob = async (eobId: string, clientId: string) => {
    try {
      const res = await fetch(`/api/billing/canonical-eobs/${eobId}/clients/${clientId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        await fetchData();
        const updated = await fetch('/api/billing/canonical-eobs').then(r => r.json());
        setCanonicalEobs(updated);
        const match = updated.find((e: CanonicalEob) => e.id === eobId);
        if (match) setActiveEobForClients(match);
      }
    } catch (err) {
      console.error('Error detaching client from EOB:', err);
    }
  };

  const handleLinkPaymentHeaderSubmit = async (eobId: string, paymentHeaderId: string) => {
    if (!paymentHeaderId) return;
    try {
      const res = await fetch(`/api/billing/canonical-eobs/${eobId}/payment-header`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentHeaderId })
      });
      if (res.ok) {
        setActiveEobForPaymentHeader(null);
        setSelectedPaymentHeaderIdToLink('');
        await fetchData();
        onRefreshStats();
      }
    } catch (err) {
      console.error('Error linking payment header:', err);
    }
  };

  const handleCreatePaymentHeaderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentHeaderForm.payerName || !paymentHeaderForm.paymentDate || !paymentHeaderForm.totalPaymentAmount) {
      alert('Payer Name, Payment Date, and Total Payment Amount are required.');
      return;
    }
    try {
      const res = await fetch('/api/billing/payment-headers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...paymentHeaderForm,
          totalPaymentAmount: parseFloat(paymentHeaderForm.totalPaymentAmount) || 0.0
        })
      });
      if (res.ok) {
        setShowCreatePaymentHeaderModal(false);
        setPaymentHeaderForm({
          payerName: '',
          paymentDate: new Date().toISOString().split('T')[0],
          totalPaymentAmount: '',
          checkOrEftNumber: '',
          paymentMethod: 'EFT',
          notes: ''
        });
        await fetchData();
        onRefreshStats();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to create payment header');
      }
    } catch (err) {
      console.error('Error creating payment header:', err);
    }
  };

  const openSegmentModalForBatch = (batch: EobSourceBatch) => {
    setCanonicalForm({
      sourceBatchId: batch.id,
      sourcePageStart: 1,
      sourcePageEnd: Math.min(batch.page_count || 1, 1),
      payerName: batch.payer_hint || '',
      receivedDate: batch.received_date || new Date().toISOString().split('T')[0],
      paymentDate: batch.payment_date_hint || '',
      totalPaymentAmount: batch.total_payment_hint ? String(batch.total_payment_hint) : '',
      checkOrEftNumber: batch.trace_number_hint || '',
      filename: `${(batch.payer_hint || 'EOB').replace(/[^a-zA-Z0-9]/g, '_')}_segment.pdf`,
      notes: batch.notes ? `Derived from ${batch.filename}: ${batch.notes}` : '',
      reviewStatus: 'NEEDS_REVIEW',
      paymentHeaderOption: 'NONE',
      paymentHeaderId: '',
      newHeaderPayer: batch.payer_hint || '',
      newHeaderDate: batch.payment_date_hint || batch.received_date || new Date().toISOString().split('T')[0],
      newHeaderAmount: batch.total_payment_hint ? String(batch.total_payment_hint) : '',
      newHeaderCheckNumber: batch.trace_number_hint || '',
      newHeaderMethod: 'EFT',
      selectedClientIds: []
    });
    setShowCreateCanonicalModal(true);
  };

  // --- CLAIM ADJUDICATION EVIDENCE BRIDGE (PHASE 1) HANDLERS ---

  const handleOpenAdjudicateModal = async (eob: CanonicalEob) => {
    setActiveEobForAdjudication(eob);
    setAdjudicateError(null);
    setClaimSearchFilter('');
    setAdjudicateForm({
      claimId: '',
      payerName: eob.payer_name || '',
      payerSequence: 'PRIMARY',
      adjudicationDate: eob.payment_date || eob.received_date || new Date().toISOString().split('T')[0],
      adjudicationOutcome: Number(eob.total_payment_amount) > 0 ? 'PAID' : 'DENIED',
      payerControlNumber: eob.check_or_eft_number || '',
      billedAmount: '',
      allowedAmount: '',
      paidAmount: eob.total_payment_amount ? String(eob.total_payment_amount) : '0.00',
      contractualAdjustment: '0.00',
      patientResponsibility: '0.00',
      remainingSecondaryBalance: '0.00',
      groupCode: Number(eob.total_payment_amount) > 0 ? 'CO' : 'PR',
      reasonCode: '',
      carcCode: '',
      rarcCode: '',
      denialReason: '',
      tplEvidenceStatus: Number(eob.total_payment_amount) > 0 ? 'VALID_PAYMENT' : 'PENDING',
      paymentHeaderId: eob.payment_header_id || '',
      notes: ''
    });
    setShowAdjudicateModal(true);
    setLoadingEligibleClaims(true);
    try {
      const res = await fetch(`/api/billing/canonical-eobs/${eob.id}/eligible-claims`);
      if (res.ok) {
        const data = await res.json();
        setEligibleClaimsForEob(data.claims || []);
        if (data.claims && data.claims.length > 0) {
          const first = data.claims[0];
          setAdjudicateForm(prev => ({
            ...prev,
            claimId: first.id,
            billedAmount: String(first.billed_amount || '')
          }));
        }
      }
    } catch (e) {
      console.error('Error fetching eligible claims:', e);
    } finally {
      setLoadingEligibleClaims(false);
    }
  };

  const handleSelectClaimForAdjudication = (claimId: string) => {
    const clm = eligibleClaimsForEob.find(c => c.id === claimId);
    setAdjudicateForm(prev => ({
      ...prev,
      claimId,
      billedAmount: clm ? String(clm.billed_amount || '') : prev.billedAmount
    }));
  };

  const handleSaveAdjudication = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeEobForAdjudication || !adjudicateForm.claimId) {
      setAdjudicateError('Please select a claim to adjudicate.');
      return;
    }
    setAdjudicateSubmitting(true);
    setAdjudicateError(null);
    try {
      const res = await fetch(`/api/billing/canonical-eobs/${activeEobForAdjudication.id}/adjudications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claimId: adjudicateForm.claimId,
          payerName: adjudicateForm.payerName,
          payerSequence: adjudicateForm.payerSequence,
          adjudicationDate: adjudicateForm.adjudicationDate,
          adjudicationOutcome: adjudicateForm.adjudicationOutcome,
          payerControlNumber: adjudicateForm.payerControlNumber,
          billedAmount: adjudicateForm.billedAmount ? parseFloat(adjudicateForm.billedAmount) : undefined,
          allowedAmount: adjudicateForm.allowedAmount ? parseFloat(adjudicateForm.allowedAmount) : 0,
          paidAmount: adjudicateForm.paidAmount ? parseFloat(adjudicateForm.paidAmount) : 0,
          contractualAdjustment: adjudicateForm.contractualAdjustment ? parseFloat(adjudicateForm.contractualAdjustment) : 0,
          patientResponsibility: adjudicateForm.patientResponsibility ? parseFloat(adjudicateForm.patientResponsibility) : 0,
          remainingSecondaryBalance: adjudicateForm.remainingSecondaryBalance ? parseFloat(adjudicateForm.remainingSecondaryBalance) : 0,
          groupCode: adjudicateForm.groupCode,
          reasonCode: adjudicateForm.reasonCode,
          carcCode: adjudicateForm.carcCode,
          rarcCode: adjudicateForm.rarcCode,
          denialReason: adjudicateForm.denialReason,
          tplEvidenceStatus: adjudicateForm.tplEvidenceStatus,
          paymentHeaderId: adjudicateForm.paymentHeaderId || undefined,
          notes: adjudicateForm.notes
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to record claim adjudication');
      }

      setShowAdjudicateModal(false);
      await fetchData();
      onRefreshStats();
    } catch (err: any) {
      setAdjudicateError(err.message || 'Error recording adjudication');
    } finally {
      setAdjudicateSubmitting(false);
    }
  };

  const handleOpenEobAdjudications = async (eob: CanonicalEob) => {
    setActiveEobForViewingAdjudications(eob);
    setShowEobAdjudicationsModal(true);
    setLoadingEobAdjudications(true);
    try {
      const res = await fetch(`/api/billing/canonical-eobs/${eob.id}/adjudications`);
      if (res.ok) {
        setEobAdjudicationsList(await res.json());
      }
    } catch (err) {
      console.error('Error fetching EOB adjudications:', err);
    } finally {
      setLoadingEobAdjudications(false);
    }
  };

  const handleOpenClaimAdjudications = async (claim: Claim | any) => {
    setActiveClaimForAdjudications(claim);
    setShowClaimAdjudicationsModal(true);
    setLoadingClaimAdjudications(true);
    try {
      const res = await fetch(`/api/billing/claims/${claim.id}/adjudications`);
      if (res.ok) {
        const data = await res.json();
        setClaimAdjudicationsList(data.adjudications || []);
      }
    } catch (err) {
      console.error('Error fetching claim adjudications:', err);
    } finally {
      setLoadingClaimAdjudications(false);
    }
  };

  const handleReverseAdjudication = async (adjId: string) => {
    if (!reversalNotes.trim()) {
      alert('Please enter a reason/note for this reversal/correction.');
      return;
    }
    setReversalSubmitting(true);
    try {
      const res = await fetch(`/api/billing/claim-adjudications/${adjId}/reverse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: reversalNotes })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to reverse adjudication');
      }
      setReversingAdjudicationId(null);
      setReversalNotes('');
      if (activeClaimForAdjudications) {
        handleOpenClaimAdjudications(activeClaimForAdjudications);
      }
      if (activeEobForViewingAdjudications) {
        handleOpenEobAdjudications(activeEobForViewingAdjudications);
      }
      await fetchData();
      onRefreshStats();
    } catch (err: any) {
      alert(err.message || 'Failed to reverse adjudication');
    } finally {
      setReversalSubmitting(false);
    }
  };

  const toggleBatchExpanded = (batchId: string) => {
    setExpandedBatchIds((prev) =>
      prev.includes(batchId) ? prev.filter((id) => id !== batchId) : [...prev, batchId]
    );
  };

  const isClosedDisposition = (item: BillingReadinessItem) =>
    String(item.billing_disposition || 'ACTIVE') === 'CLOSED_NON_BILLABLE';

  const isOriginalClaimGenerated = (item: BillingReadinessItem) =>
    item.readiness_status === 'ORIGINAL_CLAIM_GENERATED';

  const openReadinessCount = readinessItems.filter((item) => !isClosedDisposition(item) && !isOriginalClaimGenerated(item)).length;

  // Readiness selection calculations
  const filteredReadiness = readinessItems.filter((item) => {
    if (isOriginalClaimGenerated(item)) return false;
    if (routeFilter !== 'ALL' && item.billing_route !== routeFilter) return false;
    const closed = isClosedDisposition(item);
    if (statusFilter === 'CLOSED') return closed;
    if (closed) return false;
    if (statusFilter === 'READY' && item.readiness_status !== 'BILLING_READY') return false;
    if (statusFilter === 'HOLD' && (item.readiness_status === 'BILLING_READY' || item.readiness_status === 'PRIMARY_ADJUDICATED_COB_REQUIRED')) return false;
    if (statusFilter === 'WELLSKY_REVIEW' && item.readiness_status !== 'PRIMARY_ADJUDICATED_COB_REQUIRED') return false;
    return true;
  });

  const wellSkyReviewCount = readinessItems.filter((i) => !isClosedDisposition(i) && i.readiness_status === 'PRIMARY_ADJUDICATED_COB_REQUIRED').length;

  const selectedItems = readinessItems.filter((i) => selectedServiceIds.includes(i.service_id));
  const selectedReadyItems = selectedItems.filter((i) => !isClosedDisposition(i) && i.readiness_status === 'BILLING_READY');
  const selectedWellSkyReady = selectedReadyItems.filter((i) => i.billing_route === 'WELLSKY_DDD');
  const selectedClaimMdReady = selectedReadyItems.filter((i) => i.billing_route === 'CLAIMMD_COMMERCIAL');

  // Multi-select rules: Button enabled only when valid claims for that route are selected
  const canGenerateWellSky =
    !batchCacheStaleWarning &&
    selectedWellSkyReady.length > 0 &&
    selectedItems.every((i) => i.billing_route === 'WELLSKY_DDD' && i.readiness_status === 'BILLING_READY' && !isClosedDisposition(i));

  const canGenerateClaimMd =
    !batchCacheStaleWarning &&
    selectedClaimMdReady.length > 0 &&
    selectedItems.every((i) => i.billing_route === 'CLAIMMD_COMMERCIAL' && i.readiness_status === 'BILLING_READY' && !isClosedDisposition(i));

  const toggleSelectAllReady = () => {
    const allReadyIds = filteredReadiness
      .filter((i) => !isClosedDisposition(i) && i.readiness_status === 'BILLING_READY')
      .map((i) => i.service_id);

    const isAllSelected = allReadyIds.every((id) => selectedServiceIds.includes(id));
    if (isAllSelected) {
      setSelectedServiceIds((prev) => prev.filter((id) => !allReadyIds.includes(id)));
    } else {
      setSelectedServiceIds((prev) => Array.from(new Set([...prev, ...allReadyIds])));
    }
  };

  const toggleSelectRow = (serviceId: string) => {
    setSelectedServiceIds((prev) =>
      prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]
    );
  };

  // Metrics
  const readyTotal = readinessItems
    .filter((i) => !isClosedDisposition(i) && i.readiness_status === 'BILLING_READY')
    .reduce((acc, curr) => acc + (curr.calculated_charge || 0), 0);

  const holdTotal = readinessItems
    .filter((i) => !isClosedDisposition(i) && !isOriginalClaimGenerated(i) && i.readiness_status !== 'BILLING_READY')
    .reduce((acc, curr) => acc + (curr.calculated_charge || 0), 0);

  const billedTotal = claims.reduce((acc, curr) => acc + (curr.billed_amount || 0), 0);
  const paidTotal = payments.reduce((acc, curr) => acc + (curr.amount_paid || 0), 0);

  // Batches filter
  const filteredBatches = batches.filter((b) => {
    if (batchRouteFilter === 'ALL') return true;
    return b.billing_route === batchRouteFilter;
  });

  const formatRoute = (route?: string) => {
    switch (route) {
      case 'WELLSKY_DDD':
        return { label: 'Route A: WellSky / DDD', bg: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
      case 'CLAIMMD_COMMERCIAL':
        return { label: 'Route B: Commercial Primary', bg: 'bg-sky-50 text-sky-800 border-sky-200' };
      case 'CLAIMMD_PRIMARY_DDD_SECONDARY':
        return { label: 'Route C: Comm Prim + DDD Sec', bg: 'bg-purple-50 text-purple-800 border-purple-200' };
      default:
        return { label: 'Route A: WellSky / DDD', bg: 'bg-slate-100 text-slate-700 border-slate-200' };
    }
  };

  return (
    <div className="space-y-3.5">
      {/* Mobile Header (sm:hidden): Canonical Steel-to-Navy Shell */}
      <div className="sm:hidden relative overflow-hidden rounded-2xl border mobile-header-shell px-3.5 py-3 flex flex-col items-center justify-center gap-2.5 w-full">
        <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent pointer-events-none" />

        {/* ROW 1: Centered Title on ONE line at 390px */}
        <div className="relative z-10 w-full text-center">
          <h1 className="text-[15px] font-bold text-white tracking-tight text-center leading-tight whitespace-nowrap">
            Billing Engine & Revenue Operations
          </h1>
        </div>

        {/* ROW 2: Balanced Utility Row on ONE row */}
        <div className="relative z-10 flex items-center justify-between gap-2 w-full">
          {/* Re-evaluate All Services (Primary Action, ~60-65% visual weight) */}
          <button
            onClick={handleReevaluateAll}
            className="flex-1 py-1.5 px-2 rounded-lg bg-gradient-to-b from-[#162032] to-[#0d1424] hover:from-[#1e2c45] hover:to-[#121c33] text-slate-200 border border-slate-700/80 text-[11px] font-semibold flex items-center justify-center space-x-1.5 shadow-[0_2px_8px_rgba(0,0,0,0.3)] transition-all cursor-pointer active:scale-98 whitespace-nowrap min-w-0"
          >
            <RefreshCw className="w-3.5 h-3.5 text-slate-200 shrink-0" />
            <span className="truncate">Re-evaluate All Services</span>
          </button>

          {/* 3-Route Deterministic Pipeline (Secondary Status Badge, ~35-40% visual weight) */}
          <div className="shrink-0 px-2 py-0.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[9.5px] font-mono font-medium leading-tight text-center flex flex-col justify-center">
            <span>3-Route Deterministic</span>
            <span className="text-[8.5px] text-emerald-400/80 font-sans">Pipeline</span>
          </div>
        </div>
      </div>

      {/* Desktop/Tablet Header Shell: Canonical rich navy / blue-grey shell */}
      <div className="hidden sm:block relative overflow-hidden rounded-2xl border border-slate-700/80 desktop-header-shell px-6 py-4 shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)]">
        {/* Subtle specular top hairline */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />

        <div className="relative z-10 flex sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight flex items-center space-x-2.5">
              <span>Billing Engine & Revenue Operations</span>
              <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 leading-none">
                3-Route Deterministic Pipeline
              </span>
            </h1>
          </div>

          <div className="flex items-center space-x-2 shrink-0">
            <button
              onClick={handleReevaluateAll}
              className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white border border-white/20 text-xs font-semibold flex items-center space-x-1.5 shadow-xs transition-all cursor-pointer active:scale-98"
            >
              <RefreshCw className="w-3.5 h-3.5 text-slate-200" />
              <span>Re-evaluate All Services</span>
            </button>
          </div>
        </div>
      </div>

      {/* Revenue Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <div className="relative overflow-hidden p-2.5 sm:px-3.5 sm:py-2.5 rounded-xl border border-emerald-200/80 bg-white shadow-xs">
          <div className="text-[9.5px] uppercase font-bold tracking-wider text-slate-700 leading-tight">
            Billing Ready Queue
          </div>
          <div className="text-lg font-bold text-emerald-800 font-mono leading-tight mt-0.5">
            ${readyTotal.toFixed(2)}
          </div>
          <div className="text-[10px] text-slate-600 leading-tight mt-0.5 truncate">
            {readinessItems.filter((i) => !isClosedDisposition(i) && i.readiness_status === 'BILLING_READY').length} visits cleared for 837
          </div>
        </div>

        <div className="relative overflow-hidden p-2.5 sm:px-3.5 sm:py-2.5 rounded-xl border border-rose-200/80 bg-white shadow-xs">
          <div className="text-[9.5px] uppercase font-bold tracking-wider text-slate-700 leading-tight">
            Held Revenue (Exceptions)
          </div>
          <div className="text-lg font-bold text-rose-800 font-mono leading-tight mt-0.5">
            ${holdTotal.toFixed(2)}
          </div>
          <div className="text-[10px] text-slate-600 leading-tight mt-0.5 truncate">
            {readinessItems.filter((i) => !isClosedDisposition(i) && !isOriginalClaimGenerated(i) && i.readiness_status !== 'BILLING_READY').length} visits on hold
          </div>
        </div>

        <div className="relative overflow-hidden p-2.5 sm:px-3.5 sm:py-2.5 rounded-xl border border-slate-200/90 bg-white shadow-xs">
          <div className="text-[9.5px] uppercase font-bold tracking-wider text-slate-700 leading-tight">
            Generated Claim Batches
          </div>
          <div className="text-lg font-bold text-slate-900 font-mono leading-tight mt-0.5">
            ${billedTotal.toFixed(2)}
          </div>
          <div className="text-[10px] text-slate-600 leading-tight mt-0.5 truncate">
            {batches.length} batches • {claims.length} claims constructed
          </div>
        </div>

        <div className="relative overflow-hidden p-2.5 sm:px-3.5 sm:py-2.5 rounded-xl border border-emerald-200/80 bg-white shadow-xs">
          <div className="text-[9.5px] uppercase font-bold tracking-wider text-slate-700 leading-tight">
            Collected / Reconciled
          </div>
          <div className="text-lg font-bold text-slate-900 font-mono leading-tight mt-0.5">
            ${paidTotal.toFixed(2)}
          </div>
          <div className="text-[10px] text-emerald-700 leading-tight mt-0.5 truncate">
            {payments.length} payment lines posted
          </div>
        </div>
      </div>

      {/* Tabs: Distinct Views */}
      <div className="flex flex-wrap items-center gap-1.5 p-1 rounded-xl bg-white border border-slate-200/90 shadow-xs">
        {(['Readiness', 'Claims', 'Payments', 'Remittances', 'HistoricalVault'] as const).map((tab) => {
          const isActive = activeTab === tab;
          const label =
            tab === 'Readiness'
              ? 'Billing Readiness Queue'
              : tab === 'Claims'
              ? 'Claims & 837 EDI Batches'
              : tab === 'Payments'
              ? 'Payment & EOB Ledger'
              : tab === 'Remittances'
              ? 'EOB & Remittance Archive'
              : 'Historical Evidence Vault';
          const count =
            tab === 'Readiness'
              ? openReadinessCount
              : tab === 'Claims'
              ? batches.length
              : tab === 'Payments'
              ? payments.length
              : tab === 'Remittances'
              ? artifacts.length
              : historicalVaultSummary.artifactsCount;

          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                isActive
                  ? 'bg-slate-900 text-white border border-slate-800 shadow-xs'
                  : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50 border border-transparent'
              }`}
            >
              <span>{label}</span>
              <span
                className={`font-mono text-[10px] px-1.5 py-0.2 rounded-full border ${
                  isActive
                    ? 'bg-white/15 text-white border-white/20'
                    : 'bg-slate-100 text-slate-600 border-slate-200'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ========================================================================= */}
      {/* VIEW 1: BILLING READINESS — WORKING QUEUE                                 */}
      {/* ========================================================================= */}
      {activeTab === 'Readiness' && (
        <div className="space-y-3">
          {/* Top Operational Action Bar */}
          <div className="relative overflow-hidden p-2.5 sm:p-3 rounded-xl border border-slate-200/90 bg-white shadow-xs">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/20 to-transparent pointer-events-none" />

            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2.5">
              {/* Left side: Select All Ready + Selected Count + Route & Status Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center space-x-2 text-slate-800 font-semibold cursor-pointer text-xs select-none hover:text-slate-900 transition-colors">
                  <input
                    type="checkbox"
                    checked={
                      filteredReadiness.filter((i) => i.readiness_status === 'BILLING_READY').length > 0 &&
                      filteredReadiness
                        .filter((i) => i.readiness_status === 'BILLING_READY')
                        .every((i) => selectedServiceIds.includes(i.service_id))
                    }
                    onChange={toggleSelectAllReady}
                    className="w-3.5 h-3.5 rounded bg-white border border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                  />
                  <span>Select All Ready ({readinessItems.filter((i) => i.readiness_status === 'BILLING_READY').length})</span>
                </label>

                {selectedServiceIds.length > 0 && (
                  <span className="font-mono text-[10.5px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 font-semibold">
                    {selectedServiceIds.length} selected
                  </span>
                )}

                <div className="h-4 w-[1px] bg-slate-200 hidden sm:block" />

                {/* Route Filter */}
                <div className="flex items-center space-x-1.5 text-xs">
                  <Filter className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  <span className="text-slate-700 text-[11px] font-semibold">Route:</span>
                  <div className="relative flex items-center">
                    <select
                      value={routeFilter}
                      onChange={(e) => setRouteFilter(e.target.value as any)}
                      className="appearance-none bg-white border border-slate-300 hover:border-slate-400 rounded-lg pl-2.5 pr-7 py-1 text-xs text-slate-900 font-medium shadow-xs focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 transition-colors cursor-pointer"
                    >
                      <option value="ALL" className="bg-white text-slate-900">All Routes</option>
                      <option value="WELLSKY_DDD" className="bg-white text-slate-900">Route A: WellSky / DDD</option>
                      <option value="CLAIMMD_COMMERCIAL" className="bg-white text-slate-900">Route B: Commercial Primary</option>
                      <option value="CLAIMMD_PRIMARY_DDD_SECONDARY" className="bg-white text-slate-900">Route C: Comm Prim + DDD Sec</option>
                    </select>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-600 pointer-events-none absolute right-2 top-1/2 -translate-y-1/2" />
                  </div>
                </div>

                {/* Status Filter */}
                <div className="flex items-center space-x-1.5 text-xs">
                  <span className="text-slate-700 text-[11px] font-semibold">Status:</span>
                  <div className="relative flex items-center">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as any)}
                      className="appearance-none bg-white border border-slate-300 hover:border-slate-400 rounded-lg pl-2.5 pr-7 py-1 text-xs text-slate-900 font-medium shadow-xs focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 transition-colors cursor-pointer"
                    >
                      <option value="ALL" className="bg-white text-slate-900">All Statuses</option>
                      <option value="READY" className="bg-white text-slate-900">Ready Only</option>
                      <option value="HOLD" className="bg-white text-slate-900">On Hold Only</option>
                      <option value="CLOSED" className="bg-white text-slate-900">Closed / Non-billable</option>
                      <option value="WELLSKY_REVIEW" className="bg-white text-slate-900">
                        Ready for WellSky Review — COB Packet Required {wellSkyReviewCount > 0 ? `(${wellSkyReviewCount})` : ''}
                      </option>
                    </select>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-600 pointer-events-none absolute right-2 top-1/2 -translate-y-1/2" />
                  </div>
                </div>
              </div>

              {/* Right side: Action Buttons */}
              <div className="flex items-center space-x-2 shrink-0">
                <button
                  onClick={() => handleGenerateBatch('WELLSKY_DDD')}
                  disabled={!canGenerateWellSky}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center space-x-1.5 transition-all ${
                    canGenerateWellSky
                      ? 'bg-slate-900 hover:bg-slate-800 text-white shadow-xs border border-slate-700 cursor-pointer active:scale-98'
                      : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                  }`}
                  title={
                    batchCacheStaleWarning
                      ? batchCacheStaleWarning
                      : canGenerateWellSky
                      ? `Generate WellSky 837 batch for ${selectedWellSkyReady.length} selected claims`
                      : 'Select only ready WellSky/DDD claims to enable'
                  }
                >
                  <FileCheck className="w-3.5 h-3.5" />
                  <span>Generate WellSky 837</span>
                  {selectedWellSkyReady.length > 0 && (
                    <span className={`ml-0.5 px-1.5 py-0.2 rounded-full text-[10px] font-mono leading-none ${
                      canGenerateWellSky ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {selectedWellSkyReady.length}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => handleGenerateBatch('CLAIMMD_COMMERCIAL')}
                  disabled={!canGenerateClaimMd}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center space-x-1.5 transition-all ${
                    canGenerateClaimMd
                      ? 'bg-slate-900 hover:bg-slate-800 text-white shadow-xs border border-slate-700 cursor-pointer active:scale-98'
                      : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                  }`}
                  title={
                    batchCacheStaleWarning
                      ? batchCacheStaleWarning
                      : canGenerateClaimMd
                      ? `Generate Claim.MD 837 batch for ${selectedClaimMdReady.length} selected claims`
                      : 'Select only ready Claim.MD Commercial claims to enable'
                  }
                >
                  <FileCheck className="w-3.5 h-3.5" />
                  <span>Generate Claim.MD 837</span>
                  {selectedClaimMdReady.length > 0 && (
                    <span className={`ml-0.5 px-1.5 py-0.2 rounded-full text-[10px] font-mono leading-none ${
                      canGenerateClaimMd ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {selectedClaimMdReady.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {batchCacheStaleWarning && (
            <div className="relative overflow-hidden p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-950 flex items-start gap-2.5 shadow-xs">
              <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-bold text-amber-950">Batch saved — server cache recovery required</div>
                <div className="text-[11px] text-amber-900 mt-0.5">{batchCacheStaleWarning}</div>
                <div className="text-[11px] text-amber-800 mt-1">Further 837 generation is disabled until server cache recovery. Do not retry this action.</div>
              </div>
            </div>
          )}

          {/* Human Review Gate Notice Banner */}
          {statusFilter === 'WELLSKY_REVIEW' && (
            <div className="relative overflow-hidden p-3 rounded-xl bg-purple-50 border border-purple-200 text-purple-900 flex flex-col sm:flex-row sm:items-center justify-between gap-2 shadow-xs">
              <div className="flex items-center space-x-2.5">
                <div className="w-7 h-7 rounded-lg bg-purple-500/20 border border-purple-500/30 flex items-center justify-center shrink-0">
                  <ShieldAlert className="w-4 h-4 text-purple-400" />
                </div>
                <div>
                  <div className="text-xs font-bold text-purple-900 flex items-center gap-1.5">
                    <span>Ready for WellSky Review — COB Packet Required</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-purple-100 border border-purple-200 text-purple-800">
                      Phase 2A Gate Active
                    </span>
                  </div>
                  <div className="text-[11px] text-purple-800 mt-0.5">
                    Review the COB preview, then generate the DDD secondary file. Generating the file does not transmit it.
                  </div>
                </div>
              </div>
              <div className="shrink-0 flex items-center space-x-2">
                <span className="text-[10px] font-mono text-purple-800 bg-white px-2 py-1 rounded border border-purple-200">
                  {wellSkyReviewCount} visit{wellSkyReviewCount === 1 ? '' : 's'} awaiting COB review
                </span>
              </div>
            </div>
          )}

          {/* Working Queue Table */}
          <div className="rounded-xl overflow-hidden border border-slate-200/90 bg-white shadow-xs">
            {loading ? (
              <div className="p-8 text-center text-xs text-slate-600">Evaluating billing status...</div>
            ) : filteredReadiness.length === 0 ? (
              <div className="p-10 text-center text-xs text-slate-600">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="font-semibold text-slate-800">No visits matching the current filter.</p>
              </div>
            ) : (
              <div>
                {/* DESKTOP/TABLET TABLE */}
                <div className="hidden md:block">
                  <table className="w-full table-fixed text-left text-xs">
                    <thead className="bg-slate-50 text-[9.5px] uppercase font-bold text-slate-700 border-b border-slate-200">
                      <tr>
                        <th className="px-2 py-2 font-bold w-[6%] text-center"></th>
                        <th className="px-2 py-2 font-bold w-[16%]">DOS / Client</th>
                        <th className="px-2 py-2 font-bold w-[11%]">Service / CPT</th>
                        <th className="px-2 py-2 font-bold w-[14%]">Provider / Role</th>
                        <th className="px-2 py-2 font-bold w-[12%]">Payer / Route</th>
                        <th className="px-2 py-2 font-bold w-[10%]">Units / Charge</th>
                        <th className="px-2 py-2 font-bold w-[13%]">Readiness Status</th>
                        <th className="px-2 py-2 font-bold w-[18%]">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {filteredReadiness.map((item, rowIndex) => {
                        const isClosed = isClosedDisposition(item);
                        const isReady = !isClosed && item.readiness_status === 'BILLING_READY';
                        const isSelected = selectedServiceIds.includes(item.service_id);
                        const routeInfo = formatRoute(item.billing_route);
                        const isRouteCWaiting = item.readiness_status === 'HOLD_PRIMARY_ADJUDICATION_PENDING';
                        const holdReasons = item.hold_reasons || [];
                        const showCobDetail = !isReady && item.readiness_status === 'PRIMARY_ADJUDICATED_COB_REQUIRED';
                        const showEobDetail = !isReady && isRouteCWaiting;
                        const showHoldDetail = !isReady && !showCobDetail && !showEobDetail && holdReasons.length > 0;
                        const showDetail = showCobDetail || showEobDetail || showHoldDetail;
                        const rowClass = `transition-colors hover:bg-slate-50 ${
                          rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'
                        } ${isSelected ? '!bg-slate-100' : ''}`;

                        return (
                          <React.Fragment key={item.service_id}>
                          <tr className={rowClass}>
                            <td className="px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={!isReady}
                                onChange={() => toggleSelectRow(item.service_id)}
                                className={`w-3.5 h-3.5 rounded bg-white border-slate-300 text-slate-900 focus:ring-slate-400 ${
                                  isReady ? 'cursor-pointer' : 'opacity-30 cursor-not-allowed'
                                }`}
                              />
                            </td>
                            <td className="px-3 py-2 min-w-0">
                              <button
                                onClick={() => onSelectClient(item.client_id)}
                                className="font-bold text-slate-900 hover:text-slate-700 text-left text-xs transition-colors cursor-pointer leading-tight whitespace-normal break-words"
                              >
                                {item.last_name}, {item.first_name}
                              </button>
                              <div className="text-[9.5px] text-slate-600 font-mono mt-0.5 leading-snug break-words">
                                DOS: <strong className="text-slate-800">{item.dos}</strong> • ID: {item.client_id}
                              </div>
                            </td>
                            <td className="px-3 py-2 min-w-0">
                              <div className="font-mono text-slate-800 font-semibold text-xs leading-tight break-words">
                                {item.service_code} {item.modifier ? `-${item.modifier}` : ''}
                              </div>
                              <div className="text-[9.5px] text-slate-400 break-words leading-tight mt-0.5">
                                {item.service_name || 'Speech/Language Therapy'}
                              </div>
                            </td>
                            <td className="px-3 py-2 min-w-0">
                              <div className="text-xs text-slate-800 font-medium leading-tight break-words">{item.provider_name}</div>
                              <div className="text-[9.5px] text-slate-400 leading-tight mt-0.5 break-words">{item.provider_role}</div>
                            </td>
                            <td className="px-3 py-2 min-w-0">
                              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border font-semibold inline-block leading-tight whitespace-normal break-words ${routeInfo.bg}`}>
                                {routeInfo.label}
                              </span>
                              <div className="text-[9.5px] text-slate-400 mt-0.5 leading-tight break-words" title={item.primary_payer_name}>
                                {item.primary_payer_name}
                              </div>
                            </td>
                            <td className="px-2 py-2 min-w-0">
                              <div className="text-xs text-emerald-800 font-mono font-bold leading-tight">
                                ${Number(item.calculated_charge || 0).toFixed(2)}
                              </div>
                              <div className="text-[9.5px] text-slate-400 font-mono leading-tight mt-0.5">
                                {billingRateSubtitle(item)}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`text-[9px] font-mono px-2 py-0.5 rounded font-medium uppercase inline-block leading-snug whitespace-normal break-words ${
                                  isReady
                                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                    : item.readiness_status === 'PRIMARY_ADJUDICATED_COB_REQUIRED'
                                    ? 'bg-purple-50 text-purple-800 border border-purple-200'
                                    : item.readiness_status === 'HOLD_TPL_EVIDENCE_INSUFFICIENT'
                                    ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                    : item.readiness_status === 'DOWNSTREAM_CLAIM_ACTIVE'
                                    ? 'bg-slate-100 text-slate-700 border border-slate-200'
                                    : 'bg-slate-50 text-slate-700 border border-slate-200'
                                }`}
                              >
                                {isReady
                                  ? 'CLEARED TO BILL'
                                  : item.readiness_status === 'PRIMARY_ADJUDICATED_COB_REQUIRED'
                                  ? 'Ready for WellSky Review — COB Packet Required'
                                  : item.readiness_status === 'HOLD_TPL_EVIDENCE_INSUFFICIENT'
                                  ? 'TPL Evidence Insufficient'
                                  : item.readiness_status === 'DOWNSTREAM_CLAIM_ACTIVE'
                                  ? 'Downstream Claim Active'
                                  : item.readiness_status}
                              </span>
                            </td>
                            <td className="px-2 py-2 align-middle">
                              {isReady && (
                                <span className="text-[10px] text-emerald-700 flex items-center gap-1 font-medium leading-tight mb-1.5">
                                  <CheckCircle2 className="w-3 h-3 shrink-0" />
                                  <span>Gates satisfied</span>
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => setResolveServiceId(item.service_id)}
                                className="px-2 py-0.5 rounded bg-slate-900 hover:bg-slate-800 text-sky-200 text-[10px] font-bold inline-flex items-center gap-1 cursor-pointer whitespace-nowrap"
                              >
                                <span>Review / Resolve</span>
                                <ArrowRight className="w-2.5 h-2.5" />
                              </button>
                            </td>
                          </tr>
                          {showDetail && (
                            <tr className={`${rowClass} !border-t-0`}>
                              <td colSpan={8} className="px-3 pb-2.5 pt-0">
                                {showCobDetail ? (
                                  <div className="space-y-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                    <div className="flex items-center gap-1.5 text-purple-800 text-[11px] font-semibold">
                                      <CheckCircle2 className="w-3.5 h-3.5 text-purple-700 shrink-0" />
                                      <span>Primary Adjudication Verified</span>
                                    </div>
                                    <div className="text-[10px] text-amber-800 font-mono bg-amber-50 px-2 py-0.5 rounded border border-amber-200 leading-snug whitespace-normal break-words">
                                      Review the COB preview first. The secondary file stays Manual File Ready and is not transmitted.
                                    </div>
                                    {['ADMIN', 'BILLING', 'OWNER', 'OFFICE', 'DIRECTOR_OPERATIONS', 'EXECUTIVE', 'FINANCE_BILLING'].includes(userRole) && item.billing_route === 'CLAIMMD_PRIMARY_DDD_SECONDARY' && (
                                      <div className="flex flex-wrap items-center gap-1.5">
                                        <button
                                          onClick={() => handleOpenCobPreview(item.service_id)}
                                          disabled={loadingCobPreview}
                                          className="px-2.5 py-1 rounded bg-purple-600 hover:bg-purple-500 text-white text-[10px] font-bold inline-flex items-center gap-1.5 cursor-pointer transition-colors shadow-sm disabled:opacity-50"
                                        >
                                          <FileText className="w-3 h-3" />
                                          <span>Preview Secondary COB Packet</span>
                                        </button>
                                        <button
                                          onClick={() => handleGenerateDddSecondary(item.service_id)}
                                          disabled={generatingSecondaryServiceId === item.service_id}
                                          className="px-2.5 py-1 rounded bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-bold inline-flex items-center gap-1.5 cursor-pointer transition-colors shadow-sm disabled:opacity-50"
                                        >
                                          <span>{generatingSecondaryServiceId === item.service_id ? 'Generating...' : 'Generate DDD Secondary 837'}</span>
                                        </button>
                                      </div>
                                    )}
                                    {secondaryGenerateError?.serviceId === item.service_id && (
                                      <div className="text-[10px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                                        {secondaryGenerateError.message}
                                      </div>
                                    )}
                                  </div>
                                ) : showEobDetail ? (
                                  <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                    <span className="text-[9px] font-medium text-slate-700 bg-white px-1.5 py-0.5 rounded border border-slate-200 leading-none">
                                      Awaiting Primary Commercial EOB/ERA
                                    </span>
                                    <button
                                      onClick={() => openPaymentModalForService(item)}
                                      className="px-2 py-0.5 rounded bg-sky-600 hover:bg-sky-500 text-white text-[10px] font-bold inline-flex items-center gap-1 cursor-pointer transition-colors"
                                    >
                                      <CreditCard className="w-2.5 h-2.5" />
                                      <span>Post Primary EOB</span>
                                    </button>
                                  </div>
                                ) : (
                                  <div className="space-y-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                    {holdReasons.map((h, idx) => (
                                      <div key={idx} className="flex items-start gap-1.5 leading-snug min-w-0">
                                        <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0 mt-1" />
                                        <span className="text-[11px] text-slate-800 font-normal whitespace-normal break-words min-w-0">
                                          {h}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* MOBILE STACKED CARDS */}
                <div className="block md:hidden divide-y divide-slate-700/70">
                  {filteredReadiness.map((item, idx) => {
                    const isReady = !isClosedDisposition(item) && item.readiness_status === 'BILLING_READY';
                    const isSelected = selectedServiceIds.includes(item.service_id);
                    const routeInfo = formatRoute(item.billing_route);
                    const isRouteCWaiting = item.readiness_status === 'HOLD_PRIMARY_ADJUDICATION_PENDING';

                    return (
                      <div
                        key={`mobile-br-${item.service_id}`}
                        className={`p-3.5 space-y-2 transition-colors border-b border-slate-700/60 ${
                          isSelected ? 'mobile-ops-row-active border-sky-400/60' : `${idx % 2 === 0 ? 'mobile-ops-row-a' : 'mobile-ops-row-b'} hover:from-[#162238] hover:to-[#10192a]`
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 min-w-0">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={!isReady}
                              onChange={() => toggleSelectRow(item.service_id)}
                              className={`mt-0.5 w-4 h-4 rounded bg-slate-800 border-slate-700 text-sky-500 focus:ring-sky-500 shrink-0 ${
                                isReady ? 'cursor-pointer' : 'opacity-30 cursor-not-allowed'
                              }`}
                            />
                            <div className="min-w-0">
                              <button
                                onClick={() => onSelectClient(item.client_id)}
                                className="font-bold text-slate-100 hover:text-cyan-300 text-left text-xs transition-colors cursor-pointer leading-tight truncate block"
                              >
                                {item.last_name}, {item.first_name}
                              </button>
                              <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                                DOS: <strong className="text-slate-200">{item.dos}</strong> • ID: {item.client_id}
                              </div>
                            </div>
                          </div>

                          <span
                            className={`text-[9px] font-mono px-2 py-0.5 rounded font-medium uppercase shrink-0 leading-none ${
                              isReady
                                ? 'bg-emerald-950/50 text-emerald-300 border border-emerald-500/30'
                                : item.readiness_status === 'PRIMARY_ADJUDICATED_COB_REQUIRED'
                                ? 'bg-purple-950/70 text-purple-300 border border-purple-500/40'
                                : item.readiness_status === 'HOLD_TPL_EVIDENCE_INSUFFICIENT'
                                ? 'bg-amber-950/60 text-amber-300 border border-amber-500/40'
                                : item.readiness_status === 'DOWNSTREAM_CLAIM_ACTIVE'
                                ? 'bg-indigo-950/60 text-indigo-300 border border-indigo-500/30'
                                : 'bg-[#081324]/85 text-cyan-300/90 border border-cyan-500/35'
                            }`}
                          >
                            {isReady
                              ? 'CLEARED'
                              : item.readiness_status === 'PRIMARY_ADJUDICATED_COB_REQUIRED'
                              ? 'COB REVIEW REQ'
                              : item.readiness_status === 'HOLD_TPL_EVIDENCE_INSUFFICIENT'
                              ? 'TPL INSUFFICIENT'
                              : item.readiness_status === 'DOWNSTREAM_CLAIM_ACTIVE'
                              ? 'DOWNSTREAM ACTIVE'
                              : item.readiness_status.replace('HOLD_', '')}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-800/40">
                          <div className="min-w-0 flex-1">
                            <span className="font-mono text-slate-200 font-semibold text-xs">
                              {item.service_code} {item.modifier ? `-${item.modifier}` : ''}
                            </span>
                            <span className="text-[10px] text-slate-400 ml-1.5 truncate">
                              {item.service_name || 'Speech/Language Therapy'}
                            </span>
                          </div>
                          <div className="text-right shrink-0 font-mono">
                            <span className="text-xs text-emerald-400 font-bold">
                              ${Number(item.calculated_charge || 0).toFixed(2)}
                            </span>
                            <span className="text-[9.5px] text-slate-400 ml-1">
                              ({item.billable_units || 1}u)
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-[10px] text-slate-400">
                          <div className="truncate">
                            <span className="text-slate-300 font-medium">{item.provider_name}</span> ({item.provider_role})
                          </div>
                          <span className={`text-[8.5px] font-mono px-1.5 py-0.5 rounded border font-semibold shrink-0 leading-none ${routeInfo.bg}`}>
                            {routeInfo.label}
                          </span>
                        </div>

                        <div className="pt-1">
                          {isReady ? (
                            <div className="text-[10px] text-emerald-400 flex items-center gap-1 font-medium">
                              <CheckCircle2 className="w-3 h-3 shrink-0" />
                              <span>Gates satisfied — ready for 837 generation</span>
                            </div>
                          ) : item.readiness_status === 'PRIMARY_ADJUDICATED_COB_REQUIRED' ? (
                            <div className="p-2.5 rounded bg-purple-950/40 border border-purple-500/30 text-[10px] space-y-2">
                              <div className="text-purple-300 font-semibold flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3 text-purple-400 shrink-0" />
                                <span>Primary Adjudication Verified</span>
                              </div>
                              <div className="text-amber-300/90 font-mono text-[9.5px] leading-snug">
                                Review the COB preview first. The secondary file stays Manual File Ready and is not transmitted.
                              </div>
                              {['ADMIN', 'BILLING', 'OWNER', 'OFFICE', 'DIRECTOR_OPERATIONS', 'EXECUTIVE', 'FINANCE_BILLING'].includes(userRole) && item.billing_route === 'CLAIMMD_PRIMARY_DDD_SECONDARY' && (
                                <div className="space-y-1.5">
                                  <button
                                    onClick={() => handleOpenCobPreview(item.service_id)}
                                    disabled={loadingCobPreview}
                                    className="w-full px-2.5 py-1 rounded bg-purple-600 hover:bg-purple-500 text-white text-[10px] font-bold inline-flex items-center justify-center gap-1.5 cursor-pointer transition-colors shadow-sm disabled:opacity-50"
                                  >
                                    <FileText className="w-3 h-3" />
                                    <span>Preview Secondary COB Packet</span>
                                  </button>
                                  <button
                                    onClick={() => handleGenerateDddSecondary(item.service_id)}
                                    disabled={generatingSecondaryServiceId === item.service_id}
                                    className="w-full px-2.5 py-1 rounded bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-bold inline-flex items-center justify-center cursor-pointer transition-colors shadow-sm disabled:opacity-50"
                                  >
                                    <span>{generatingSecondaryServiceId === item.service_id ? 'Generating...' : 'Generate DDD Secondary 837'}</span>
                                  </button>
                                </div>
                              )}
                              {secondaryGenerateError?.serviceId === item.service_id && (
                                <div className="text-[10px] text-rose-200 bg-rose-950/50 border border-rose-500/40 rounded px-2 py-1">
                                  {secondaryGenerateError.message}
                                </div>
                              )}
                            </div>
                          ) : isRouteCWaiting ? (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-[9px] font-medium text-cyan-200/90 bg-[#081324]/85 px-1.5 py-0.5 rounded border border-cyan-500/30">
                                Awaiting Primary Commercial EOB
                              </span>
                              <button
                                onClick={() => openPaymentModalForService(item)}
                                className="px-2 py-0.5 rounded bg-sky-600 hover:bg-sky-500 text-white text-[10px] font-bold inline-flex items-center gap-1 cursor-pointer transition-colors"
                              >
                                <CreditCard className="w-2.5 h-2.5" />
                                <span>Post Primary EOB</span>
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-1 pt-1 border-t border-slate-800/40">
                              {item.hold_reasons?.map((h, idx) => (
                                <div key={idx} className="flex items-start gap-1.5 leading-snug min-w-0">
                                  <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0 mt-1" />
                                  <span className="text-[11px] text-slate-300 font-normal whitespace-normal break-words min-w-0">
                                    {h}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => setResolveServiceId(item.service_id)}
                            className="mt-1 px-2 py-0.5 rounded bg-slate-900 hover:bg-slate-800 text-sky-200 text-[10px] font-bold inline-flex items-center gap-1 cursor-pointer"
                          >
                            <span>Review / Resolve</span>
                            <ArrowRight className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* VIEW 2: CLAIMS & 837 EDI — GENERATED FILE HISTORY                         */}
      {/* ========================================================================= */}
      {activeTab === 'Claims' && (
        <div className="space-y-3">
          {/* Header Description & Transmission Status */}
          <div className="relative overflow-hidden p-2.5 sm:px-3 sm:py-2 rounded-xl border border-slate-200/90 bg-white text-xs text-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-2 shadow-xs">
            <div className="flex items-center space-x-2">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
              <div>
                <span className="font-bold text-slate-900 text-xs">Generated 837 EDI Batch History & Claim Transmission Archive</span>
                <span className="text-[11px] text-slate-600 ml-2 hidden md:inline">
                  — Immutable batches with cryptographic SHA-256 integrity verification
                </span>
              </div>
            </div>
            <span className="text-[9.5px] font-mono px-2 py-0.5 rounded bg-amber-50 text-amber-800 font-bold border border-amber-200 whitespace-nowrap self-start sm:self-auto leading-none">
              LIVE TRANSMISSION OFF • FILE DOWNLOAD ONLY
            </span>
          </div>

          {/* Route Filter Pills */}
          <div className="flex flex-wrap items-center gap-1.5 p-1 bg-white border border-slate-200/90 rounded-xl shadow-xs">
            <button
              onClick={() => setBatchRouteFilter('ALL')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                batchRouteFilter === 'ALL'
                  ? 'bg-slate-900 text-white border border-slate-800 shadow-xs'
                  : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50 border border-transparent'
              }`}
            >
              <span>All Batches</span>
              <span
                className={`font-mono text-[10px] px-1.5 py-0.2 rounded-full border ${
                  batchRouteFilter === 'ALL'
                    ? 'bg-white/15 text-white border-white/20'
                    : 'bg-slate-100 text-slate-600 border-slate-200'
                }`}
              >
                {batches.length}
              </span>
            </button>

            <button
              onClick={() => setBatchRouteFilter('WELLSKY_DDD')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                batchRouteFilter === 'WELLSKY_DDD'
                  ? 'bg-slate-900 text-white border border-slate-800 shadow-xs'
                  : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50 border border-transparent'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
              <span>WellSky / DDD Batches</span>
              <span
                className={`font-mono text-[10px] px-1.5 py-0.2 rounded-full border ${
                  batchRouteFilter === 'WELLSKY_DDD'
                    ? 'bg-white/15 text-white border-white/20'
                    : 'bg-slate-100 text-slate-600 border-slate-200'
                }`}
              >
                {batches.filter((b) => b.billing_route === 'WELLSKY_DDD').length}
              </span>
            </button>

            <button
              onClick={() => setBatchRouteFilter('CLAIMMD_COMMERCIAL')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                batchRouteFilter === 'CLAIMMD_COMMERCIAL'
                  ? 'bg-slate-900 text-white border border-slate-800 shadow-xs'
                  : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50 border border-transparent'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
              <span>Claim.MD / Private Batches</span>
              <span
                className={`font-mono text-[10px] px-1.5 py-0.2 rounded-full border ${
                  batchRouteFilter === 'CLAIMMD_COMMERCIAL'
                    ? 'bg-white/15 text-white border-white/20'
                    : 'bg-slate-100 text-slate-600 border-slate-200'
                }`}
              >
                {batches.filter((b) => b.billing_route === 'CLAIMMD_COMMERCIAL').length}
              </span>
            </button>

            <button
              onClick={() => setBatchRouteFilter('CLAIMMD_PRIMARY_DDD_SECONDARY')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                batchRouteFilter === 'CLAIMMD_PRIMARY_DDD_SECONDARY'
                  ? 'bg-slate-900 text-white border border-slate-800 shadow-xs'
                  : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50 border border-transparent'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
              <span>DDD Secondary / COB Batches</span>
              <span
                className={`font-mono text-[10px] px-1.5 py-0.2 rounded-full border ${
                  batchRouteFilter === 'CLAIMMD_PRIMARY_DDD_SECONDARY'
                    ? 'bg-white/15 text-white border-white/20'
                    : 'bg-slate-100 text-slate-600 border-slate-200'
                }`}
              >
                {batches.filter((b) => b.billing_route === 'CLAIMMD_PRIMARY_DDD_SECONDARY').length}
              </span>
            </button>
          </div>

          {/* Batches Table List */}
          {filteredBatches.length === 0 ? (
            <div className="py-6 px-4 text-center text-xs text-slate-600 rounded-xl border border-slate-200/90 bg-white shadow-xs">
              <FileCheck className="w-6 h-6 text-slate-500 mx-auto mb-1.5" />
              <p className="font-semibold text-slate-800">No generated batches found in this category.</p>
              <p className="text-[10.5px] text-slate-600 mt-0.5">
                Generate batches from the 'Billing Readiness Queue' tab.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200/90 bg-white shadow-xs overflow-hidden">
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-[9.5px] uppercase text-slate-700 border-b border-slate-200">
                  <tr>
                    <th className="px-3.5 py-2 font-bold tracking-wider">Batch #</th>
                    <th className="px-3.5 py-2 font-bold tracking-wider">Payer & Route</th>
                    <th className="px-3.5 py-2 font-bold tracking-wider">File & Integrity</th>
                    <th className="px-3.5 py-2 font-bold tracking-wider">Dates of Service</th>
                    <th className="px-3.5 py-2 font-bold tracking-wider text-right">Charges & Claims</th>
                    <th className="px-3.5 py-2 font-bold tracking-wider text-center">Status</th>
                    <th className="px-3.5 py-2 font-bold tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {filteredBatches.map((batch, idx) => {
                    const isExpanded = expandedBatchIds.includes(batch.id);
                    const routeInfo = formatRoute(batch.billing_route);
                    const batchClaims = claims.filter((c) => c.batch_id === batch.id);

                    return (
                      <React.Fragment key={batch.id}>
                        <tr
                          className={`hover:bg-[#20314a] transition-all duration-150 group ${
                            isExpanded
                              ? 'ops-row-active'
                              : idx % 2 === 1
                              ? 'ops-row-b'
                              : 'ops-row-a'
                          }`}
                        >
                          {/* Batch # & Created */}
                          <td className="px-3.5 py-2">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => toggleBatchExpanded(batch.id)}
                                className="p-0.5 rounded hover:bg-slate-800/80 text-slate-400 hover:text-white transition-colors cursor-pointer shrink-0"
                                title={isExpanded ? 'Collapse claims' : 'Expand claims'}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-3.5 h-3.5 text-slate-600" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-slate-800" />
                                )}
                              </button>
                              <FileText className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                              <div className="min-w-0">
                                <span className="font-mono font-bold text-white text-xs tracking-tight leading-none block">
                                  {batch.batch_number}
                                </span>
                                <span className="text-[9.5px] text-slate-400 font-mono mt-0.5 block leading-none">
                                  {batch.generated_at}
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* Payer & Route */}
                          <td className="px-3.5 py-2">
                            <div
                              className="font-sans text-xs font-semibold text-slate-200 truncate max-w-[200px] leading-tight"
                              title={batch.payer_name || batch.payer_id}
                            >
                              {batch.payer_name || batch.payer_id}
                            </div>
                            <div className="mt-0.5 flex items-center gap-1.5 leading-none">
                              <span
                                className={`text-[9px] font-mono px-1.5 py-0.5 rounded border font-semibold leading-none ${routeInfo.bg}`}
                              >
                                {routeInfo.label}
                              </span>
                            </div>
                          </td>

                          {/* File & Integrity */}
                          <td className="px-3.5 py-2">
                            <div
                              className="text-slate-300 font-mono text-[11px] truncate max-w-[190px] leading-tight"
                              title={batch.filename}
                            >
                              <strong className="text-slate-200 font-normal">{batch.filename}</strong>
                            </div>
                            <div className="text-[10px] text-slate-400 font-mono mt-0.5 flex items-center gap-1 leading-none">
                              {batch.file_hash ? (
                                <span title={batch.file_hash} className="truncate max-w-[170px] text-slate-400">
                                  SHA: <strong className="text-slate-300 font-normal">{batch.file_hash.substring(0, 10)}...</strong>
                                </span>
                              ) : (
                                <span className="text-slate-500">—</span>
                              )}
                            </div>
                          </td>

                          {/* Dates of Service */}
                          <td className="px-3.5 py-2 font-mono text-xs">
                            <div className="text-slate-300 font-medium leading-tight">
                              <strong className="text-slate-200 font-normal">{batch.dos_start}</strong>
                              {batch.dos_end && batch.dos_end !== batch.dos_start ? (
                                <>
                                  <span className="text-slate-500 mx-1">→</span>
                                  <strong className="text-slate-200 font-normal">{batch.dos_end}</strong>
                                </>
                              ) : null}
                            </div>
                          </td>

                          {/* Charges & Claims */}
                          <td className="px-3.5 py-2 text-right whitespace-nowrap">
                            <div className="text-xs font-bold text-emerald-400 font-mono leading-tight">
                              ${Number(batch.total_charges || 0).toFixed(2)}
                            </div>
                            <div className="text-[9.5px] text-slate-400 font-mono leading-tight mt-0.5">
                              {batch.claim_count} claim{batch.claim_count !== 1 ? 's' : ''}
                            </div>
                          </td>

                          {/* Status */}
                          <td className="px-3.5 py-2 text-center whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold bg-emerald-950/70 text-emerald-300 border border-emerald-500/35 shadow-[0_0_6px_rgba(16,185,129,0.15)] leading-none">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                              {batch.status}
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="px-3.5 py-2 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end space-x-1.5">
                              <button
                                onClick={() => setInspectBatch(batch)}
                                className="px-2 py-1 rounded-md bg-cyan-950/70 hover:bg-cyan-900/80 text-cyan-300 hover:text-cyan-200 border border-cyan-500/40 text-[11px] font-semibold inline-flex items-center gap-1 transition-all cursor-pointer active:scale-95"
                                title="View ANSI ASC X12 837 EDI payload"
                              >
                                <Eye className="w-3 h-3" />
                                <span>EDI</span>
                              </button>

                              <button
                                onClick={() => handleDownloadBatchById(batch.id, batch.filename)}
                                className="px-2.5 py-1 rounded-md bg-emerald-950/70 hover:bg-emerald-900/80 text-emerald-300 hover:text-emerald-200 border border-emerald-500/40 text-[11px] font-semibold inline-flex items-center gap-1 shadow-[0_0_8px_rgba(16,185,129,0.15)] transition-all cursor-pointer active:scale-95"
                                title="Download 837 EDI batch text file"
                              >
                                <Download className="w-3 h-3" />
                                <span>Download</span>
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expandable Claims Table Inside Batch */}
                        {isExpanded && (
                          <tr key={`${batch.id}-expanded`} className="bg-[#050912]/95 border-b border-slate-800/80">
                            <td
                              colSpan={7}
                              className="p-3 sm:p-4 bg-gradient-to-b from-[#060a14] to-[#04070d] border-t border-cyan-500/20 shadow-[inset_0_2px_8px_rgba(0,0,0,0.6)]"
                            >
                              <div className="text-xs font-semibold text-slate-300 mb-2 flex items-center justify-between">
                                <span className="text-[11px] font-bold text-white flex items-center gap-1.5">
                                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_6px_rgba(6,182,212,0.6)]" />
                                  <span>Claims in Batch ({batchClaims.length})</span>
                                </span>
                                <span className="text-[10px] text-slate-400 font-mono">
                                  Immutable audit records • 837 EDI Constructed
                                </span>
                              </div>
                              {batchClaims.length === 0 ? (
                                <div className="py-3 text-center text-xs text-slate-400">
                                  No individual claim details attached to this batch record.
                                </div>
                              ) : (
                                <div className="overflow-x-auto rounded-lg border border-slate-800/80 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]">
                                  <table className="w-full text-left text-xs">
                                    <thead className="relative bg-gradient-to-b from-[#111927] via-[#0d1422] to-[#090d16] text-[9.5px] uppercase font-bold text-slate-300 border-b border-slate-700/80 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]">
                                      <tr>
                                        <th className="px-3 py-1.5 font-bold">Claim #</th>
                                        <th className="px-3 py-1.5 font-bold">Client / DOS</th>
                                        <th className="px-3 py-1.5 font-bold">CPT / Service</th>
                                        <th className="px-3 py-1.5 font-bold text-right">Billed / Charge</th>
                                        <th className="px-3 py-1.5 font-bold">Paid / Bal</th>
                                        <th className="px-3 py-1.5 font-bold text-center">Status</th>
                                        <th className="px-3 py-1.5 font-bold text-right">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800/60">
                                      {batchClaims.map((c, cIdx) => (
                                        <tr key={c.id} className={`hover:bg-[#20314a] transition-colors ${cIdx % 2 === 0 ? 'ops-row-a' : 'ops-row-b'}`}>
                                          <td className="px-3 py-1.5 font-mono text-cyan-400 font-bold text-xs whitespace-nowrap">
                                            {c.claim_number}
                                            {c.claim_frequency_code === '7' && (
                                              <span className="ml-1.5 text-[8.5px] px-1 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-semibold">
                                                FREQ 7
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-3 py-1.5">
                                            <div className="font-semibold text-slate-100 text-xs leading-tight">
                                              {c.last_name}, {c.first_name}
                                            </div>
                                            <div className="text-[9.5px] text-slate-400 font-mono mt-0.5 leading-none">
                                              DOS: <strong className="text-slate-300">{c.dos}</strong>
                                            </div>
                                          </td>
                                          <td className="px-3 py-1.5">
                                            <span className="font-mono text-xs font-semibold text-slate-200">
                                              {c.service_code || '92507'}
                                              {c.modifier ? `-${c.modifier}` : ''}
                                            </span>
                                          </td>
                                          <td className="px-3 py-1.5 font-mono text-slate-100 font-bold text-xs whitespace-nowrap text-right">
                                            ${Number(c.billed_amount || 0).toFixed(2)}
                                          </td>
                                          <td className="px-3 py-1.5 whitespace-nowrap">
                                            <span className="font-mono text-emerald-400 font-bold text-xs">
                                              ${Number(c.amount_paid || 0).toFixed(2)}
                                            </span>
                                            <span className="text-[9.5px] text-slate-400 font-mono ml-1">
                                              (bal: ${Number(c.open_balance ?? c.billed_amount).toFixed(2)})
                                            </span>
                                          </td>
                                          <td className="px-3 py-1.5 whitespace-nowrap text-center">
                                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-sky-950/70 text-sky-300 border border-sky-600/40 font-bold uppercase leading-none inline-block">
                                              {c.status}
                                            </span>
                                          </td>
                                          <td className="px-3 py-1.5 text-right whitespace-nowrap space-x-1">
                                            <button
                                              onClick={() => setInspectClaim(c)}
                                              className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-sky-300 border border-slate-700 text-[10px] font-semibold cursor-pointer transition-colors"
                                            >
                                              Inspect
                                            </button>
                                            <button
                                              onClick={() => handleOpenClaimAdjudications(c)}
                                              className="px-2 py-0.5 rounded bg-indigo-950/80 hover:bg-indigo-900 text-indigo-300 border border-indigo-700/60 text-[10px] font-semibold cursor-pointer transition-colors"
                                              title="View primary adjudication evidence and history"
                                            >
                                              Adj Evidence
                                            </button>
                                            {c.status !== 'PAID' && (
                                              <button
                                                onClick={() => openPaymentModalForClaim(c)}
                                                className="px-2 py-0.5 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-[10px] font-bold cursor-pointer transition-colors"
                                              >
                                                Post Pay
                                              </button>
                                            )}
                                            <button
                                              onClick={() => {
                                                setSelectedClaimForReplacement(c);
                                                setShowReplacementModal(true);
                                              }}
                                              className="px-2 py-0.5 rounded bg-amber-950/50 hover:bg-amber-900/70 text-amber-300 border border-amber-800/40 text-[10px] font-semibold cursor-pointer transition-colors"
                                              title="Create Replacement Claim (Frequency 7)"
                                            >
                                              Correct (7)
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* MOBILE STACKED BATCH CARDS */}
            <div className="block md:hidden divide-y divide-slate-700/70">
              {filteredBatches.map((batch, idx) => {
                const isExpanded = expandedBatchIds.includes(batch.id);
                const routeInfo = formatRoute(batch.billing_route);
                const batchClaims = claims.filter((c) => c.batch_id === batch.id);

                return (
                  <div key={`mobile-batch-${batch.id}`} className={`p-3.5 ${idx % 2 === 0 ? 'mobile-ops-row-a' : 'mobile-ops-row-b'} space-y-2.5 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                          <span className="font-mono font-bold text-white text-xs">
                            {batch.batch_number}
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                          {batch.generated_at}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="px-2 py-0.5 rounded-full text-[8.5px] font-mono font-bold uppercase bg-emerald-950/80 text-emerald-300 border border-emerald-500/40">
                          {batch.status || 'GENERATED'}
                        </span>
                        <button
                          onClick={() => handleDownloadBatchById(batch.id, batch.filename)}
                          className="p-1 rounded bg-emerald-950/70 hover:bg-emerald-900/80 text-emerald-300 border border-emerald-500/40 cursor-pointer"
                          title="Download 837 EDI file"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-700/50">
                      <div className="min-w-0 flex-1 truncate">
                        <div className="text-slate-200 font-semibold text-xs truncate">
                          {batch.payer_name || batch.payer_id}
                        </div>
                        <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                          DOS: {batch.dos_start}{batch.dos_end && batch.dos_end !== batch.dos_start ? ` → ${batch.dos_end}` : ''}
                        </div>
                      </div>
                      <div className="text-right shrink-0 font-mono">
                        <div className="text-xs text-emerald-400 font-bold">
                          ${Number(batch.total_charges || 0).toFixed(2)}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {batch.claim_count} claims
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-1">
                      <span className={`text-[8.5px] font-mono px-1.5 py-0.5 rounded border font-semibold ${routeInfo.bg}`}>
                        {routeInfo.label}
                      </span>

                      <button
                        onClick={() => toggleBatchExpanded(batch.id)}
                        className="text-[10.5px] text-cyan-300 font-semibold inline-flex items-center gap-1 cursor-pointer hover:underline"
                      >
                        <span>{isExpanded ? 'Hide Claims' : `View Claims (${batchClaims.length})`}</span>
                        {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      </button>
                    </div>

                    {/* Expanded Mobile Claims */}
                    {isExpanded && (
                      <div className="pt-2 border-t border-slate-700/70 space-y-2">
                        <div className="text-[10px] font-bold text-slate-300 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                          <span>Claims in Batch ({batchClaims.length})</span>
                        </div>

                        {batchClaims.length === 0 ? (
                          <div className="text-center py-2 text-slate-500 text-[10.5px]">
                            No individual claim details.
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            {batchClaims.map((c) => (
                              <div key={`mb-clm-${c.id}`} className="p-2.5 rounded-lg bg-[#243854] border border-slate-600/70 text-xs space-y-1.5 shadow-sm">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <div className="font-semibold text-slate-100 text-xs">
                                      {c.last_name}, {c.first_name}
                                    </div>
                                    <div className="text-[10px] text-cyan-400 font-mono mt-0.5">
                                      {c.claim_number} • DOS: {c.dos}
                                    </div>
                                  </div>
                                  <span className="text-[8.5px] font-mono px-1.5 py-0.5 rounded bg-sky-950/70 text-sky-300 border border-sky-600/40 uppercase font-bold shrink-0">
                                    {c.status}
                                  </span>
                                </div>

                                <div className="flex items-center justify-between text-[11px] font-mono pt-1 border-t border-slate-800/40">
                                  <span className="text-slate-300">
                                    CPT {c.service_code || '92507'}{c.modifier ? `-${c.modifier}` : ''}
                                  </span>
                                  <div className="space-x-1">
                                    <span className="text-slate-100 font-bold">${Number(c.billed_amount || 0).toFixed(2)}</span>
                                    <span className="text-emerald-400 font-semibold">(pd: ${Number(c.amount_paid || 0).toFixed(2)})</span>
                                  </div>
                                </div>

                                <div className="flex items-center justify-end gap-1.5 pt-1">
                                  <button
                                    onClick={() => setInspectClaim(c)}
                                    className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-sky-300 border border-slate-700 text-[10px] font-semibold cursor-pointer"
                                  >
                                    Inspect
                                  </button>
                                  <button
                                    onClick={() => handleOpenClaimAdjudications(c)}
                                    className="px-2 py-0.5 rounded bg-indigo-950/80 hover:bg-indigo-900 text-indigo-300 border border-indigo-700/60 text-[10px] font-semibold cursor-pointer"
                                  >
                                    Adj Evidence
                                  </button>
                                  {c.status !== 'PAID' && (
                                    <button
                                      onClick={() => openPaymentModalForClaim(c)}
                                      className="px-2 py-0.5 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-[10px] font-bold cursor-pointer"
                                    >
                                      Post Pay
                                    </button>
                                  )}
                                  <button
                                    onClick={() => {
                                      setSelectedClaimForReplacement(c);
                                      setShowReplacementModal(true);
                                    }}
                                    className="px-2 py-0.5 rounded bg-amber-950/50 hover:bg-amber-900/70 text-amber-300 border border-amber-800/40 text-[10px] font-semibold cursor-pointer"
                                  >
                                    Correct (7)
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* VIEW 3: PAYMENT & EOB ADJUDICATION LEDGER                                 */}
      {/* ========================================================================= */}
      {activeTab === 'Payments' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl border border-slate-200 bg-white shadow-xs">
            <div>
              <h2 className="text-sm font-bold text-slate-900 flex items-center space-x-2">
                <CreditCard className="w-4 h-4 text-emerald-700" />
                <span>Payment Adjudication Ledger & EOB Reconciliation</span>
              </h2>
              <p className="text-xs text-slate-600 mt-0.5">
                Record real private-payer adjudication, contractual write-offs, copays, deductibles, or denial reasons.
              </p>
            </div>
            <div className="flex items-center space-x-2 self-start sm:self-auto">
              <button
                onClick={() => setShowCreatePaymentHeaderModal(true)}
                className="px-3 py-1.5 rounded-lg bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 text-xs font-semibold flex items-center space-x-1.5 cursor-pointer shadow-xs"
              >
                <Plus className="w-3.5 h-3.5 text-slate-600" />
                <span>+ Record Payment Header</span>
              </button>
              <button
                onClick={() => {
                  setSelectedClaimForPayment(null);
                  setSelectedServiceForPayment(null);
                  setPaymentForm({
                    claimId: claims.length > 0 ? claims[0].id : '',
                    serviceId: '',
                    payerName: 'UnitedHealthcare',
                    clientName: claims.length > 0 ? `${claims[0].last_name}, ${claims[0].first_name}` : 'Client',
                    dos: new Date().toISOString().split('T')[0],
                    serviceCode: '92507',
                    amountBilled: 65.0,
                    amountPaid: 55.25,
                    allowedAmount: 55.25,
                    patientResp: 0.0,
                    deductible: 0.0,
                    coinsurance: 0.0,
                    copay: 0.0,
                    adjustment: 9.75,
                    paymentDate: new Date().toISOString().split('T')[0],
                    checkNumber: 'EFT-772910',
                    eraRef: 'ERA-835-9921',
                    eobRef: 'EOB-PRIVATE-PAYER',
                    denialReason: '',
                    notes: 'Direct EOB adjudication posted'
                  });
                  setShowPaymentModal(true);
                }}
                className="px-3 py-1.5 rounded-lg bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 text-xs font-semibold flex items-center space-x-1.5"
              >
                <span>+ Post Claim Adjudication</span>
              </button>
              <button
                onClick={() => {
                  setShowArtifactModal(true);
                  if (artifactLines.length === 0) addArtifactLine();
                }}
                className="px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-bold flex items-center space-x-1.5"
              >
                <UploadCloud className="w-3.5 h-3.5" />
                <span>Upload Multi-Client EOB</span>
              </button>
            </div>
          </div>

          {/* SECTION: POSTED PAYMENT HEADERS (CHECK / EFT DEPOSITS) */}
          <div className="rounded-xl overflow-hidden border border-slate-200 bg-white shadow-xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-2.5 bg-gradient-to-r from-[#0a1324] via-[#070d1a] to-[#050912] border-b border-slate-800">
              <div>
                <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
                  <CreditCard className="w-3.5 h-3.5 text-slate-200" />
                  <span>Posted Payment & Check Headers (Deposit Records)</span>
                  <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white border border-white/20">
                    {paymentHeaders.length} headers
                  </span>
                </h3>
                <p className="text-[11px] text-slate-300 mt-0.5">
                  Incoming remittances, checks, and EFT deposits. Note: <strong className="text-amber-200 font-semibold">Payment posted ≠ reconciled</strong>. Claim-level balance allocation occurs during adjudication matching.
                </p>
              </div>
              <button
                onClick={() => setShowCreatePaymentHeaderModal(true)}
                className="px-2.5 py-1 rounded bg-white hover:bg-slate-100 text-slate-900 text-[11px] font-bold flex items-center gap-1 shrink-0 self-start sm:self-auto cursor-pointer"
              >
                <Plus className="w-3 h-3" />
                <span>New Payment Header</span>
              </button>
            </div>

            {paymentHeaders.length === 0 ? (
              <div className="py-6 px-4 text-center text-xs text-slate-600">
                <p className="font-semibold text-slate-800">No payment headers posted yet.</p>
                <p className="text-[10.5px] text-slate-600 mt-0.5">
                  Record incoming checks or EFT deposits directly or link them when creating canonical EOBs.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto p-4">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-[9.5px] uppercase font-bold text-slate-700 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2">Header ID</th>
                      <th className="px-3 py-2">Payer</th>
                      <th className="px-3 py-2">Payment Date</th>
                      <th className="px-3 py-2">Check / EFT #</th>
                      <th className="px-3 py-2">Total Amount</th>
                      <th className="px-3 py-2">Method</th>
                      <th className="px-3 py-2">Linked EOBs</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {paymentHeaders.map((ph) => (
                      <tr key={ph.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{ph.id}</td>
                        <td className="px-3 py-2 font-semibold text-slate-900">{ph.payer_name}</td>
                        <td className="px-3 py-2 font-mono text-slate-700">{ph.payment_date}</td>
                        <td className="px-3 py-2 font-mono text-sky-800">{ph.check_or_eft_number || 'N/A'}</td>
                        <td className="px-3 py-2 font-mono text-emerald-800 font-bold">
                          ${Number(ph.total_payment_amount || 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2">
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-slate-100 text-slate-800 border border-slate-200">
                            {ph.payment_method}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-sky-50 text-sky-800 border border-sky-200">
                            {ph.linked_eob_count || 0} EOB(s)
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                            {ph.status} (Unallocated)
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="rounded-xl overflow-hidden border border-slate-200 bg-white shadow-xs">
            {payments.length === 0 ? (
              <div className="p-10 text-center text-xs text-slate-600">
                <CreditCard className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                <p className="font-semibold text-slate-800">No payment records posted yet.</p>
                <p className="text-[11px] text-slate-600 mt-1">
                  Post payments against claims or upload an EOB/ERA remittance artifact.
                </p>
              </div>
            ) : (
              <div>
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-gradient-to-b from-[#111928] to-[#0b101b] text-[9.5px] uppercase font-bold text-slate-200 border-b border-slate-700/70">
                      <tr>
                        <th className="px-5 py-3 font-bold">Payment Date / Check #</th>
                        <th className="px-5 py-3 font-bold">Claim # / Client</th>
                        <th className="px-5 py-3 font-bold">Billed</th>
                        <th className="px-5 py-3 font-bold">Allowed</th>
                        <th className="px-5 py-3 font-bold">Paid</th>
                        <th className="px-5 py-3 font-bold">Write-off / Patient</th>
                        <th className="px-5 py-3 font-bold">EOB / Artifact Ref</th>
                        <th className="px-5 py-3 font-bold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {payments.map((p, pIdx) => (
                        <tr key={p.id} className={`hover:bg-slate-50 transition-colors ${pIdx % 2 === 0 ? 'ops-row-a' : 'ops-row-b'}`}>
                          <td className="px-5 py-3.5">
                            <div className="font-semibold text-slate-900">{p.payment_date}</div>
                            <div className="text-[10px] font-mono text-slate-600">
                              {p.check_or_eft_number || 'EFT-N/A'}
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="font-mono text-sky-800 font-semibold">{p.claim_number || 'Direct Svc Post'}</div>
                            <div className="text-slate-700 text-xs mt-0.5">
                              {p.last_name ? `${p.last_name}, ${p.first_name}` : 'Client Record'}
                            </div>
                          </td>
                          <td className="px-5 py-3.5 font-mono text-slate-700">
                            ${Number(p.amount_billed || 0).toFixed(2)}
                          </td>
                          <td className="px-5 py-3.5 font-mono text-slate-700">
                            ${Number(p.allowed_amount || p.amount_paid || 0).toFixed(2)}
                          </td>
                          <td className="px-5 py-3.5 font-mono text-emerald-800 font-bold">
                            ${Number(p.amount_paid || 0).toFixed(2)}
                          </td>
                          <td className="px-5 py-3.5 font-mono text-amber-800">
                            ${Number(p.contractual_adjustment || 0).toFixed(2)}
                            <div className="text-[9px] text-slate-600">
                              Pt: ${Number(p.patient_responsibility || 0).toFixed(2)}
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="text-slate-700 text-[11px] truncate max-w-[160px]">
                              {p.artifact_filename || p.eob_reference || p.era_835_reference || 'Manual Post'}
                            </div>
                            {p.denial_reason && (
                              <span className="text-[9px] text-rose-800 font-mono block">
                                Reason: {p.denial_reason}
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3.5">
                            <span
                              className={`text-[10px] font-mono px-2 py-0.5 rounded font-bold uppercase border ${
                                p.status === 'POSTED' || p.status === 'RECONCILED'
                                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                  : p.status === 'DENIED'
                                  ? 'bg-rose-50 text-rose-800 border-rose-200'
                                  : 'bg-amber-50 text-amber-900 border-amber-200'
                              }`}
                            >
                              {p.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* MOBILE STACKED CARDS */}
                <div className="block md:hidden divide-y divide-slate-200">
                  {payments.map((p, idx) => (
                    <div key={`mb-pay-${p.id}`} className={`p-3.5 ${idx % 2 === 0 ? 'mobile-ops-row-a' : 'mobile-ops-row-b'} space-y-2`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-semibold text-slate-900 text-xs">
                            {p.last_name ? `${p.last_name}, ${p.first_name}` : 'Client Record'}
                          </div>
                          <div className="text-[10px] text-sky-800 font-mono mt-0.5">
                            {p.claim_number || 'Direct Svc Post'} • {p.payment_date}
                          </div>
                        </div>
                        <span
                          className={`text-[9.5px] font-mono px-2 py-0.5 rounded font-bold uppercase shrink-0 leading-none border ${
                            p.status === 'POSTED' || p.status === 'RECONCILED'
                              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                              : p.status === 'DENIED'
                              ? 'bg-rose-50 text-rose-800 border-rose-200'
                              : 'bg-amber-50 text-amber-900 border-amber-200'
                          }`}
                        >
                          {p.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-200">
                        <div className="text-[10px] text-slate-600 font-mono">
                          Ref: {p.check_or_eft_number || 'EFT-N/A'}
                        </div>
                        <div className="text-right font-mono">
                          <span className="text-xs text-emerald-800 font-bold">
                            ${Number(p.amount_paid || 0).toFixed(2)}
                          </span>
                          <span className="text-[10px] text-slate-600 ml-1.5">
                            (Billed: ${Number(p.amount_billed || 0).toFixed(2)})
                          </span>
                        </div>
                      </div>
                      {(p.artifact_filename || p.eob_reference || p.era_835_reference) && (
                        <div className="text-[10px] text-slate-600 truncate">
                          Artifact: <span className="text-slate-800">{p.artifact_filename || p.eob_reference || p.era_835_reference}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* VIEW 4: EOB BATCHES & CANONICAL REMITTANCE ARCHIVE                        */}
      {/* ========================================================================= */}
      {activeTab === 'Remittances' && (
        <div className="space-y-4">
          {/* Header Bar */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 p-4 mobile-ops-shell md:bg-slate-900/60 rounded-xl border border-slate-600/80 md:border-slate-800">
            <div>
              <h2 className="text-sm font-bold text-white flex items-center space-x-2">
                <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
                <span>EOB / Remittance Document Archive</span>
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Scanned multi-page EOB batch intake, logical canonical EOB segmentation, client attachments, and immutable PDF lineage.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
              <button
                onClick={() => setShowUploadBatchModal(true)}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center space-x-1.5 shadow-lg shadow-emerald-600/20 cursor-pointer"
              >
                <FileUp className="w-3.5 h-3.5" />
                <span>+ Upload Scanned PDF Batch</span>
              </button>
              <button
                onClick={() => {
                  setCanonicalForm({
                    sourceBatchId: '',
                    sourcePageStart: 1,
                    sourcePageEnd: 1,
                    payerName: '',
                    receivedDate: new Date().toISOString().split('T')[0],
                    paymentDate: '',
                    totalPaymentAmount: '',
                    checkOrEftNumber: '',
                    filename: '',
                    notes: '',
                    reviewStatus: 'NEEDS_REVIEW',
                    paymentHeaderOption: 'NONE',
                    paymentHeaderId: '',
                    newHeaderPayer: '',
                    newHeaderDate: new Date().toISOString().split('T')[0],
                    newHeaderAmount: '',
                    newHeaderCheckNumber: '',
                    newHeaderMethod: 'EFT',
                    selectedClientIds: []
                  });
                  setShowCreateCanonicalModal(true);
                }}
                className="px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold flex items-center space-x-1.5 shadow-md cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>+ Create Canonical EOB</span>
              </button>
            </div>
          </div>

          {/* Sub-tab Navigation */}
          <div className="flex items-center gap-1.5 p-1 bg-slate-900/60 rounded-xl border border-slate-700/60 w-fit">
            <button
              onClick={() => setRemittanceSubTab('CANONICAL')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                remittanceSubTab === 'CANONICAL'
                  ? 'bg-sky-500/20 text-sky-200 border border-sky-500/50 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200 border border-transparent'
              }`}
            >
              <span>Canonical EOB Records</span>
              <span className="font-mono text-[10px] px-1.5 py-0.2 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                {canonicalEobs.length}
              </span>
            </button>
            <button
              onClick={() => setRemittanceSubTab('BATCHES')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                remittanceSubTab === 'BATCHES'
                  ? 'bg-sky-500/20 text-sky-200 border border-sky-500/50 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200 border border-transparent'
              }`}
            >
              <span>Scanned Source Batches</span>
              <span className="font-mono text-[10px] px-1.5 py-0.2 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                {eobBatches.length}
              </span>
            </button>
          </div>

          {/* SUB-TAB 1: CANONICAL EOB RECORDS */}
          {remittanceSubTab === 'CANONICAL' && (
            <div className="rounded-xl overflow-hidden border border-slate-600/90 md:border-slate-800 mobile-ops-shell md:bg-transparent md:glass-panel shadow-[0_4px_20px_rgba(0,0,0,0.35)]">
              {canonicalEobs.length === 0 ? (
                <div className="p-10 text-center text-xs text-slate-400">
                  <FileSpreadsheet className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                  <p className="font-semibold text-slate-300">No canonical EOB records segmented yet.</p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Upload a scanned multi-page EOB batch and define logical EOB segments, or create a direct canonical EOB.
                  </p>
                  <div className="mt-4 flex justify-center gap-2">
                    <button
                      onClick={() => setShowUploadBatchModal(true)}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5"
                    >
                      <FileUp className="w-3.5 h-3.5" />
                      <span>Upload Batch PDF</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-white/5 text-[10px] uppercase text-slate-400 border-b border-slate-800">
                        <tr>
                          <th className="px-5 py-3 font-bold">EOB ID / File</th>
                          <th className="px-5 py-3 font-bold">Payer / Trace #</th>
                          <th className="px-5 py-3 font-bold">Source Batch & Pages</th>
                          <th className="px-5 py-3 font-bold">Payment Amount</th>
                          <th className="px-5 py-3 font-bold">Attached Clients</th>
                          <th className="px-5 py-3 font-bold">Payment Header</th>
                          <th className="px-5 py-3 font-bold">Review Status</th>
                          <th className="px-5 py-3 font-bold">Adjudication Evidence</th>
                          <th className="px-5 py-3 font-bold text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/70">
                        {canonicalEobs.map((eob, eIdx) => (
                          <tr key={eob.id} className={`hover:bg-[#20314a] transition-colors ${eIdx % 2 === 0 ? 'ops-row-a' : 'ops-row-b'}`}>
                            <td className="px-5 py-3.5">
                              <div className="font-semibold text-slate-200 flex items-center space-x-1.5">
                                <FileText className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                                <span className="truncate max-w-[160px]" title={eob.filename}>{eob.filename}</span>
                              </div>
                              <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                                ID: {eob.id} • {Math.round(eob.file_size / 1024)} KB
                              </div>
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="text-slate-200 font-semibold">{eob.payer_name}</div>
                              <div className="text-[10px] font-mono text-slate-400">
                                {eob.check_or_eft_number ? `Ref: ${eob.check_or_eft_number}` : 'No Trace #'}
                              </div>
                              <div className="text-[10px] text-slate-500">
                                Recv: {eob.received_date} {eob.payment_date ? `• Pmt: ${eob.payment_date}` : ''}
                              </div>
                            </td>
                            <td className="px-5 py-3.5">
                              {eob.source_batch_filename ? (
                                <div>
                                  <div className="text-slate-300 font-medium truncate max-w-[140px]" title={eob.source_batch_filename}>
                                    {eob.source_batch_filename}
                                  </div>
                                  <div className="text-[10px] font-mono text-sky-400">
                                    Pages: {eob.source_page_start}–{eob.source_page_end}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-[10px] text-slate-500 font-mono">Direct / Unsegmented</span>
                              )}
                            </td>
                            <td className="px-5 py-3.5 font-mono text-emerald-400 font-bold text-sm">
                              ${Number(eob.total_payment_amount || 0).toFixed(2)}
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="space-y-1">
                                {eob.attached_clients && eob.attached_clients.length > 0 ? (
                                  <div className="flex flex-wrap gap-1 max-w-[200px]">
                                    {eob.attached_clients.map((c) => (
                                      <span
                                        key={c.clientId}
                                        className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-sky-950/80 text-sky-300 border border-sky-800/60"
                                        title={`AHCCCS: ${c.ahcccsId || 'N/A'}`}
                                      >
                                        {c.clientName}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-amber-400/80 font-mono">0 attached</span>
                                )}
                                <button
                                  onClick={() => {
                                    setActiveEobForClients(eob);
                                    setSelectedClientIdToAttach('');
                                    setClientSearchQuery('');
                                  }}
                                  className="text-[10px] text-sky-400 hover:text-sky-300 flex items-center gap-1 cursor-pointer"
                                >
                                  <Users className="w-3 h-3" />
                                  <span>Manage Clients</span>
                                </button>
                              </div>
                            </td>
                            <td className="px-5 py-3.5">
                              {eob.payment_header_id ? (
                                <div className="space-y-0.5">
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-950/80 text-emerald-300 border border-emerald-800/60 block w-fit">
                                    Linked: ${Number(eob.payment_header_amount || 0).toFixed(2)}
                                  </span>
                                  <div className="text-[9.5px] font-mono text-slate-400">
                                    {eob.payment_header_check_number || 'EFT'} ({eob.payment_header_status || 'POSTED'})
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={() => {
                                    setActiveEobForPaymentHeader(eob);
                                    setSelectedPaymentHeaderIdToLink('');
                                  }}
                                  className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20 cursor-pointer flex items-center gap-1"
                                >
                                  <Link2 className="w-3 h-3" />
                                  <span>Link Payment</span>
                                </button>
                              )}
                            </td>
                            <td className="px-5 py-3.5">
                              <button
                                onClick={() => handleToggleVerifyCanonical(eob.id)}
                                className={`px-2 py-0.5 rounded text-[10px] font-bold border transition-colors cursor-pointer flex items-center gap-1 ${
                                  eob.review_status === 'VERIFIED'
                                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25'
                                    : 'bg-amber-500/15 text-amber-300 border-amber-500/30 hover:bg-amber-500/25'
                                }`}
                                title="Click to toggle Verified / Needs Review status"
                              >
                                {eob.review_status === 'VERIFIED' ? (
                                  <>
                                    <Check className="w-3 h-3" />
                                    <span>VERIFIED</span>
                                  </>
                                ) : (
                                  <>
                                    <AlertTriangle className="w-3 h-3" />
                                    <span>NEEDS REVIEW</span>
                                  </>
                                )}
                              </button>
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="space-y-1.5">
                                {Number(eob.adjudications_count || 0) > 0 ? (
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      onClick={() => handleOpenEobAdjudications(eob)}
                                      className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-emerald-950/80 text-emerald-300 border border-emerald-800/60 hover:bg-emerald-900/80 cursor-pointer flex items-center gap-1"
                                      title="View claims adjudicated under this canonical EOB"
                                    >
                                      <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                                      <span>{eob.adjudications_count} Claim{Number(eob.adjudications_count) !== 1 ? 's' : ''} Linked</span>
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-slate-500 font-mono block">0 Claims Linked</span>
                                )}
                                <button
                                  onClick={() => handleOpenAdjudicateModal(eob)}
                                  className="px-2 py-0.5 rounded text-[10px] font-semibold bg-indigo-950/80 hover:bg-indigo-900/90 text-indigo-300 border border-indigo-700/60 cursor-pointer flex items-center gap-1"
                                  title="Record primary adjudication facts for a claim"
                                >
                                  <Plus className="w-3 h-3 text-indigo-400" />
                                  <span>+ Adjudicate Claim</span>
                                </button>
                              </div>
                            </td>
                            <td className="px-5 py-3.5 text-right space-x-1.5 whitespace-nowrap">
                              <button
                                onClick={() =>
                                  setInlineDocViewer({
                                    title: `Canonical EOB: ${eob.payer_name} (${eob.filename})`,
                                    url: `/api/billing/canonical-eobs/${eob.id}/content`,
                                    filename: eob.filename
                                  })
                                }
                                className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 text-[11px] font-semibold inline-flex items-center space-x-1 cursor-pointer"
                                title="View PDF segment"
                              >
                                <Eye className="w-3 h-3 text-sky-400" />
                                <span>View</span>
                              </button>
                              <a
                                href={`/api/billing/canonical-eobs/${eob.id}/download`}
                                download={eob.filename}
                                className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-sky-300 border border-slate-700 text-[11px] font-semibold inline-flex items-center space-x-1 cursor-pointer"
                                title="Download PDF segment"
                              >
                                <Download className="w-3 h-3" />
                                <span>Download</span>
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* MOBILE STACKED CARDS FOR CANONICAL EOBS */}
                  <div className="block md:hidden divide-y divide-slate-700/70">
                    {canonicalEobs.map((eob, idx) => (
                      <div key={`mb-can-${eob.id}`} className={`p-3.5 ${idx % 2 === 0 ? 'mobile-ops-row-a' : 'mobile-ops-row-b'} space-y-2.5`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-slate-100 text-xs flex items-center gap-1.5 truncate">
                              <FileText className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                              <span className="truncate">{eob.payer_name}</span>
                            </div>
                            <div className="text-[10px] text-slate-400 font-mono mt-0.5 truncate">
                              {eob.filename} • Recv: {eob.received_date}
                            </div>
                          </div>
                          <div className="text-right font-mono shrink-0">
                            <div className="text-xs text-emerald-400 font-bold">
                              ${Number(eob.total_payment_amount || 0).toFixed(2)}
                            </div>
                            <div className="text-[9.5px] text-slate-400">
                              {eob.check_or_eft_number || 'No Trace #'}
                            </div>
                          </div>
                        </div>

                        {eob.source_batch_filename && (
                          <div className="text-[10px] text-sky-400 font-mono bg-sky-950/40 p-1 rounded border border-sky-800/40 truncate">
                            Batch: {eob.source_batch_filename} (pp. {eob.source_page_start}–{eob.source_page_end})
                          </div>
                        )}

                        <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-slate-700/50">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggleVerifyCanonical(eob.id)}
                              className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                                eob.review_status === 'VERIFIED'
                                  ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                  : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                              }`}
                            >
                              {eob.review_status === 'VERIFIED' ? 'VERIFIED' : 'NEEDS REVIEW'}
                            </button>
                            <button
                              onClick={() => {
                                setActiveEobForClients(eob);
                                setSelectedClientIdToAttach('');
                                setClientSearchQuery('');
                              }}
                              className="text-[10px] text-sky-400 underline font-semibold cursor-pointer"
                            >
                              {eob.attached_clients?.length || 0} client(s)
                            </button>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {Number(eob.adjudications_count || 0) > 0 ? (
                              <button
                                onClick={() => handleOpenEobAdjudications(eob)}
                                className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold bg-emerald-950/80 text-emerald-300 border border-emerald-800/60 flex items-center gap-1"
                              >
                                <CheckCircle2 className="w-2.5 h-2.5 text-emerald-400" />
                                <span>{eob.adjudications_count} Adj</span>
                              </button>
                            ) : null}
                            <button
                              onClick={() => handleOpenAdjudicateModal(eob)}
                              className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-950/80 text-indigo-200 border border-indigo-700/60"
                            >
                              + Adj
                            </button>
                            <button
                              onClick={() =>
                                setInlineDocViewer({
                                  title: `Canonical EOB: ${eob.payer_name}`,
                                  url: `/api/billing/canonical-eobs/${eob.id}/content`,
                                  filename: eob.filename
                                })
                              }
                              className="px-2 py-1 rounded bg-slate-800 text-slate-300 border border-slate-700 text-[10px]"
                            >
                              <Eye className="w-3 h-3 inline mr-1 text-sky-400" />
                              <span>View</span>
                            </button>
                            <a
                              href={`/api/billing/canonical-eobs/${eob.id}/download`}
                              download={eob.filename}
                              className="px-2 py-1 rounded bg-slate-800 text-sky-300 border border-slate-700 text-[10px]"
                            >
                              <Download className="w-3 h-3 inline mr-1" />
                              <span>PDF</span>
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SUB-TAB 2: SCANNED SOURCE BATCHES */}
          {remittanceSubTab === 'BATCHES' && (
            <div className="rounded-xl overflow-hidden border border-slate-600/90 md:border-slate-800 mobile-ops-shell md:bg-transparent md:glass-panel shadow-[0_4px_20px_rgba(0,0,0,0.35)]">
              {eobBatches.length === 0 ? (
                <div className="p-10 text-center text-xs text-slate-400">
                  <FileUp className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                  <p className="font-semibold text-slate-300">No scanned EOB PDF batches uploaded yet.</p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Upload a multi-page PDF scan containing remittances from one or multiple payers. The source file remains immutable.
                  </p>
                  <button
                    onClick={() => setShowUploadBatchModal(true)}
                    className="mt-4 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer"
                  >
                    <FileUp className="w-3.5 h-3.5" />
                    <span>Upload First Scanned Batch</span>
                  </button>
                </div>
              ) : (
                <div>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-white/5 text-[10px] uppercase text-slate-400 border-b border-slate-800">
                        <tr>
                          <th className="px-5 py-3 font-bold">Batch ID / Filename</th>
                          <th className="px-5 py-3 font-bold">Received / Uploaded</th>
                          <th className="px-5 py-3 font-bold">Pages & Size</th>
                          <th className="px-5 py-3 font-bold">SHA-256 Hash</th>
                          <th className="px-5 py-3 font-bold">Derived EOBs</th>
                          <th className="px-5 py-3 font-bold">Processing Status</th>
                          <th className="px-5 py-3 font-bold text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/70">
                        {eobBatches.map((batch, bIdx) => (
                          <tr key={batch.id} className={`hover:bg-[#20314a] transition-colors ${bIdx % 2 === 0 ? 'ops-row-a' : 'ops-row-b'}`}>
                            <td className="px-5 py-3.5">
                              <div className="font-semibold text-slate-200 flex items-center space-x-1.5">
                                <FileUp className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                                <span className="truncate max-w-[200px]" title={batch.filename}>{batch.filename}</span>
                              </div>
                              <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                                ID: {batch.id}
                              </div>
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="text-slate-300 font-medium">Recv: {batch.received_date}</div>
                              <div className="text-[10px] text-slate-400 font-mono">
                                Up: {batch.uploaded_at?.split(' ')[0] || 'Today'} by {batch.uploaded_by}
                              </div>
                            </td>
                            <td className="px-5 py-3.5 font-mono text-slate-300">
                              <span className="px-1.5 py-0.5 rounded bg-sky-950 text-sky-300 border border-sky-800/60 font-semibold mr-1.5">
                                {batch.page_count || 1} {batch.page_count === 1 ? 'page' : 'pages'}
                              </span>
                              <span>{Math.round((batch.byte_size || 0) / 1024)} KB</span>
                            </td>
                            <td className="px-5 py-3.5">
                              <span className="text-[9.5px] font-mono text-slate-400 block truncate max-w-[120px]" title={batch.file_hash}>
                                {batch.file_hash?.substring(0, 14)}...
                              </span>
                            </td>
                            <td className="px-5 py-3.5">
                              <span className="px-2 py-0.5 rounded text-[10.5px] font-mono bg-sky-500/10 text-sky-300 border border-sky-500/20 font-semibold">
                                {batch.derived_eob_count || 0} segment(s)
                              </span>
                            </td>
                            <td className="px-5 py-3.5">
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${
                                  batch.processing_status === 'COMPLETE'
                                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                    : batch.processing_status === 'PARTIALLY_PROCESSED'
                                    ? 'bg-sky-500/15 text-sky-300 border-sky-500/30'
                                    : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                                }`}
                              >
                                {batch.processing_status}
                              </span>
                            </td>
                            <td className="px-5 py-3.5 text-right space-x-1.5 whitespace-nowrap">
                              <button
                                onClick={() => openSegmentModalForBatch(batch)}
                                className="px-2.5 py-1 rounded bg-sky-600 hover:bg-sky-500 text-white text-[11px] font-bold inline-flex items-center space-x-1 cursor-pointer shadow-sm"
                                title="Segment logical EOB from this batch"
                              >
                                <Scissors className="w-3 h-3" />
                                <span>Segment EOB</span>
                              </button>
                              <button
                                onClick={() =>
                                  setInlineDocViewer({
                                    title: `Source Batch: ${batch.filename} (${batch.page_count} pages)`,
                                    url: `/api/billing/eob-batches/${batch.id}/content`,
                                    filename: batch.filename
                                  })
                                }
                                className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 text-[11px] font-semibold inline-flex items-center space-x-1 cursor-pointer"
                                title="View original scanned PDF"
                              >
                                <Eye className="w-3 h-3 text-sky-400" />
                                <span>View</span>
                              </button>
                              <a
                                href={`/api/billing/eob-batches/${batch.id}/download`}
                                download={batch.filename}
                                className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-sky-300 border border-slate-700 text-[11px] font-semibold inline-flex items-center space-x-1 cursor-pointer"
                                title="Download original source PDF"
                              >
                                <Download className="w-3 h-3" />
                                <span>Download</span>
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* MOBILE STACKED CARDS FOR BATCHES */}
                  <div className="block md:hidden divide-y divide-slate-700/70">
                    {eobBatches.map((batch, idx) => (
                      <div key={`mb-batch-${batch.id}`} className={`p-3.5 ${idx % 2 === 0 ? 'mobile-ops-row-a' : 'mobile-ops-row-b'} space-y-2.5`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-slate-100 text-xs flex items-center gap-1.5 truncate">
                              <FileUp className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                              <span className="truncate">{batch.filename}</span>
                            </div>
                            <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                              ID: {batch.id} • Recv: {batch.received_date}
                            </div>
                          </div>
                          <div className="text-right font-mono shrink-0">
                            <span className="px-1.5 py-0.5 rounded bg-sky-950 text-sky-300 border border-sky-800/60 text-xs font-semibold">
                              {batch.page_count} pp
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-700/50">
                          <span className="text-xs text-sky-300 font-mono">
                            {batch.derived_eob_count || 0} derived EOB(s)
                          </span>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => openSegmentModalForBatch(batch)}
                              className="px-2.5 py-1 rounded bg-sky-600 text-white text-[10.5px] font-bold inline-flex items-center gap-1 cursor-pointer"
                            >
                              <Scissors className="w-3 h-3" />
                              <span>Segment</span>
                            </button>
                            <a
                              href={`/api/billing/eob-batches/${batch.id}/download`}
                              download={batch.filename}
                              className="px-2 py-1 rounded bg-slate-800 text-sky-300 border border-slate-700 text-[10.5px]"
                            >
                              <Download className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* VIEW 5: ISOLATED HISTORICAL EVIDENCE VAULT (PHASE 2C.4)                   */}
      {/* ========================================================================= */}
      {activeTab === 'HistoricalVault' && (
        <div className="space-y-4 animate-in fade-in-50 duration-200">
          {/* Header Card */}
          <div className="p-4 rounded-xl bg-gradient-to-r from-slate-900 via-[#0d1626] to-slate-900 border border-slate-700/80 shadow-lg">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <Archive className="w-5 h-5 text-cyan-400" />
                  <h2 className="text-base font-bold text-white tracking-tight">
                    Isolated Historical Evidence Vault
                  </h2>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/25">
                    Phase 2C.4
                  </span>
                </div>
                <p className="text-xs text-slate-400 max-w-2xl">
                  Archival, diagnostic, and human-review support module for historical TPL / COB lineage research.
                  Completely air-gapped from live billing, active claims, and the billing engine.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-mono px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center space-x-1.5">
                  <Lock className="w-3 h-3" />
                  <span>DB Immutability Triggers Active</span>
                </span>
                <span className="text-[10px] font-mono px-2.5 py-1 rounded-lg bg-sky-500/10 text-sky-400 border border-sky-500/20 flex items-center space-x-1.5">
                  <ShieldCheck className="w-3 h-3" />
                  <span>Zero Live Billing Coupling</span>
                </span>
              </div>
            </div>
          </div>

          {/* Hard Scope & Immutability Governance Banner */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-white border border-slate-200 text-xs shadow-xs">
              <div className="flex items-center space-x-2 text-sky-800 font-semibold mb-1">
                <Database className="w-3.5 h-3.5 shrink-0" />
                <span>Append-Only Immutability</span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                PostgreSQL triggers block UPDATE, DELETE, and TRUNCATE on all 5 vault tables. Corrections occur only through new superseding assertions.
              </p>
            </div>

            <div className="p-3 rounded-lg bg-white border border-slate-200 text-xs shadow-xs">
              <div className="flex items-center space-x-2 text-amber-800 font-semibold mb-1">
                <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
                <span>Transport Evidence Barrier</span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                TA1 and 999 acknowledgements record transport-layer proof only. They never establish claim acceptance, payment, or adjudication.
              </p>
            </div>

            <div className="p-3 rounded-lg bg-white border border-slate-200 text-xs shadow-xs">
              <div className="flex items-center space-x-2 text-emerald-800 font-semibold mb-1">
                <FileCheck className="w-3.5 h-3.5 shrink-0" />
                <span>Rigorous COB Verification</span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                A verified secondary COB conclusion strictly requires primary EOB + page, WellSky claim ID, secondary 837, and downstream remittance.
              </p>
            </div>
          </div>

          {/* KPI Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            <div className="p-3 rounded-xl bg-white border border-slate-200 shadow-xs">
              <span className="text-[10px] uppercase font-bold text-slate-700 block">Canonical Artifacts</span>
              <span className="text-xl font-bold font-mono text-slate-900 mt-1 block">
                {historicalVaultSummary.artifactsCount}
              </span>
              <span className="text-[10px] text-slate-600">Immutable files</span>
            </div>

            <div className="p-3 rounded-xl bg-white border border-slate-200 shadow-xs">
              <span className="text-[10px] uppercase font-bold text-slate-700 block">Retrievals</span>
              <span className="text-xl font-bold font-mono text-slate-900 mt-1 block">
                {historicalVaultSummary.retrievalsCount}
              </span>
              <span className="text-[10px] text-slate-600">Provenance entries</span>
            </div>

            <div className="p-3 rounded-xl bg-white border border-slate-200 shadow-xs">
              <span className="text-[10px] uppercase font-bold text-slate-700 block">Candidate Anchors</span>
              <span className="text-xl font-bold font-mono text-slate-900 mt-1 block">
                {historicalVaultSummary.candidateAnchorsCount}
              </span>
              <span className="text-[10px] text-slate-600">Unseeded / Pristine</span>
            </div>

            <div className="p-3 rounded-xl bg-white border border-slate-200 shadow-xs">
              <span className="text-[10px] uppercase font-bold text-slate-700 block">Transport Acks</span>
              <span className="text-xl font-bold font-mono text-slate-900 mt-1 block">
                {historicalVaultSummary.transportAcksCount}
              </span>
              <span className="text-[10px] text-slate-600">TA1 / 999 proof</span>
            </div>

            <div className="p-3 rounded-xl bg-white border border-slate-200 shadow-xs">
              <span className="text-[10px] uppercase font-bold text-slate-700 block">Lineage Assertions</span>
              <span className="text-xl font-bold font-mono text-slate-900 mt-1 block">
                {historicalVaultSummary.lineageAssertionsCount}
              </span>
              <span className="text-[10px] text-slate-600">Human review ledger</span>
            </div>
          </div>

          {/* Sub-Tabs for Read-Only Inspection */}
          <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 pb-2">
            {[
              { id: 'SUMMARY', label: 'Vault Governance' },
              { id: 'EDI_PREVIEW', label: 'EDI Crosswalk & Ack Preview' },
              { id: 'ARTIFACTS', label: `Canonical Artifacts (${historicalVaultSummary.artifactsCount})` },
              { id: 'ANCHORS', label: `Candidate Anchors (${historicalVaultSummary.candidateAnchorsCount})` },
              { id: 'TRANSPORT', label: `Transport Acks (${historicalVaultSummary.transportAcksCount})` },
              { id: 'ASSERTIONS', label: `Lineage Assertions (${historicalVaultSummary.lineageAssertionsCount})` }
            ].map((st) => (
              <button
                key={st.id}
                onClick={() => setHistoricalSubTab(st.id as any)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  historicalSubTab === st.id
                    ? 'bg-slate-900 text-white border border-slate-800'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-transparent'
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>

          {/* Sub-Tab 1: Governance & Scope Verification */}
          {historicalSubTab === 'SUMMARY' && (
            <div className="p-6 rounded-xl bg-white border border-slate-200 space-y-4 text-center py-10 shadow-xs">
              <div className="w-12 h-12 rounded-2xl bg-sky-50 border border-sky-200 text-sky-700 flex items-center justify-center mx-auto">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div className="max-w-md mx-auto space-y-1">
                <h3 className="text-sm font-bold text-slate-900">Historical Vault Initialized & Clean</h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  The isolated historical evidence tables, triggers, and write-once intake endpoints are active.
                  No historical evidence or candidate discovery records have been seeded or imported yet.
                </p>
              </div>

              <div className="max-w-lg mx-auto p-3.5 rounded-lg bg-slate-50 border border-slate-200 text-left text-xs font-mono space-y-2 text-slate-700">
                <div className="flex justify-between items-center text-[11px] text-slate-600 border-b border-slate-200 pb-1">
                  <span>Module Security Audit:</span>
                  <span className="text-emerald-700 font-bold">PASS (100% Isolated)</span>
                </div>
                <div>• References to live claims table: <span className="text-emerald-700 font-bold">0</span></div>
                <div>• References to billing batches: <span className="text-emerald-700 font-bold">0</span></div>
                <div>• References to canonical EOBs: <span className="text-emerald-700 font-bold">0</span></div>
                <div>• References to claim adjudications: <span className="text-emerald-700 font-bold">0</span></div>
                <div>• References to billing engine: <span className="text-emerald-700 font-bold">0</span></div>
                <div>• Mutation API endpoints (PUT/PATCH/DELETE): <span className="text-emerald-700 font-bold">0 (Blocked)</span></div>
              </div>
            </div>
          )}

          {/* Sub-Tab: EDI Crosswalk & Ack Preview (Phase 2C.5A) */}
          {historicalSubTab === 'EDI_PREVIEW' && (
            <div className="space-y-5">
              {/* Mandatory Governance Banner */}
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start space-x-3.5">
                <Lock className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-amber-900">
                      Historical Diagnostic Preview Only
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-amber-100 text-amber-900 border border-amber-300">
                      Zero Operational Mutation
                    </span>
                  </div>
                  <p className="text-xs text-amber-950 leading-relaxed">
                    Historical diagnostic preview only. No claim transmission or operational state changes are enabled.
                    Evaluates observed historical WellSky 837/TA1/999 identifier behaviors in pure memory without modifying operational claims, billing readiness queues, authorizations, or payment ledgers.
                  </p>
                </div>
              </div>

              {/* Action Toolbar & Test Controls */}
              <div className="flex flex-wrap items-center justify-between gap-3 p-3.5 rounded-xl bg-white border border-slate-200 shadow-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={handleLoadSampleTestCase}
                    disabled={ediPreviewLoading}
                    className="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-slate-900 hover:bg-slate-800 text-white flex items-center space-x-1.5 shadow-sm transition-all disabled:opacity-50"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-slate-200" />
                    <span>Load Authorized WellSky Test Set</span>
                  </button>
                  <button
                    onClick={() => setShowRawInputs(!showRawInputs)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 transition-colors"
                  >
                    {showRawInputs ? 'Hide Raw EDI Editor' : 'View / Edit Raw EDI Snippets'}
                  </button>
                  {ediPreviewResult && (
                    <button
                      onClick={() => runEdiPreview()}
                      disabled={ediPreviewLoading}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-700 hover:bg-emerald-600 text-white flex items-center space-x-1 transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${ediPreviewLoading ? 'animate-spin' : ''}`} />
                      <span>Re-evaluate Preview</span>
                    </button>
                  )}
                </div>
                <div className="text-[11px] text-slate-600 font-mono flex items-center space-x-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                  <span>Pure In-Memory Evaluator Ready (Zero Persistence)</span>
                </div>
              </div>

              {/* Collapsible Raw EDI Input Tester */}
              {showRawInputs && (
                <div className="p-4 rounded-xl bg-white border border-slate-200 space-y-3 shadow-xs">
                  <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                    <div className="flex items-center space-x-2">
                      {(['837', 'TA1', '999'] as const).map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveEdiTab(tab)}
                          className={`px-3 py-1 rounded text-xs font-mono font-medium transition-colors ${
                            activeEdiTab === tab
                              ? 'bg-slate-900 text-white border border-slate-800'
                              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-transparent'
                          }`}
                        >
                          Raw {tab} Snippet
                        </button>
                      ))}
                    </div>
                    <span className="text-[11px] text-amber-800 font-mono font-medium">Manual preview — no canonical artifact provenance</span>
                  </div>

                  {activeEdiTab === '837' && (
                    <div>
                      <label className="text-[10px] uppercase font-bold text-slate-700 block mb-1">
                        Outbound WellSky Secondary 837 Professional EDI:
                      </label>
                      <textarea
                        value={ediInput837}
                        onChange={(e) => setEdiInput837(e.target.value)}
                        placeholder="ISA*00*...~&#10;GS*HC*...~&#10;ST*837*0001*...~&#10;CLM*TC-CLAIM-101*150.00...~"
                        rows={6}
                        className="w-full font-mono text-xs bg-white border border-slate-300 rounded-lg p-3 text-slate-800 placeholder:text-slate-400 focus:border-slate-500 focus:outline-none"
                      />
                    </div>
                  )}

                  {activeEdiTab === 'TA1' && (
                    <div>
                      <label className="text-[10px] uppercase font-bold text-slate-700 block mb-1">
                        Clearinghouse TA1 Interchange Acknowledgment EDI:
                      </label>
                      <textarea
                        value={ediInputTa1}
                        onChange={(e) => setEdiInputTa1(e.target.value)}
                        placeholder="ISA*00*...~&#10;TA1*000000001*230901*1015*A*000~&#10;IEA*0*...~"
                        rows={5}
                        className="w-full font-mono text-xs bg-white border border-slate-300 rounded-lg p-3 text-slate-800 placeholder:text-slate-400 focus:border-slate-500 focus:outline-none"
                      />
                    </div>
                  )}

                  {activeEdiTab === '999' && (
                    <div>
                      <label className="text-[10px] uppercase font-bold text-slate-700 block mb-1">
                        Clearinghouse 999 Implementation Acknowledgment EDI:
                      </label>
                      <textarea
                        value={ediInput999}
                        onChange={(e) => setEdiInput999(e.target.value)}
                        placeholder="ISA*00*...~&#10;GS*FA*...~&#10;ST*999*0001*...~&#10;AK1*HC*000000001*...~&#10;AK2*837*TC-CLAIM-101*...~&#10;IK5*A~&#10;AK9*A*1*1*1~"
                        rows={6}
                        className="w-full font-mono text-xs bg-white border border-slate-300 rounded-lg p-3 text-slate-800 placeholder:text-slate-400 focus:border-slate-500 focus:outline-none"
                      />
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      onClick={() => runEdiPreview()}
                      disabled={ediPreviewLoading}
                      className="px-4 py-2 rounded-lg text-xs font-semibold bg-slate-900 hover:bg-slate-800 text-white transition-colors"
                    >
                      {ediPreviewLoading ? 'Evaluating Preview...' : 'Run In-Memory Evaluation'}
                    </button>
                  </div>
                </div>
              )}

              {/* Sample Notes Callout if loaded */}
              {sampleNotes && (
                <div className="p-3 rounded-lg bg-sky-50 border border-sky-200 text-xs text-slate-800 flex items-start space-x-2">
                  <Info className="w-4 h-4 text-sky-700 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-sky-900">Authorized Historical Test Case Loaded: </span>
                    <span className="text-slate-700">{sampleNotes}</span>
                  </div>
                </div>
              )}

              {/* Empty state if preview hasn't run yet */}
              {!ediPreviewResult && !ediPreviewLoading && (
                <div className="p-10 rounded-xl bg-white border border-slate-200 text-center space-y-3 shadow-xs">
                  <div className="w-12 h-12 rounded-2xl bg-sky-50 border border-sky-200 text-sky-700 flex items-center justify-center mx-auto">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div className="max-w-md mx-auto space-y-1">
                    <h3 className="text-sm font-bold text-slate-900">EDI Crosswalk & Acknowledgement Tester</h3>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Click <strong className="text-slate-900">"Load Authorized WellSky Test Set"</strong> above to inspect the observed historical crosswalk behavior, or input custom 837/TA1/999 snippets.
                    </p>
                  </div>
                </div>
              )}

              {/* Active Preview Results */}
              {ediPreviewResult && (
                <div className="space-y-5 animate-in fade-in duration-300">
                  {/* Top Status & Scope Card */}
                  <div className="p-4 rounded-xl bg-white border border-slate-200 flex flex-wrap items-center justify-between gap-4 shadow-xs">
                    <div className="flex items-center space-x-3">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-[10px] uppercase font-bold text-slate-600">Overall Acknowledgment Status:</span>
                          <span
                            className={`px-2.5 py-0.5 rounded text-xs font-bold font-mono tracking-wide border ${
                              ediPreviewResult.status === 'TRANSACTION_VERIFIED'
                                ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                : ediPreviewResult.status === 'BATCH_ACCEPTED'
                                ? 'bg-sky-50 text-sky-800 border-sky-200'
                                : ediPreviewResult.status === 'REJECTED'
                                ? 'bg-rose-50 text-rose-800 border-rose-200'
                                : ediPreviewResult.status === 'AMBIGUOUS'
                                ? 'bg-amber-50 text-amber-900 border-amber-200'
                                : 'bg-slate-100 text-slate-800 border-slate-300'
                            }`}
                          >
                            {ediPreviewResult.status}
                          </span>
                        </div>
                        <p className="text-xs text-slate-700 font-medium">
                          {ediPreviewResult.statusReason}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-xs font-mono">
                      <span className="px-2 py-1 rounded bg-slate-50 border border-slate-200 text-slate-700">
                        Scope: <strong className="text-slate-900">{ediPreviewResult.evidenceScope}</strong>
                      </span>
                      <span className="px-2 py-1 rounded bg-slate-50 border border-slate-200 text-slate-700">
                        Barrier: <strong className="text-emerald-800">Transport-Only (is_transport_only=true)</strong>
                      </span>
                      <span className="px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-900 font-medium">
                        Preview is nonpersistent; no write operation is available from this screen.
                      </span>
                    </div>
                  </div>

                  {/* Provenance Notice Badge */}
                  <div className="px-3.5 py-2 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-between text-xs font-mono text-slate-600">
                    <div className="flex items-center space-x-2">
                      <span className="text-[10px] uppercase font-bold text-slate-600">Provenance Status:</span>
                      <span className="text-amber-900 font-semibold">{ediPreviewResult.provenanceNotice}</span>
                    </div>
                    <span className="text-[11px] text-slate-600">Pure In-Memory Evaluation</span>
                  </div>

                  {/* Structural Errors / Warnings Callout */}
                  {(ediPreviewResult.structuralErrors?.length > 0 || ediPreviewResult.structuralWarnings?.length > 0) && (
                    <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 space-y-1.5 text-xs">
                      <div className="flex items-center space-x-2 text-amber-900 font-bold uppercase tracking-wider text-[11px]">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />
                        <span>Parser Structural Diagnostic Notices</span>
                      </div>
                      {ediPreviewResult.structuralErrors.map((err: string, idx: number) => (
                        <div key={`err-${idx}`} className="text-rose-800 font-mono text-[11px]">• ERROR: {err}</div>
                      ))}
                      {ediPreviewResult.structuralWarnings.map((warn: string, idx: number) => (
                        <div key={`warn-${idx}`} className="text-amber-900 font-mono text-[11px]">• WARNING: {warn}</div>
                      ))}
                    </div>
                  )}

                  {/* WellSky Discrepancy & Alias Matching Card */}
                  {ediPreviewResult.wellskyDiscrepancyDetected && (
                    <div className="p-4 rounded-xl bg-purple-50 border border-purple-200 space-y-2">
                      <div className="flex items-center space-x-2 text-purple-900 text-xs font-bold uppercase tracking-wider">
                        <AlertCircle className="w-4 h-4 text-purple-700" />
                        <span>Observed WellSky Acknowledgement Discrepancy Detected</span>
                      </div>
                      <p className="text-xs text-slate-700 leading-relaxed">
                        WellSky historical acknowledgements exhibit non-standard X12 identifier aliases. Pulse models these explicitly with verifiable rule provenance rather than silently rewriting control numbers:
                      </p>
                      <ul className="space-y-1 text-xs text-slate-700 font-mono bg-white p-2.5 rounded-lg border border-purple-200">
                        {ediPreviewResult.wellskyDiscrepancyNotes.map((note: string, idx: number) => (
                          <li key={idx} className="flex items-start space-x-1.5">
                            <span className="text-purple-700">•</span>
                            <span>{note}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Evidence-Chain Timeline */}
                  <div className="p-4 rounded-xl bg-white border border-slate-200 space-y-3 shadow-xs">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center space-x-2">
                        <Layers className="w-4 h-4 text-sky-700" />
                        <span>Evidence-Chain Timeline</span>
                      </h4>
                      <span className="text-[11px] text-slate-600 font-mono">837 → TA1 → 999 → Adjudication</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      {ediPreviewResult.timeline.map((step: any, idx: number) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border flex flex-col justify-between space-y-2 ${
                            step.isMissingNotice
                              ? 'bg-rose-50 border-rose-200'
                              : step.status === 'VERIFIED' || step.status === 'ACCEPTED'
                              ? 'bg-slate-50 border-slate-200'
                              : 'bg-white border-slate-200'
                          }`}
                        >
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-bold font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                                Step {step.step}
                              </span>
                              <span
                                className={`text-[10px] font-bold font-mono px-1.5 py-0.5 rounded ${
                                  step.status === 'VERIFIED'
                                    ? 'bg-emerald-50 text-emerald-800'
                                    : step.status === 'ACCEPTED'
                                    ? 'bg-sky-50 text-sky-800'
                                    : 'bg-rose-50 text-rose-800'
                                }`}
                              >
                                {step.status}
                              </span>
                            </div>
                            <h5 className="text-xs font-semibold text-slate-900">{step.title}</h5>
                            <p className="text-[11px] text-slate-600 leading-snug">{step.artifactDescription}</p>
                          </div>

                          <div className="pt-2 border-t border-slate-200 text-[10px] font-mono text-slate-600 space-y-0.5">
                            <div className="text-slate-800 font-medium">{step.timestampOrRef}</div>
                            <div className="text-[10px] text-slate-600 line-clamp-2">{step.details}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Explicit Adjudication Missing Warning */}
                    <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 flex items-start space-x-2.5">
                      <AlertTriangle className="w-4 h-4 text-rose-700 shrink-0 mt-0.5" />
                      <div className="text-xs text-rose-950 leading-relaxed">
                        <strong className="text-rose-900">Financial Evidence Boundary Notice: </strong>
                        An 837’s SVD, CAS, DTP*573, and REF*F8 fields are prior-payer facts asserted in the submitted COB packet.
                        They are not independent proof that a payment settled. Only a verified primary EOB/ERA may establish a verified primary payment.
                        Never mark a claim paid, resolved, or ready for secondary submission from the 837 or transport acknowledgements alone.
                      </div>
                    </div>
                  </div>

                  {/* Financial Evidence Boundary & Arithmetic Sanity Check Cards */}
                  {ediPreviewResult.transactionMatches && ediPreviewResult.transactionMatches.length > 0 && (
                    <div className="p-4 rounded-xl bg-white border border-slate-200 space-y-3 shadow-xs">
                      <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center space-x-2">
                          <CircleDollarSign className="w-4 h-4 text-emerald-700" />
                          <span>Arithmetic Sanity Check (COB Assertions)</span>
                        </h4>
                        <span className="text-[11px] text-slate-600 font-mono">Mathematical Balance Check Only</span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {ediPreviewResult.transactionMatches.map((tx: any, idx: number) => {
                          const check = tx.arithmeticCheck;
                          const isBalanced = check.status === 'MATHEMATICALLY_BALANCED_ASSERTION_ONLY';
                          return (
                            <div key={idx} className="p-3.5 rounded-lg bg-slate-50 border border-slate-200 space-y-2">
                              <div className="flex items-center justify-between text-xs">
                                <span className="font-mono font-bold text-slate-900">
                                  Claim #{tx.claimIndex} ({tx.maskedSubmitterClaimId || tx.submitterClaimId})
                                </span>
                                <span
                                  className={`px-2 py-0.5 rounded text-[10px] font-bold font-mono border ${
                                    isBalanced
                                      ? 'bg-sky-50 text-sky-800 border-sky-200'
                                      : check.status === 'DISCREPANCY'
                                      ? 'bg-rose-50 text-rose-800 border-rose-200'
                                      : 'bg-amber-50 text-amber-900 border-amber-200'
                                  }`}
                                >
                                  {check.status}
                                </span>
                              </div>

                              <div className="grid grid-cols-3 gap-2 text-xs font-mono bg-white p-2 rounded border border-slate-200">
                                <div>
                                  <span className="text-[10px] text-slate-600 block">Submitted (CLM02)</span>
                                  <span className="text-slate-900 font-bold">${check.submittedCharge.toFixed(2)}</span>
                                </div>
                                <div>
                                  <span className="text-[10px] text-slate-600 block">Asserted Paid (SVD02)</span>
                                  <span className="text-emerald-800 font-bold">${check.assertedPriorPaidAmount.toFixed(2)}</span>
                                </div>
                                <div>
                                  <span className="text-[10px] text-slate-600 block">Asserted Adj (CAS)</span>
                                  <span className="text-amber-800 font-bold">${check.assertedPriorAdjustments.toFixed(2)}</span>
                                </div>
                              </div>

                              {check.discrepancyMessage && (
                                <p className="text-xs text-rose-800 font-mono bg-rose-50 p-1.5 rounded">
                                  {check.discrepancyMessage}
                                </p>
                              )}

                              <div className="text-[10px] text-slate-600 leading-tight">
                                {check.boundaryNotice}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Transaction Sets Breakdown */}
                  {ediPreviewResult.transactionMatches && ediPreviewResult.transactionMatches.length > 0 && (
                    <div className="p-4 rounded-xl bg-white border border-slate-200 space-y-3 shadow-xs">
                      <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-sky-700" />
                          <span>Transaction Sets Status (AK2 / IK5 Linkage)</span>
                        </h4>
                        <span className="text-[11px] text-slate-600 font-mono">
                          {ediPreviewResult.transactionMatches.length} Transaction(s)
                        </span>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="border-b border-slate-200 text-[10px] uppercase font-bold text-slate-600">
                              <th className="pb-2">Claim ID (Masked)</th>
                              <th className="pb-2">ST02 Control (Masked)</th>
                              <th className="pb-2">Matched AK2-02 (Masked)</th>
                              <th className="pb-2">Match Rule</th>
                              <th className="pb-2">IK5 Status</th>
                              <th className="pb-2">Result</th>
                              <th className="pb-2">Explanation</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200 font-mono">
                            {ediPreviewResult.transactionMatches.map((tx: any, idx: number) => {
                              const maskedSt = tx.maskedSt02Control || (tx.st02Control ? (tx.st02Control.length <= 4 ? `***${tx.st02Control.slice(-1)}` : `${tx.st02Control.slice(0, 2)}***${tx.st02Control.slice(-2)}`) : '—');
                              const rawAk = tx.matchedAk202;
                              const maskedAk = tx.maskedAk202 || (rawAk ? (rawAk.length <= 4 ? `***${rawAk.slice(-1)}` : `${rawAk.slice(0, 2)}***${rawAk.slice(-2)}`) : '—');
                              return (
                              <tr key={idx} className="hover:bg-slate-50">
                                <td className="py-2 text-slate-900 font-bold">{tx.maskedSubmitterClaimId || tx.submitterClaimId}</td>
                                <td className="py-2 text-slate-600">{maskedSt}</td>
                                <td className="py-2 text-sky-800">{maskedAk}</td>
                                <td className="py-2 text-purple-800 text-[11px]">{tx.matchMethod || '—'}</td>
                                <td className="py-2">
                                  {tx.ik5Status ? (
                                    <span
                                      className={`px-1.5 py-0.5 rounded text-[10px] ${
                                        tx.ik5Status === 'A'
                                          ? 'bg-emerald-50 text-emerald-800 font-bold'
                                          : 'bg-rose-50 text-rose-800 font-bold'
                                      }`}
                                    >
                                      IK5*{tx.ik5Status}
                                    </span>
                                  ) : (
                                    <span className="text-slate-500">—</span>
                                  )}
                                </td>
                                <td className="py-2">
                                  <span
                                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                      tx.transactionStatus === 'TRANSACTION_VERIFIED'
                                        ? 'bg-emerald-50 text-emerald-800'
                                        : tx.transactionStatus === 'BATCH_ACCEPTED'
                                        ? 'bg-sky-50 text-sky-800'
                                        : tx.transactionStatus === 'REJECTED'
                                        ? 'bg-rose-50 text-rose-800'
                                        : 'bg-amber-50 text-amber-900'
                                    }`}
                                  >
                                    {tx.transactionStatus}
                                  </span>
                                </td>
                                <td className="py-2 text-[11px] font-sans text-slate-700">{tx.explanation}</td>
                              </tr>
                            );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Identifier Crosswalk Table (De-identified & Masked) */}
                  {ediPreviewResult.extractedCrosswalks && ediPreviewResult.extractedCrosswalks.length > 0 && (
                    <div className="p-4 rounded-xl bg-white border border-slate-200 space-y-3 shadow-xs">
                      <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center space-x-2">
                          <Hash className="w-4 h-4 text-sky-700" />
                          <span>Identifier Crosswalk Model (Masked)</span>
                        </h4>
                        <span className="text-[11px] text-slate-600 font-mono">
                          {ediPreviewResult.extractedCrosswalks.length} Crosswalk Node(s)
                        </span>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="border-b border-slate-200 text-[10px] uppercase font-bold text-slate-600">
                              <th className="pb-2">Path / Segment</th>
                              <th className="pb-2">Identifier Type</th>
                              <th className="pb-2">Value (Masked)</th>
                              <th className="pb-2">Semantic Role</th>
                              <th className="pb-2">Match Method & Rule</th>
                              <th className="pb-2">Confidence</th>
                              <th className="pb-2">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200 font-mono">
                            {ediPreviewResult.extractedCrosswalks.map((node: any, idx: number) => (
                              <tr key={idx} className="hover:bg-slate-50">
                                <td className="py-2 text-sky-800 font-bold">{node.segmentElementPath}</td>
                                <td className="py-2 text-slate-600">{node.identifierType}</td>
                                <td className="py-2 text-slate-900 font-semibold">
                                  {node.maskedIdentifierValue || node.identifierValue}
                                </td>
                                <td className="py-2 text-slate-700 text-[11px]">{node.semanticRole}</td>
                                <td className="py-2 text-purple-800 text-[11px] font-semibold">{node.matchMethod}</td>
                                <td className="py-2 text-slate-600">{Math.round(node.confidence * 100)}%</td>
                                <td className="py-2">
                                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-800 border border-slate-200">
                                    {node.reviewerStatus}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Sub-Tab 2: Canonical Artifacts */}
          {historicalSubTab === 'ARTIFACTS' && (
            <div className="p-6 rounded-xl bg-white border border-slate-200 text-center py-10 space-y-2 shadow-xs">
              <FileText className="w-8 h-8 text-slate-500 mx-auto" />
              <h3 className="text-sm font-semibold text-slate-800">No Canonical Artifacts Registered</h3>
              <p className="text-xs text-slate-600 max-w-sm mx-auto">
                Immutable artifacts (WellSky 837s, TA1s, 999s, EOP PDFs, 835 ERAs) can be registered via the write-once intake service.
              </p>
            </div>
          )}

          {/* Sub-Tab 3: Candidate Anchors */}
          {historicalSubTab === 'ANCHORS' && (
            <div className="p-6 rounded-xl bg-white border border-slate-200 text-center py-10 space-y-2 shadow-xs">
              <Hash className="w-8 h-8 text-slate-500 mx-auto" />
              <h3 className="text-sm font-semibold text-slate-800">No Candidate Anchors Created</h3>
              <p className="text-xs text-slate-600 max-w-sm mx-auto">
                Per Phase 2C.4 directives, the three known candidate discoveries have not been seeded. They await human-authorized archival intake.
              </p>
            </div>
          )}

          {/* Sub-Tab 4: Transport Acknowledgements */}
          {historicalSubTab === 'TRANSPORT' && (
            <div className="p-6 rounded-xl bg-white border border-slate-200 text-center py-10 space-y-2 shadow-xs">
              <Layers className="w-8 h-8 text-slate-500 mx-auto" />
              <h3 className="text-sm font-semibold text-slate-800">No Transport Acknowledgements Recorded</h3>
              <p className="text-xs text-slate-600 max-w-sm mx-auto">
                TA1 / 999 transport evidence is stored with explicit scope (UNPAIRED, BATCH_LEVEL, or TRANSACTION_VERIFIED) and gated as transport-only.
              </p>
            </div>
          )}

          {/* Sub-Tab 5: Lineage Assertion Ledger */}
          {historicalSubTab === 'ASSERTIONS' && (
            <div className="p-6 rounded-xl bg-white border border-slate-200 text-center py-10 space-y-2 shadow-xs">
              <CheckCircle2 className="w-8 h-8 text-slate-500 mx-auto" />
              <h3 className="text-sm font-semibold text-slate-800">No Review Assertions Logged</h3>
              <p className="text-xs text-slate-600 max-w-sm mx-auto">
                Every human review conclusion is recorded as an append-only assertion in this ledger. Superseded assertions are never updated or deleted.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 1: BATCH GENERATION CONFIRMATION / PREVIEW                          */}
      {/* ========================================================================= */}
      {batchPreview && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200/90 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5 space-y-4 shadow-xl text-slate-800 animate-in fade-in zoom-in-95">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 flex items-center space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>
                    {batchPreview.billingRoute === 'WELLSKY_DDD' ? 'WellSky 837 Batch Created' : 'Claim.MD 837 Batch Created'}
                  </span>
                  <span className="text-xs font-mono px-2 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200">
                    {batchPreview.batchNumber}
                  </span>
                </h3>
                <div className="text-xs text-slate-600 mt-0.5">
                  ANSI ASC X12 837P file constructed and stored. Live external transmission is gated OFF.
                </div>
              </div>
              <button onClick={() => setBatchPreview(null)} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 flex items-start space-x-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5 text-slate-500" />
              <div>
                <strong>Operator Manual Upload Step:</strong> The operator manually downloads this file and uploads it directly to {batchPreview.billingRoute === 'WELLSKY_DDD' ? 'WellSky' : 'Claim.MD'}. No automated external transmission occurs.
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3.5 rounded-lg bg-slate-50 border border-slate-200 text-xs font-mono">
              <div>
                <span className="text-slate-500 block text-[10px] uppercase">Number of Claims</span>
                <span className="text-slate-900 font-bold text-sm">{batchPreview.claimCount}</span>
              </div>
              <div>
                <span className="text-slate-500 block text-[10px] uppercase">Total Charges</span>
                <span className="text-emerald-700 font-bold text-sm">${batchPreview.totalCharges.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-slate-500 block text-[10px] uppercase">DOS Range</span>
                <span className="text-slate-800">{batchPreview.dosRange}</span>
              </div>
              <div>
                <span className="text-slate-500 block text-[10px] uppercase">Sender Account</span>
                <span className="text-sky-800">{batchPreview.sender}</span>
              </div>
              <div>
                <span className="text-slate-500 block text-[10px] uppercase">Payer / Route</span>
                <span className="text-slate-800 truncate block" title={batchPreview.payer}>{batchPreview.payer}</span>
              </div>
              <div>
                <span className="text-slate-500 block text-[10px] uppercase">Timestamp</span>
                <span className="text-slate-700">{batchPreview.generatedAt}</span>
              </div>
            </div>

            <div className="space-y-1.5 text-xs">
              <div className="text-slate-800">
                <span className="text-slate-500">Clients:</span> {batchPreview.clients.join(', ')}
              </div>
              <div className="flex items-start justify-between gap-2 p-2 rounded bg-white border border-slate-200 text-[11px] font-mono">
                <div className="min-w-0 break-all text-slate-900">
                  <span className="text-slate-500">Filename:</span> {batchPreview.filename}
                  <span className="mx-2 text-slate-400">|</span>
                  <span className="text-slate-500">SHA-256:</span> {batchPreview.fileHash}
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(batchPreview.fileHash);
                    setCopiedHash(true);
                    setTimeout(() => setCopiedHash(false), 2000);
                  }}
                  className="shrink-0 text-[10px] px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200 inline-flex items-center space-x-1"
                >
                  {copiedHash ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                  <span>{copiedHash ? 'Copied' : 'Copy Hash'}</span>
                </button>
              </div>
            </div>

            <div>
              <div className="text-[10px] text-slate-500 font-mono mb-1">Generated ANSI ASC X12 837P EDI Payload:</div>
              <pre
                className="p-3 bg-slate-50 rounded-lg border border-slate-200 font-mono text-[11px] text-slate-800 overflow-x-auto whitespace-pre leading-relaxed max-h-40 selection:bg-sky-200"
                aria-label="Generated ANSI ASC X12 837P EDI payload"
              >
                {batchPreview.ediContent}
              </pre>
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-slate-200">
              <span className="text-[11px] text-slate-500">
                Saved in file repository.
              </span>
              <div className="space-x-2">
                <button
                  onClick={() => setBatchPreview(null)}
                  className="px-4 py-2 rounded-lg bg-slate-100 text-slate-800 text-xs hover:bg-slate-200 border border-slate-200"
                >
                  Close
                </button>
                <button
                  onClick={() => handleDownloadBatchContent(batchPreview.filename, batchPreview.ediContent)}
                  className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold inline-flex items-center space-x-2 shadow-lg shadow-emerald-600/20"
                >
                  <Download className="w-4 h-4" />
                  <span>
                    {batchPreview.billingRoute === 'WELLSKY_DDD' ? 'Download WellSky 837' : 'Download Claim.MD 837'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 2: INSPECT BATCH EDI (FROM CLAIMS VIEW)                             */}
      {/* ========================================================================= */}
      {inspectBatch && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f141e] border border-slate-700/80 rounded-xl max-w-3xl w-full p-6 space-y-4 shadow-2xl">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center space-x-2">
                  <span>Batch EDI Viewer</span>
                  <span className="text-xs font-mono px-2 py-0.5 rounded bg-sky-500/20 text-sky-400">
                    {inspectBatch.batch_number}
                  </span>
                </h3>
                <div className="text-xs text-slate-400 mt-0.5">
                  Route: {inspectBatch.billing_route} • {inspectBatch.claim_count} claims • ${Number(inspectBatch.total_charges || 0).toFixed(2)}
                </div>
              </div>
              <button onClick={() => setInspectBatch(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center justify-between p-2 rounded bg-slate-950 border border-slate-800 text-[11px] font-mono text-slate-300">
              <div className="truncate max-w-[500px]">
                <span className="text-slate-400">Filename:</span> {inspectBatch.filename}
                <span className="mx-2 text-slate-600">|</span>
                <span className="text-slate-400">SHA-256:</span> {inspectBatch.file_hash}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(inspectBatch.file_hash);
                  setCopiedHash(true);
                  setTimeout(() => setCopiedHash(false), 2000);
                }}
                className="text-[10px] px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 inline-flex items-center space-x-1"
              >
                {copiedHash ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                <span>{copiedHash ? 'Copied' : 'Copy Hash'}</span>
              </button>
            </div>

            <div className="p-3 bg-slate-950 rounded-lg border border-slate-800 font-mono text-[11px] text-sky-300 overflow-x-auto whitespace-pre leading-relaxed max-h-80 selection:bg-sky-500/30">
              {inspectBatch.edi_837_content}
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-slate-800">
              <span className="text-[11px] text-slate-400">
                Status: {inspectBatch.status} (Live Transmission OFF)
              </span>
              <div className="space-x-2">
                <button
                  onClick={() => setInspectBatch(null)}
                  className="px-4 py-2 rounded-lg bg-slate-800 text-slate-300 text-xs"
                >
                  Close
                </button>
                <button
                  onClick={() => handleDownloadBatchById(inspectBatch.id, inspectBatch.filename)}
                  className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold inline-flex items-center space-x-1.5"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download File (.txt)</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 3: INSPECT INDIVIDUAL CLAIM EDI                                     */}
      {/* ========================================================================= */}
      {inspectClaim && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f141e] border border-slate-700/80 rounded-xl max-w-4xl w-full p-6 space-y-4 shadow-2xl">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center space-x-2">
                  <span>ANSI ASC X12 837P Payload Inspector</span>
                  <span className="text-xs font-mono px-2 py-0.5 rounded bg-sky-500/20 text-sky-400">
                    Claim #{inspectClaim.claim_number}
                  </span>
                </h3>
                <div className="text-xs text-slate-400 mt-0.5">
                  Production-grade electronic claim transaction for {inspectClaim.clearinghouse_gateway}
                </div>
              </div>
              <button onClick={() => setInspectClaim(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-900 border border-slate-800 text-xs font-mono text-slate-300">
              <div className="truncate max-w-[500px]">
                <span className="text-slate-400">Filename:</span> {inspectClaim.filename || `ALLEGIANT_837P_${inspectClaim.claim_number}.txt`}
                <span className="mx-2 text-slate-600">|</span>
                <span className="text-slate-400">SHA-256:</span> {inspectClaim.file_hash ? inspectClaim.file_hash.substring(0, 20) + '...' : 'Verified'}
              </div>
              <button
                onClick={() => {
                  if (inspectClaim.file_hash) {
                    navigator.clipboard.writeText(inspectClaim.file_hash);
                    setCopiedHash(true);
                    setTimeout(() => setCopiedHash(false), 2000);
                  }
                }}
                className="text-[10px] px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 inline-flex items-center space-x-1"
              >
                {copiedHash ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                <span>{copiedHash ? 'Copied' : 'Copy Hash'}</span>
              </button>
            </div>

            <div className="p-3 bg-slate-950 rounded-lg border border-slate-800 font-mono text-[11px] text-sky-300 overflow-x-auto whitespace-pre leading-relaxed max-h-96 selection:bg-sky-500/30">
              {inspectClaim.edi_837_content}
            </div>

            <div className="flex justify-between items-center pt-2">
              <div className="text-[11px] text-amber-400 font-mono">
                Clearinghouse Gateway: {inspectClaim.clearinghouse_gateway} • Transport: {inspectClaim.external_transport_status}
              </div>
              <div className="space-x-2">
                <button
                  onClick={() => handleDownload837(inspectClaim.id, inspectClaim.filename)}
                  className="px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold inline-flex items-center space-x-1.5"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download File (.txt)</span>
                </button>
                <button
                  onClick={() => handleOpenClaimAdjudications(inspectClaim)}
                  className="px-4 py-2 rounded-lg bg-indigo-950/80 hover:bg-indigo-900 text-indigo-300 border border-indigo-700/60 text-xs font-semibold inline-flex items-center space-x-1.5"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" />
                  <span>Adjudication History</span>
                </button>
                <button
                  onClick={() => setInspectClaim(null)}
                  className="px-4 py-2 rounded-lg bg-slate-800 text-slate-300 text-xs"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 4: RESUBMISSION / REPLACEMENT CLAIM (FREQUENCY 7)                   */}
      {/* ========================================================================= */}
      {showReplacementModal && selectedClaimForReplacement && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f141e] border border-slate-700/80 rounded-xl max-w-md w-full p-6 space-y-4 shadow-2xl">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-white">
                Create Replacement Claim (Frequency 7)
              </h3>
              <button onClick={() => setShowReplacementModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateReplacement} className="space-y-4 text-xs">
              <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800 space-y-1">
                <div><span className="text-slate-400">Original Claim:</span> {selectedClaimForReplacement.claim_number}</div>
                <div><span className="text-slate-400">Client:</span> {selectedClaimForReplacement.last_name}, {selectedClaimForReplacement.first_name}</div>
                <div><span className="text-slate-400">Payer:</span> {selectedClaimForReplacement.payer_name}</div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1">
                  Original Payer Control Number (ICN / DCN)
                </label>
                <input
                  type="text"
                  placeholder="e.g. 20240901889210"
                  value={replacementForm.payerControlNumber}
                  onChange={(e) => setReplacementForm({ ...replacementForm, payerControlNumber: e.target.value })}
                  className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                />
                <span className="text-[10px] text-slate-400 mt-1 block">
                  Reported in REF*F8 segment for replacement/void claims per ANSI standard.
                </span>
              </div>

              <div>
                <label className="block text-slate-400 mb-1">Resubmission Reason</label>
                <select
                  value={replacementForm.resubmissionReason}
                  onChange={(e) => setReplacementForm({ ...replacementForm, resubmissionReason: e.target.value })}
                  className="w-full glass-input rounded-lg p-2 text-xs"
                >
                  <option value="Corrected CPT/Modifier">Corrected CPT / Modifier</option>
                  <option value="Corrected Diagnosis">Corrected Diagnosis</option>
                  <option value="Corrected Units/Duration">Corrected Units / Duration</option>
                  <option value="Updated Member Policy ID">Updated Member Policy ID</option>
                  <option value="Primary Adjudication Attached">Primary Adjudication Attached</option>
                </select>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowReplacementModal(false)}
                  className="px-4 py-2 rounded-lg bg-slate-800 text-slate-300 text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold shadow-lg shadow-amber-600/20"
                >
                  Generate Corrected 837P
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 5: POST PAYMENT / REAL ADJUDICATION (EOB / ERA)                     */}
      {/* ========================================================================= */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f141e] border border-slate-700/80 rounded-xl max-w-lg w-full p-6 space-y-4 shadow-2xl">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white">
                  Post Payment / EOB Adjudication
                </h3>
                <div className="text-xs text-slate-400 mt-0.5">
                  Record real payer adjudication, contractual adjustments, and patient responsibilities
                </div>
              </div>
              <button onClick={() => setShowPaymentModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handlePostPayment} className="space-y-3.5 text-xs">
              {/* Context Summary */}
              <div className="p-3 rounded-lg bg-slate-900/80 border border-slate-800 grid grid-cols-2 gap-2 text-[11px]">
                <div>
                  <span className="text-slate-400">Payer:</span>{' '}
                  <strong className="text-white">{paymentForm.payerName}</strong>
                </div>
                <div>
                  <span className="text-slate-400">Client:</span>{' '}
                  <strong className="text-white">{paymentForm.clientName || 'Selected Client'}</strong>
                </div>
                <div>
                  <span className="text-slate-400">DOS:</span>{' '}
                  <span className="text-slate-300 font-mono">{paymentForm.dos || 'Service Date'}</span>
                </div>
                <div>
                  <span className="text-slate-400">Service/CPT:</span>{' '}
                  <span className="text-slate-300 font-mono">{paymentForm.serviceCode}</span>
                </div>
              </div>

              {/* Amounts Grid */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Billed Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={paymentForm.amountBilled}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amountBilled: parseFloat(e.target.value) || 0 })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Allowed Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentForm.allowedAmount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, allowedAmount: parseFloat(e.target.value) || 0 })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Payment Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={paymentForm.amountPaid}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amountPaid: parseFloat(e.target.value) || 0 })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono font-bold text-emerald-400"
                  />
                </div>
              </div>

              {/* Cost Shares & Adjustment */}
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <label className="block text-slate-400 mb-1">Deductible ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentForm.deductible}
                    onChange={(e) => setPaymentForm({ ...paymentForm, deductible: parseFloat(e.target.value) || 0 })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Copay ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentForm.copay}
                    onChange={(e) => setPaymentForm({ ...paymentForm, copay: parseFloat(e.target.value) || 0 })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Coinsurance ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentForm.coinsurance}
                    onChange={(e) => setPaymentForm({ ...paymentForm, coinsurance: parseFloat(e.target.value) || 0 })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Adjustment ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentForm.adjustment}
                    onChange={(e) => setPaymentForm({ ...paymentForm, adjustment: parseFloat(e.target.value) || 0 })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono text-amber-400"
                  />
                </div>
              </div>

              {/* Payment Details */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Payment Date</label>
                  <input
                    type="date"
                    required
                    value={paymentForm.paymentDate}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Check / EFT Number</label>
                  <input
                    type="text"
                    placeholder="e.g. EFT-992019"
                    value={paymentForm.checkNumber}
                    onChange={(e) => setPaymentForm({ ...paymentForm, checkNumber: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                  />
                </div>
              </div>

              {/* References & Denial */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">EOB / Artifact Reference</label>
                  <input
                    type="text"
                    placeholder="e.g. EOB-COMMERCIAL-01"
                    value={paymentForm.eobRef}
                    onChange={(e) => setPaymentForm({ ...paymentForm, eobRef: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Denial Reason (if denied)</label>
                  <input
                    type="text"
                    placeholder="e.g. PR-1 Ded / CO-97 Inclusive"
                    value={paymentForm.denialReason}
                    onChange={(e) => setPaymentForm({ ...paymentForm, denialReason: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1">Comments / Notes</label>
                <input
                  type="text"
                  placeholder="Additional adjudication comments"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="w-full glass-input rounded-lg p-2 text-xs"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 rounded-lg bg-slate-800 text-slate-300 text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow-lg shadow-emerald-600/20"
                >
                  Post Adjudication Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 6: MULTI-CLIENT EOB ALLOCATION MODAL                                */}
      {/* ========================================================================= */}
      {showArtifactModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f141e] border border-slate-700/80 rounded-xl max-w-3xl w-full p-6 space-y-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white">
                  Multi-Client EOB / ERA Artifact Ingestion & Line Allocation
                </h3>
                <div className="text-xs text-slate-400 mt-0.5">
                  Upload a combined payer remittance document and allocate payment amounts to individual client claims.
                </div>
              </div>
              <button onClick={() => setShowArtifactModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUploadArtifact} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 mb-1">Select EOB Document File (.txt, .edi, .csv, .pdf)</label>
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="w-full text-xs text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-sky-600 file:text-white hover:file:bg-sky-500"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Document Filename</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. AZ_DDD_REMIT_2026_09_01.txt"
                    value={artifactForm.filename}
                    onChange={(e) => setArtifactForm({ ...artifactForm, filename: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Payer Name</label>
                  <input
                    type="text"
                    required
                    value={artifactForm.payerName}
                    onChange={(e) => setArtifactForm({ ...artifactForm, payerName: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Check / EFT Number</label>
                  <input
                    type="text"
                    placeholder="e.g. EFT-882910"
                    value={artifactForm.checkOrEftNumber}
                    onChange={(e) => setArtifactForm({ ...artifactForm, checkOrEftNumber: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Adjudication Date</label>
                  <input
                    type="date"
                    required
                    value={artifactForm.adjudicationDate}
                    onChange={(e) => setArtifactForm({ ...artifactForm, adjudicationDate: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                  />
                </div>
              </div>

              {/* Allocation Lines */}
              <div className="space-y-2 border-t border-slate-800 pt-3">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-slate-200">
                    Allocate Payments to Client Claims ({artifactLines.length} lines)
                  </span>
                  <button
                    type="button"
                    onClick={addArtifactLine}
                    className="px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-sky-400 text-xs font-medium"
                  >
                    + Add Claim Line
                  </button>
                </div>

                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {artifactLines.map((line, idx) => (
                    <div key={idx} className="p-3 bg-slate-900/80 rounded-lg border border-slate-800 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-[11px] font-semibold text-slate-300">Line #{idx + 1}</span>
                        <button
                          type="button"
                          onClick={() => setArtifactLines(artifactLines.filter((_, i) => i !== idx))}
                          className="text-rose-400 hover:text-rose-300 text-[10px]"
                        >
                          Remove Line
                        </button>
                      </div>

                      <div className="grid grid-cols-4 gap-2">
                        <div className="col-span-2">
                          <label className="block text-[10px] text-slate-400 mb-0.5">Target Claim</label>
                          <select
                            value={line.claimId}
                            onChange={(e) => {
                              const updated = [...artifactLines];
                              updated[idx].claimId = e.target.value;
                              const c = claims.find((c) => c.id === e.target.value);
                              if (c) {
                                updated[idx].amountBilled = c.billed_amount || 65.0;
                                updated[idx].amountPaid = c.billed_amount ? c.billed_amount - 9.75 : 55.25;
                                updated[idx].allowedAmount = c.billed_amount ? c.billed_amount - 9.75 : 55.25;
                              }
                              setArtifactLines(updated);
                            }}
                            className="w-full glass-input rounded p-1.5 text-xs"
                          >
                            {claims.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.claim_number} - {c.last_name}, {c.first_name} (${Number(c.billed_amount || 0).toFixed(2)})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-400 mb-0.5">Paid ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={line.amountPaid}
                            onChange={(e) => {
                              const updated = [...artifactLines];
                              updated[idx].amountPaid = parseFloat(e.target.value) || 0;
                              setArtifactLines(updated);
                            }}
                            className="w-full glass-input rounded p-1.5 text-xs font-mono text-emerald-400"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-400 mb-0.5">Adjustment ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={line.adjustment}
                            onChange={(e) => {
                              const updated = [...artifactLines];
                              updated[idx].adjustment = parseFloat(e.target.value) || 0;
                              setArtifactLines(updated);
                            }}
                            className="w-full glass-input rounded p-1.5 text-xs font-mono text-amber-400"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowArtifactModal(false)}
                  className="px-4 py-2 rounded-lg bg-slate-800 text-slate-300 text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow-lg shadow-emerald-600/20"
                >
                  Save & Allocate Remittance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 5: UPLOAD SCANNED EOB BATCH                                         */}
      {/* ========================================================================= */}
      {showUploadBatchModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0c121e] border border-slate-700/80 rounded-xl max-w-lg w-full p-5 space-y-4 shadow-2xl">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center space-x-2">
                  <FileUp className="w-4 h-4 text-emerald-400" />
                  <span>Upload Scanned EOB Batch (PDF)</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  The original source file remains immutable. Logical EOBs can be segmented afterwards.
                </p>
              </div>
              <button
                onClick={() => setShowUploadBatchModal(false)}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {batchUploadError && (
              <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 text-rose-400" />
                <span>{batchUploadError}</span>
              </div>
            )}

            <form onSubmit={handleUploadBatchSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Select Scanned PDF File <span className="text-rose-400">*</span>
                </label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleBatchFileChange}
                  className="w-full text-xs text-slate-300 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-slate-800 file:text-sky-300 hover:file:bg-slate-700 cursor-pointer border border-slate-700 rounded-lg p-1.5 bg-slate-950"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Filename <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={uploadBatchForm.filename}
                    onChange={(e) => setUploadBatchForm({ ...uploadBatchForm, filename: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs"
                    placeholder="Scanned_Batch_2026_09.pdf"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Received Date <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="date"
                    value={uploadBatchForm.receivedDate}
                    onChange={(e) => setUploadBatchForm({ ...uploadBatchForm, receivedDate: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">Payer Hint (Optional)</label>
                  <input
                    type="text"
                    value={uploadBatchForm.payerHint}
                    onChange={(e) => setUploadBatchForm({ ...uploadBatchForm, payerHint: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs"
                    placeholder="e.g. BCBS, UHC, Mixed"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">Check/Trace # Hint (Optional)</label>
                  <input
                    type="text"
                    value={uploadBatchForm.traceNumberHint}
                    onChange={(e) => setUploadBatchForm({ ...uploadBatchForm, traceNumberHint: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                    placeholder="e.g. EFT-12345"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-400 mb-1">Notes / Description</label>
                <textarea
                  rows={2}
                  value={uploadBatchForm.notes}
                  onChange={(e) => setUploadBatchForm({ ...uploadBatchForm, notes: e.target.value })}
                  className="w-full glass-input rounded-lg p-2 text-xs"
                  placeholder="Scanned batch from front desk mail receipt..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowUploadBatchModal(false)}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={batchUploading || !uploadBatchForm.fileContent}
                  className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-lg shadow-emerald-600/20"
                >
                  {batchUploading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
                  <span>{batchUploading ? 'Uploading & Hashing...' : 'Upload & Archive Batch'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 6: CREATE / SEGMENT CANONICAL EOB                                   */}
      {/* ========================================================================= */}
      {showCreateCanonicalModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#0c121e] border border-slate-700/80 rounded-xl max-w-xl w-full p-5 space-y-4 shadow-2xl my-8">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center space-x-2">
                  <Scissors className="w-4 h-4 text-sky-400" />
                  <span>Segment / Create Canonical EOB Record</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Extract a logical EOB from a source batch or create a standalone canonical remittance.
                </p>
              </div>
              <button
                onClick={() => setShowCreateCanonicalModal(false)}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {canonicalError && (
              <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 text-rose-400" />
                <span>{canonicalError}</span>
              </div>
            )}

            <form onSubmit={handleCreateCanonicalSubmit} className="space-y-3">
              {/* Batch Selector & Page Range */}
              <div className="p-3 bg-sky-950/20 border border-sky-800/40 rounded-lg space-y-2.5">
                <div>
                  <label className="block text-[11px] font-semibold text-sky-200 mb-1">
                    Source Batch (Scanned PDF)
                  </label>
                  <select
                    value={canonicalForm.sourceBatchId}
                    onChange={(e) => {
                      const bId = e.target.value;
                      const batch = eobBatches.find((b) => b.id === bId);
                      setCanonicalForm({
                        ...canonicalForm,
                        sourceBatchId: bId,
                        sourcePageStart: 1,
                        sourcePageEnd: batch ? Math.min(batch.page_count || 1, 1) : 1,
                        payerName: canonicalForm.payerName || batch?.payer_hint || '',
                        receivedDate: batch?.received_date || canonicalForm.receivedDate
                      });
                    }}
                    className="w-full glass-input rounded-lg p-2 text-xs"
                  >
                    <option value="">-- No Source Batch (Direct Canonical Entry) --</option>
                    {eobBatches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.filename} ({b.page_count || 1} pages • Recv: {b.received_date})
                      </option>
                    ))}
                  </select>
                </div>

                {canonicalForm.sourceBatchId && (
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div>
                      <label className="block text-[10.5px] font-semibold text-slate-300 mb-1">
                        Page Start <span className="text-rose-400">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={canonicalForm.sourcePageStart}
                        onChange={(e) =>
                          setCanonicalForm({ ...canonicalForm, sourcePageStart: parseInt(e.target.value) || 1 })
                        }
                        className="w-full glass-input rounded p-1.5 text-xs font-mono"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10.5px] font-semibold text-slate-300 mb-1">
                        Page End <span className="text-rose-400">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={canonicalForm.sourcePageEnd}
                        onChange={(e) =>
                          setCanonicalForm({ ...canonicalForm, sourcePageEnd: parseInt(e.target.value) || 1 })
                        }
                        className="w-full glass-input rounded p-1.5 text-xs font-mono"
                        required
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Payer & Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Payer Name <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={canonicalForm.payerName}
                    onChange={(e) => setCanonicalForm({ ...canonicalForm, payerName: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs"
                    placeholder="e.g. BCBS Arizona, UnitedHealthcare"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Check / EFT / Trace #
                  </label>
                  <input
                    type="text"
                    value={canonicalForm.checkOrEftNumber}
                    onChange={(e) => setCanonicalForm({ ...canonicalForm, checkOrEftNumber: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                    placeholder="e.g. EFT-99214"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Received Date <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="date"
                    value={canonicalForm.receivedDate}
                    onChange={(e) => setCanonicalForm({ ...canonicalForm, receivedDate: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Payment Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={canonicalForm.paymentDate}
                    onChange={(e) => setCanonicalForm({ ...canonicalForm, paymentDate: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Total Payment Amount ($) <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={canonicalForm.totalPaymentAmount}
                    onChange={(e) => setCanonicalForm({ ...canonicalForm, totalPaymentAmount: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono text-emerald-400 font-bold"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Review Status
                  </label>
                  <select
                    value={canonicalForm.reviewStatus}
                    onChange={(e) =>
                      setCanonicalForm({
                        ...canonicalForm,
                        reviewStatus: e.target.value as 'NEEDS_REVIEW' | 'VERIFIED'
                      })
                    }
                    className="w-full glass-input rounded-lg p-2 text-xs"
                  >
                    <option value="NEEDS_REVIEW">Needs Review</option>
                    <option value="VERIFIED">Verified</option>
                  </select>
                </div>
              </div>

              {/* Client Attachment Selection */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                  Attach Client(s) (Optional)
                </label>
                <div className="max-h-28 overflow-y-auto border border-slate-700/60 rounded-lg p-2 bg-slate-950 space-y-1">
                  {clientsList.map((client) => {
                    const isSelected = canonicalForm.selectedClientIds.includes(client.id);
                    return (
                      <label
                        key={client.id}
                        className={`flex items-center gap-2 p-1 rounded text-xs cursor-pointer ${
                          isSelected ? 'bg-sky-950/50 text-sky-200' : 'text-slate-300 hover:bg-slate-800'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCanonicalForm({
                                ...canonicalForm,
                                selectedClientIds: [...canonicalForm.selectedClientIds, client.id]
                              });
                            } else {
                              setCanonicalForm({
                                ...canonicalForm,
                                selectedClientIds: canonicalForm.selectedClientIds.filter((id) => id !== client.id)
                              });
                            }
                          }}
                          className="rounded text-sky-500 focus:ring-0"
                        />
                        <span className="font-semibold">{client.last_name}, {client.first_name}</span>
                        <span className="text-[10px] font-mono text-slate-400">ID: {client.id}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Payment Header Linkage */}
              <div className="p-3 bg-slate-900/60 border border-slate-800 rounded-lg space-y-2">
                <label className="block text-[11px] font-semibold text-slate-300">
                  Payment Header / Check Linkage
                </label>
                <div className="flex gap-4 text-xs">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="pmtOpt"
                      checked={canonicalForm.paymentHeaderOption === 'NONE'}
                      onChange={() => setCanonicalForm({ ...canonicalForm, paymentHeaderOption: 'NONE' })}
                    />
                    <span>Unlinked</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="pmtOpt"
                      checked={canonicalForm.paymentHeaderOption === 'EXISTING'}
                      onChange={() => setCanonicalForm({ ...canonicalForm, paymentHeaderOption: 'EXISTING' })}
                    />
                    <span>Link Existing</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="pmtOpt"
                      checked={canonicalForm.paymentHeaderOption === 'NEW'}
                      onChange={() =>
                        setCanonicalForm({
                          ...canonicalForm,
                          paymentHeaderOption: 'NEW',
                          newHeaderPayer: canonicalForm.payerName,
                          newHeaderAmount: canonicalForm.totalPaymentAmount,
                          newHeaderCheckNumber: canonicalForm.checkOrEftNumber
                        })
                      }
                    />
                    <span>Create New Header</span>
                  </label>
                </div>

                {canonicalForm.paymentHeaderOption === 'EXISTING' && (
                  <select
                    value={canonicalForm.paymentHeaderId}
                    onChange={(e) => setCanonicalForm({ ...canonicalForm, paymentHeaderId: e.target.value })}
                    className="w-full glass-input rounded p-1.5 text-xs mt-1"
                  >
                    <option value="">-- Select Payment Header --</option>
                    {paymentHeaders.map((ph) => (
                      <option key={ph.id} value={ph.id}>
                        {ph.id} • {ph.payer_name} • ${Number(ph.total_payment_amount).toFixed(2)} ({ph.check_or_eft_number})
                      </option>
                    ))}
                  </select>
                )}

                {canonicalForm.paymentHeaderOption === 'NEW' && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <div>
                      <input
                        type="text"
                        placeholder="Payer Name"
                        value={canonicalForm.newHeaderPayer}
                        onChange={(e) => setCanonicalForm({ ...canonicalForm, newHeaderPayer: e.target.value })}
                        className="w-full glass-input rounded p-1.5 text-xs"
                      />
                    </div>
                    <div>
                      <select
                        value={canonicalForm.newHeaderMethod}
                        onChange={(e) =>
                          setCanonicalForm({
                            ...canonicalForm,
                            newHeaderMethod: e.target.value as any
                          })
                        }
                        className="w-full glass-input rounded p-1.5 text-xs"
                      >
                        <option value="EFT">EFT</option>
                        <option value="CHECK">Paper Check</option>
                        <option value="ACH">ACH</option>
                        <option value="VIRTUAL_CARD">Virtual Card</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-400 mb-1">Notes</label>
                <textarea
                  rows={2}
                  value={canonicalForm.notes}
                  onChange={(e) => setCanonicalForm({ ...canonicalForm, notes: e.target.value })}
                  className="w-full glass-input rounded-lg p-2 text-xs"
                  placeholder="Segmented remittance notes..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowCreateCanonicalModal(false)}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={canonicalSubmitting}
                  className="px-4 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-md"
                >
                  {canonicalSubmitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Scissors className="w-3.5 h-3.5" />}
                  <span>{canonicalSubmitting ? 'Creating Segment...' : 'Create Canonical EOB'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 7: MANAGE ATTACHED CLIENTS FOR CANONICAL EOB                        */}
      {/* ========================================================================= */}
      {activeEobForClients && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0c121e] border border-slate-700/80 rounded-xl max-w-md w-full p-5 space-y-4 shadow-2xl">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center space-x-2">
                  <Users className="w-4 h-4 text-sky-400" />
                  <span>Attached Clients</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  EOB: <strong className="text-white">{activeEobForClients.payer_name}</strong> (${Number(activeEobForClients.total_payment_amount).toFixed(2)})
                </p>
              </div>
              <button
                onClick={() => setActiveEobForClients(null)}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Currently Attached */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-semibold text-slate-400">Current Clients</label>
              {activeEobForClients.attached_clients && activeEobForClients.attached_clients.length > 0 ? (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {activeEobForClients.attached_clients.map((c) => (
                    <div
                      key={c.clientId}
                      className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800 text-xs"
                    >
                      <div>
                        <div className="font-semibold text-slate-200">{c.clientName}</div>
                        <div className="text-[10px] font-mono text-slate-400">ID: {c.clientId} {c.ahcccsId ? `• AHCCCS: ${c.ahcccsId}` : ''}</div>
                      </div>
                      <button
                        onClick={() => handleDetachClientFromEob(activeEobForClients.id, c.clientId)}
                        className="px-2 py-0.5 rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 text-[10px] flex items-center gap-1 cursor-pointer"
                      >
                        <Unlink className="w-3 h-3" />
                        <span>Detach</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic p-2 bg-slate-950 rounded">No clients currently attached.</p>
              )}
            </div>

            {/* Attach New Client */}
            <div className="space-y-2 pt-2 border-t border-slate-800">
              <label className="block text-[11px] font-semibold text-slate-300">Attach Another Client</label>
              <input
                type="text"
                placeholder="Search client name or ID..."
                value={clientSearchQuery}
                onChange={(e) => setClientSearchQuery(e.target.value)}
                className="w-full glass-input rounded p-1.5 text-xs mb-1.5"
              />
              <select
                value={selectedClientIdToAttach}
                onChange={(e) => setSelectedClientIdToAttach(e.target.value)}
                className="w-full glass-input rounded-lg p-2 text-xs"
              >
                <option value="">-- Choose Client to Attach --</option>
                {clientsList
                  .filter((c) => {
                    const q = clientSearchQuery.toLowerCase();
                    if (!q) return true;
                    return `${c.first_name} ${c.last_name} ${c.id}`.toLowerCase().includes(q);
                  })
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.last_name}, {c.first_name} ({c.id})
                    </option>
                  ))}
              </select>

              <button
                onClick={() => handleAttachClientToEob(activeEobForClients.id, selectedClientIdToAttach)}
                disabled={!selectedClientIdToAttach}
                className="w-full px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-bold flex items-center justify-center gap-1.5 cursor-pointer mt-2"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Attach Selected Client</span>
              </button>
            </div>

            <div className="flex justify-end pt-2 border-t border-slate-800">
              <button
                onClick={() => setActiveEobForClients(null)}
                className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs cursor-pointer"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 8: LINK PAYMENT HEADER TO CANONICAL EOB                             */}
      {/* ========================================================================= */}
      {activeEobForPaymentHeader && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0c121e] border border-slate-700/80 rounded-xl max-w-md w-full p-5 space-y-4 shadow-2xl">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center space-x-2">
                  <Link2 className="w-4 h-4 text-sky-400" />
                  <span>Link Payment Header</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Link Canonical EOB: <strong className="text-white">{activeEobForPaymentHeader.payer_name}</strong>
                </p>
              </div>
              <button
                onClick={() => setActiveEobForPaymentHeader(null)}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Select Existing Payment Header
                </label>
                <select
                  value={selectedPaymentHeaderIdToLink}
                  onChange={(e) => setSelectedPaymentHeaderIdToLink(e.target.value)}
                  className="w-full glass-input rounded-lg p-2 text-xs"
                >
                  <option value="">-- Choose Payment Header --</option>
                  {paymentHeaders.map((ph) => (
                    <option key={ph.id} value={ph.id}>
                      {ph.id} • {ph.payer_name} • ${Number(ph.total_payment_amount).toFixed(2)} ({ph.payment_method}, {ph.check_or_eft_number})
                    </option>
                  ))}
                </select>
              </div>

              <div className="p-3 bg-sky-950/20 border border-sky-800/40 rounded-lg text-xs text-slate-300 space-y-1">
                <p className="font-semibold text-sky-300">Don't see the deposit header?</p>
                <p className="text-[11px] text-slate-400">
                  You can record a new Payment Header in the Payment & EOB Ledger tab.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setActiveEobForPaymentHeader(null)}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() =>
                    handleLinkPaymentHeaderSubmit(activeEobForPaymentHeader.id, selectedPaymentHeaderIdToLink)
                  }
                  disabled={!selectedPaymentHeaderIdToLink}
                  className="px-4 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                >
                  <Link2 className="w-3.5 h-3.5" />
                  <span>Link Header</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 9: CREATE STANDALONE PAYMENT HEADER                                 */}
      {/* ========================================================================= */}
      {showCreatePaymentHeaderModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0c121e] border border-slate-700/80 rounded-xl max-w-md w-full p-5 space-y-4 shadow-2xl">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center space-x-2">
                  <CreditCard className="w-4 h-4 text-emerald-400" />
                  <span>Record Payment Header (Deposit)</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Record incoming EFT or paper check deposit before claim allocation.
                </p>
              </div>
              <button
                onClick={() => setShowCreatePaymentHeaderModal(false)}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreatePaymentHeaderSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Payer Name <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  value={paymentHeaderForm.payerName}
                  onChange={(e) => setPaymentHeaderForm({ ...paymentHeaderForm, payerName: e.target.value })}
                  className="w-full glass-input rounded-lg p-2 text-xs"
                  placeholder="e.g. Arizona DDD, Blue Cross Blue Shield"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Payment Date <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="date"
                    value={paymentHeaderForm.paymentDate}
                    onChange={(e) => setPaymentHeaderForm({ ...paymentHeaderForm, paymentDate: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Total Amount ($) <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentHeaderForm.totalPaymentAmount}
                    onChange={(e) =>
                      setPaymentHeaderForm({ ...paymentHeaderForm, totalPaymentAmount: e.target.value })
                    }
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono text-emerald-400 font-bold"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Check / EFT Number
                  </label>
                  <input
                    type="text"
                    value={paymentHeaderForm.checkOrEftNumber}
                    onChange={(e) =>
                      setPaymentHeaderForm({ ...paymentHeaderForm, checkOrEftNumber: e.target.value })
                    }
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                    placeholder="e.g. EFT-992144"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Payment Method
                  </label>
                  <select
                    value={paymentHeaderForm.paymentMethod}
                    onChange={(e) =>
                      setPaymentHeaderForm({
                        ...paymentHeaderForm,
                        paymentMethod: e.target.value as any
                      })
                    }
                    className="w-full glass-input rounded-lg p-2 text-xs"
                  >
                    <option value="EFT">EFT</option>
                    <option value="CHECK">Paper Check</option>
                    <option value="ACH">ACH</option>
                    <option value="VIRTUAL_CARD">Virtual Card</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-400 mb-1">Notes</label>
                <textarea
                  rows={2}
                  value={paymentHeaderForm.notes}
                  onChange={(e) => setPaymentHeaderForm({ ...paymentHeaderForm, notes: e.target.value })}
                  className="w-full glass-input rounded-lg p-2 text-xs"
                  placeholder="Bank deposit batch ref or notes..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowCreatePaymentHeaderModal(false)}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-lg shadow-emerald-600/20"
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  <span>Record Payment Header</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 10: INLINE DOCUMENT VIEWER (PDF)                                    */}
      {/* ========================================================================= */}
      {inlineDocViewer && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-[#0b101b] border border-slate-700/80 rounded-xl w-full max-w-4xl h-[85vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="flex justify-between items-center px-4 py-3 border-b border-slate-800 bg-slate-900/80">
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4 text-sky-400" />
                <span className="font-semibold text-sm text-slate-200 truncate max-w-lg">
                  {inlineDocViewer.title}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <a
                  href={inlineDocViewer.url.replace('/content', '/download')}
                  download={inlineDocViewer.filename}
                  className="px-3 py-1 rounded bg-slate-800 hover:bg-slate-700 text-sky-300 border border-slate-700 text-xs font-semibold flex items-center gap-1 cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download</span>
                </a>
                <button
                  onClick={() => setInlineDocViewer(null)}
                  className="p-1 rounded text-slate-400 hover:text-white cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-slate-950 p-2 overflow-hidden">
              <iframe
                src={inlineDocViewer.url}
                title={inlineDocViewer.title}
                className="w-full h-full rounded border border-slate-800"
              />
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 11: RECORD CLAIM ADJUDICATION EVIDENCE (PHASE 1)                    */}
      {/* ========================================================================= */}
      {showAdjudicateModal && activeEobForAdjudication && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0c121e] border border-slate-700/80 rounded-xl max-w-2xl w-full p-5 space-y-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center space-x-2">
                  <FileSpreadsheet className="w-4 h-4 text-indigo-400" />
                  <span>Record Claim Adjudication Evidence</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Link verified primary-payer adjudication facts to a specific claim from canonical EOB <span className="font-mono text-slate-200">{activeEobForAdjudication.filename}</span>.
                </p>
              </div>
              <button
                onClick={() => setShowAdjudicateModal(false)}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Crucial Non-Destructive Banner */}
            <div className="p-3 bg-indigo-950/40 border border-indigo-700/50 rounded-lg text-xs space-y-1">
              <div className="flex items-center gap-1.5 font-bold text-indigo-200">
                <Info className="w-4 h-4 text-indigo-400 shrink-0" />
                <span>Auditable Primary Adjudication Bridge (Phase 1)</span>
              </div>
              <p className="text-slate-300 text-[11px] leading-relaxed">
                Recording adjudication captures payer remittance facts, CARC/RARC codes, and TPL qualification.
                <strong className="text-amber-300 ml-1">It does NOT mark the claim paid, alter balances, modify billing readiness, or automatically generate/transmit secondary 837 files.</strong>
              </p>
            </div>

            {adjudicateError && (
              <div className="p-2.5 bg-rose-950/50 border border-rose-800/80 rounded-lg text-rose-300 text-xs flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>{adjudicateError}</span>
              </div>
            )}

            <form onSubmit={handleSaveAdjudication} className="space-y-4">
              {/* Target Claim Selector */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Target Claim <span className="text-rose-400">*</span>
                </label>
                {loadingEligibleClaims ? (
                  <div className="text-xs text-slate-400 py-2">Loading eligible claims...</div>
                ) : eligibleClaimsForEob.length === 0 ? (
                  <div className="p-3 bg-slate-900/80 rounded-lg border border-slate-800 text-xs text-amber-300">
                    No claims found. Please ensure claims exist in the system.
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                      <input
                        type="text"
                        placeholder="Search by client name, claim #, or AHCCCS ID..."
                        value={claimSearchFilter}
                        onChange={(e) => setClaimSearchFilter(e.target.value)}
                        className="w-full glass-input rounded-lg pl-8 pr-3 py-1.5 text-xs"
                      />
                    </div>
                    <select
                      value={adjudicateForm.claimId}
                      onChange={(e) => handleSelectClaimForAdjudication(e.target.value)}
                      className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                      required
                    >
                      <option value="">-- Select Claim to Adjudicate --</option>
                      {eligibleClaimsForEob
                        .filter((c) => {
                          if (!claimSearchFilter) return true;
                          const q = claimSearchFilter.toLowerCase();
                          return (
                            (c.claim_number || '').toLowerCase().includes(q) ||
                            (c.client_name || '').toLowerCase().includes(q) ||
                            (c.ahcccs_id || '').toLowerCase().includes(q) ||
                            (c.payer_name || '').toLowerCase().includes(q)
                          );
                        })
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.is_attached_client ? '★ [Attached Client] ' : ''}
                            {c.claim_number} | {c.client_name} ({c.ahcccs_id || 'N/A'}) | DOS: {c.dos_start || c.dos || 'N/A'} | Billed: ${Number(c.billed_amount || 0).toFixed(2)} | Route: {c.billing_route}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Payer & Adjudication Outcome */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Payer Name <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={adjudicateForm.payerName}
                    onChange={(e) => setAdjudicateForm({ ...adjudicateForm, payerName: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Adjudication Outcome <span className="text-rose-400">*</span>
                  </label>
                  <select
                    value={adjudicateForm.adjudicationOutcome}
                    onChange={(e) => {
                      const outcome = e.target.value as any;
                      setAdjudicateForm({
                        ...adjudicateForm,
                        adjudicationOutcome: outcome,
                        tplEvidenceStatus: outcome === 'PAID' || outcome === 'PARTIAL' ? 'VALID_PAYMENT' : outcome === 'DENIED' ? 'VALID_DENIAL' : 'PENDING'
                      });
                    }}
                    className="w-full glass-input rounded-lg p-2 text-xs font-semibold"
                  >
                    <option value="PAID">PAID (Full Payment)</option>
                    <option value="PARTIAL">PARTIAL (Partial Payment / Split)</option>
                    <option value="DENIED">DENIED ($0 Non-Covered / Exhausted)</option>
                    <option value="REVERSED">REVERSED (Prior Adjudication Rescinded)</option>
                    <option value="CORRECTION">CORRECTION (Corrected Adjudication)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Adjudication Date <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="date"
                    value={adjudicateForm.adjudicationDate}
                    onChange={(e) => setAdjudicateForm({ ...adjudicateForm, adjudicationDate: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs"
                    required
                  />
                </div>
              </div>

              {/* Payer Control Number & Payer Sequence */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Payer Control Number (ICN / CCN)
                  </label>
                  <input
                    type="text"
                    value={adjudicateForm.payerControlNumber}
                    onChange={(e) => setAdjudicateForm({ ...adjudicateForm, payerControlNumber: e.target.value })}
                    className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                    placeholder="e.g. 262391008472"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                    Payer Sequence
                  </label>
                  <select
                    value={adjudicateForm.payerSequence}
                    onChange={(e) => setAdjudicateForm({ ...adjudicateForm, payerSequence: e.target.value as any })}
                    className="w-full glass-input rounded-lg p-2 text-xs"
                  >
                    <option value="PRIMARY">PRIMARY (Commercial / Private Carrier)</option>
                    <option value="SECONDARY">SECONDARY</option>
                  </select>
                </div>
              </div>

              {/* Financial Breakdown */}
              <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-800 space-y-2">
                <div className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                  Remittance Financial Breakdown
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] text-slate-400 mb-0.5">Billed Amount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={adjudicateForm.billedAmount}
                      onChange={(e) => setAdjudicateForm({ ...adjudicateForm, billedAmount: e.target.value })}
                      className="w-full glass-input rounded p-1.5 text-xs font-mono"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-400 mb-0.5">Allowed Amount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={adjudicateForm.allowedAmount}
                      onChange={(e) => setAdjudicateForm({ ...adjudicateForm, allowedAmount: e.target.value })}
                      className="w-full glass-input rounded p-1.5 text-xs font-mono"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-emerald-400 font-semibold mb-0.5">Paid Amount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={adjudicateForm.paidAmount}
                      onChange={(e) => setAdjudicateForm({ ...adjudicateForm, paidAmount: e.target.value })}
                      className="w-full glass-input rounded p-1.5 text-xs font-mono text-emerald-300 font-bold"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-400 mb-0.5">Contractual Adj ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={adjudicateForm.contractualAdjustment}
                      onChange={(e) => setAdjudicateForm({ ...adjudicateForm, contractualAdjustment: e.target.value })}
                      className="w-full glass-input rounded p-1.5 text-xs font-mono text-amber-300"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-400 mb-0.5">Patient Resp ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={adjudicateForm.patientResponsibility}
                      onChange={(e) => setAdjudicateForm({ ...adjudicateForm, patientResponsibility: e.target.value })}
                      className="w-full glass-input rounded p-1.5 text-xs font-mono"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-sky-400 font-semibold mb-0.5">Remaining Sec. Balance ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={adjudicateForm.remainingSecondaryBalance}
                      onChange={(e) => setAdjudicateForm({ ...adjudicateForm, remainingSecondaryBalance: e.target.value })}
                      className="w-full glass-input rounded p-1.5 text-xs font-mono text-sky-300"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              {/* Adjustment Codes & Denial */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <label className="block text-[10px] text-slate-400 mb-0.5">Group Code</label>
                  <select
                    value={adjudicateForm.groupCode}
                    onChange={(e) => setAdjudicateForm({ ...adjudicateForm, groupCode: e.target.value })}
                    className="w-full glass-input rounded p-1.5 text-xs font-mono"
                  >
                    <option value="">None</option>
                    <option value="CO">CO (Contractual Obligation)</option>
                    <option value="PR">PR (Patient Responsibility)</option>
                    <option value="OA">OA (Other Adjustment)</option>
                    <option value="PI">PI (Payer Initiated)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-slate-400 mb-0.5">CARC Code</label>
                  <input
                    type="text"
                    value={adjudicateForm.carcCode}
                    onChange={(e) => setAdjudicateForm({ ...adjudicateForm, carcCode: e.target.value })}
                    className="w-full glass-input rounded p-1.5 text-xs font-mono"
                    placeholder="e.g. 45, 1, 96"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-400 mb-0.5">RARC Code</label>
                  <input
                    type="text"
                    value={adjudicateForm.rarcCode}
                    onChange={(e) => setAdjudicateForm({ ...adjudicateForm, rarcCode: e.target.value })}
                    className="w-full glass-input rounded p-1.5 text-xs font-mono"
                    placeholder="e.g. MA01, N1"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-400 mb-0.5">Reason Code</label>
                  <input
                    type="text"
                    value={adjudicateForm.reasonCode}
                    onChange={(e) => setAdjudicateForm({ ...adjudicateForm, reasonCode: e.target.value })}
                    className="w-full glass-input rounded p-1.5 text-xs font-mono"
                    placeholder="e.g. PR-1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                  Denial / Adjustment Reason Text
                </label>
                <input
                  type="text"
                  value={adjudicateForm.denialReason}
                  onChange={(e) => setAdjudicateForm({ ...adjudicateForm, denialReason: e.target.value })}
                  className="w-full glass-input rounded-lg p-2 text-xs"
                  placeholder="e.g. Charges exceed fee schedule allowance or Service not covered by plan"
                />
              </div>

              {/* TPL Evidence Classification */}
              <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-800 space-y-1.5">
                <label className="block text-xs font-semibold text-slate-300">
                  TPL Evidence Classification
                </label>
                <select
                  value={adjudicateForm.tplEvidenceStatus}
                  onChange={(e) => setAdjudicateForm({ ...adjudicateForm, tplEvidenceStatus: e.target.value as any })}
                  className="w-full glass-input rounded-lg p-2 text-xs font-semibold"
                >
                  <option value="PENDING">PENDING — Not yet evaluated for secondary submission</option>
                  <option value="VALID_PAYMENT">VALID_PAYMENT — Qualifies as valid primary adjudication payment for secondary submission</option>
                  <option value="VALID_DENIAL">VALID_DENIAL — Qualifies as valid benefit exhaustion / non-covered primary denial</option>
                  <option value="INSUFFICIENT_EVIDENCE">INSUFFICIENT_EVIDENCE — Clerical error, missing info, or invalid primary denial</option>
                </select>
                <p className="text-[10px] text-slate-400">
                  Note: Marking VALID_PAYMENT or VALID_DENIAL creates auditable evidence; it does NOT automatically transmit or advance claims without human review.
                </p>
              </div>

              {/* Optional Payment Header Link */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                  Associated Bank Deposit / Payment Header (Optional)
                </label>
                <select
                  value={adjudicateForm.paymentHeaderId}
                  onChange={(e) => setAdjudicateForm({ ...adjudicateForm, paymentHeaderId: e.target.value })}
                  className="w-full glass-input rounded-lg p-2 text-xs font-mono"
                >
                  <option value="">None / $0 Denial (No Deposit Header)</option>
                  {paymentHeaders.map((hdr) => (
                    <option key={hdr.id} value={hdr.id}>
                      {hdr.payer_name} | ${Number(hdr.total_payment_amount).toFixed(2)} | {hdr.payment_method} {hdr.check_or_eft_number ? `(${hdr.check_or_eft_number})` : ''} | {hdr.payment_date}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-400 mb-1">Notes / Audit Log</label>
                <textarea
                  rows={2}
                  value={adjudicateForm.notes}
                  onChange={(e) => setAdjudicateForm({ ...adjudicateForm, notes: e.target.value })}
                  className="w-full glass-input rounded-lg p-2 text-xs"
                  placeholder="Additional adjudication notes, COB instructions, or reviewer rationale..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowAdjudicateModal(false)}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={adjudicateSubmitting}
                  className="px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-lg shadow-indigo-600/20"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>{adjudicateSubmitting ? 'Recording...' : 'Record Adjudication Evidence'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 12: CLAIM ADJUDICATION HISTORY & REVERSALS                           */}
      {/* ========================================================================= */}
      {showClaimAdjudicationsModal && activeClaimForAdjudications && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0c121e] border border-slate-700/80 rounded-xl max-w-3xl w-full p-5 space-y-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center space-x-2">
                  <FileText className="w-4 h-4 text-sky-400" />
                  <span>Adjudication Evidence History: {activeClaimForAdjudications.claim_number}</span>
                </h3>
                <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap gap-2">
                  <span>Client: <strong className="text-slate-200">{activeClaimForAdjudications.client_name || `${activeClaimForAdjudications.first_name || ''} ${activeClaimForAdjudications.last_name || ''}`}</strong></span>
                  <span>• Route: <strong className="text-slate-200">{activeClaimForAdjudications.billing_route}</strong></span>
                  <span>• Billed: <strong className="text-slate-200">${Number(activeClaimForAdjudications.billed_amount || 0).toFixed(2)}</strong></span>
                  <span>• Status: <strong className="text-slate-200">{activeClaimForAdjudications.status}</strong></span>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowClaimAdjudicationsModal(false);
                  setActiveClaimForAdjudications(null);
                  setReversingAdjudicationId(null);
                }}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingClaimAdjudications ? (
              <div className="p-8 text-center text-xs text-slate-400">Loading adjudication history...</div>
            ) : claimAdjudicationsList.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400 space-y-2">
                <FileSpreadsheet className="w-8 h-8 text-slate-500 mx-auto" />
                <p className="font-semibold text-slate-300">No primary adjudication events recorded yet for this claim.</p>
                <p className="text-[11px] text-slate-500">
                  To record primary adjudication, go to Remittances &gt; Canonical EOB Records, select the appropriate EOB, and click "+ Adjudicate Claim".
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {claimAdjudicationsList.map((adj) => (
                  <div
                    key={adj.id}
                    className={`p-4 rounded-xl border ${
                      adj.adjudication_outcome === 'REVERSED'
                        ? 'bg-rose-950/20 border-rose-800/40'
                        : adj.reversal_of_adjudication_id
                        ? 'bg-amber-950/20 border-amber-800/40'
                        : 'bg-slate-900/60 border-slate-800'
                    } space-y-3`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800/60 pb-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            adj.adjudication_outcome === 'PAID'
                              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                              : adj.adjudication_outcome === 'DENIED'
                              ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                              : adj.adjudication_outcome === 'PARTIAL'
                              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                              : 'bg-slate-500/20 text-slate-300 border border-slate-500/40'
                          }`}
                        >
                          {adj.adjudication_outcome}
                        </span>
                        <span className="text-xs font-semibold text-slate-200">
                          {adj.payer_name} ({adj.payer_sequence})
                        </span>
                        <span className="text-[11px] text-slate-400 font-mono">
                          Date: {adj.adjudication_date}
                        </span>
                        {adj.reversal_of_adjudication_id && (
                          <span className="text-[10px] font-mono text-amber-300 bg-amber-950/60 px-1.5 py-0.5 rounded border border-amber-800/50">
                            Reversal of {adj.reversal_of_adjudication_id.slice(0, 8)}...
                          </span>
                        )}
                      </div>

                      {/* TPL Evidence Status Badge */}
                      <div>
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            adj.tpl_evidence_status === 'VALID_PAYMENT'
                              ? 'bg-emerald-950 text-emerald-300 border border-emerald-700/60'
                              : adj.tpl_evidence_status === 'VALID_DENIAL'
                              ? 'bg-sky-950 text-sky-300 border border-sky-700/60'
                              : adj.tpl_evidence_status === 'INSUFFICIENT_EVIDENCE'
                              ? 'bg-rose-950 text-rose-300 border border-rose-700/60'
                              : 'bg-amber-950 text-amber-300 border border-amber-700/60'
                          }`}
                        >
                          TPL: {adj.tpl_evidence_status}
                        </span>
                      </div>
                    </div>

                    {/* Financial Metrics */}
                    <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-xs font-mono">
                      <div className="bg-slate-950/60 p-2 rounded border border-slate-800/60">
                        <div className="text-[9.5px] text-slate-500 uppercase">Billed</div>
                        <div className="text-slate-200 font-bold">${Number(adj.billed_amount || 0).toFixed(2)}</div>
                      </div>
                      <div className="bg-slate-950/60 p-2 rounded border border-slate-800/60">
                        <div className="text-[9.5px] text-slate-500 uppercase">Allowed</div>
                        <div className="text-slate-300">${Number(adj.allowed_amount || 0).toFixed(2)}</div>
                      </div>
                      <div className="bg-slate-950/60 p-2 rounded border border-slate-800/60">
                        <div className="text-[9.5px] text-emerald-400 uppercase">Paid</div>
                        <div className="text-emerald-400 font-bold">${Number(adj.paid_amount || 0).toFixed(2)}</div>
                      </div>
                      <div className="bg-slate-950/60 p-2 rounded border border-slate-800/60">
                        <div className="text-[9.5px] text-amber-400 uppercase">Contract Adj</div>
                        <div className="text-amber-300">${Number(adj.contractual_adjustment || 0).toFixed(2)}</div>
                      </div>
                      <div className="bg-slate-950/60 p-2 rounded border border-slate-800/60">
                        <div className="text-[9.5px] text-slate-400 uppercase">Pt Resp</div>
                        <div className="text-slate-300">${Number(adj.patient_responsibility || 0).toFixed(2)}</div>
                      </div>
                      <div className="bg-slate-950/60 p-2 rounded border border-slate-800/60">
                        <div className="text-[9.5px] text-sky-400 uppercase">Sec. Balance</div>
                        <div className="text-sky-300 font-bold">${Number(adj.remaining_secondary_balance || 0).toFixed(2)}</div>
                      </div>
                    </div>

                    {/* Codes & Reasons */}
                    {(adj.group_code || adj.carc_code || adj.rarc_code || adj.denial_reason || adj.payer_control_number) && (
                      <div className="text-xs text-slate-300 bg-slate-950/40 p-2.5 rounded border border-slate-800/50 space-y-1">
                        <div className="flex flex-wrap gap-2 text-[11px] font-mono">
                          {adj.payer_control_number && <span>ICN/CCN: <strong className="text-slate-200">{adj.payer_control_number}</strong></span>}
                          {adj.group_code && <span>Group: <strong className="text-indigo-300">{adj.group_code}</strong></span>}
                          {adj.carc_code && <span>CARC: <strong className="text-amber-300">{adj.carc_code}</strong></span>}
                          {adj.rarc_code && <span>RARC: <strong className="text-sky-300">{adj.rarc_code}</strong></span>}
                        </div>
                        {adj.denial_reason && (
                          <div className="text-[11px] text-slate-400">
                            Reason: <span className="text-slate-200">{adj.denial_reason}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Lineage References */}
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-[10px] text-slate-400 border-t border-slate-800/40">
                      <div className="flex items-center gap-3">
                        {adj.eob_filename && (
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3 text-sky-400" />
                            <span>EOB: {adj.eob_filename}</span>
                            <button
                              onClick={() =>
                                setInlineDocViewer({
                                  title: `Canonical EOB: ${adj.eob_filename}`,
                                  url: `/api/billing/canonical-eobs/${adj.canonical_eob_id}/content`,
                                  filename: adj.eob_filename || 'eob.pdf'
                                })
                              }
                              className="text-sky-400 hover:text-sky-300 underline ml-1 cursor-pointer"
                            >
                              View PDF
                            </button>
                          </span>
                        )}
                        {adj.payment_header_check && (
                          <span className="flex items-center gap-1 font-mono">
                            <CreditCard className="w-3 h-3 text-emerald-400" />
                            <span>Deposit: {adj.payment_header_check} (${Number(adj.payment_header_total || 0).toFixed(2)})</span>
                          </span>
                        )}
                      </div>
                      <div className="text-slate-500 font-mono">
                        Recorded {new Date(adj.created_at).toLocaleString()} by {adj.created_by}
                      </div>
                    </div>

                    {/* Reversal / Correction Action */}
                    {adj.adjudication_outcome !== 'REVERSED' && (
                      <div className="pt-1">
                        {reversingAdjudicationId === adj.id ? (
                          <div className="p-2.5 bg-rose-950/40 border border-rose-800/60 rounded space-y-2">
                            <div className="text-xs font-semibold text-rose-300">
                              Record Reversal / Correction for this Adjudication Event
                            </div>
                            <input
                              type="text"
                              value={reversalNotes}
                              onChange={(e) => setReversalNotes(e.target.value)}
                              placeholder="Audit reason for reversal / correction..."
                              className="w-full glass-input rounded p-1.5 text-xs"
                            />
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => {
                                  setReversingAdjudicationId(null);
                                  setReversalNotes('');
                                }}
                                className="px-2 py-1 rounded bg-slate-800 text-slate-300 text-xs"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleReverseAdjudication(adj.id)}
                                disabled={reversalSubmitting}
                                className="px-3 py-1 rounded bg-rose-700 hover:bg-rose-600 text-white text-xs font-bold cursor-pointer"
                              >
                                {reversalSubmitting ? 'Recording...' : 'Confirm Reversal'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setReversingAdjudicationId(adj.id);
                              setReversalNotes('');
                            }}
                            className="text-[10px] text-amber-400 hover:text-amber-300 underline font-mono cursor-pointer"
                          >
                            + Record Reversal / Correction Event
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end pt-3 border-t border-slate-800">
              <button
                onClick={() => {
                  setShowClaimAdjudicationsModal(false);
                  setActiveClaimForAdjudications(null);
                }}
                className="px-4 py-2 rounded-lg bg-slate-800 text-slate-300 text-xs cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 13: EOB LINKED ADJUDICATIONS VIEW                                    */}
      {/* ========================================================================= */}
      {showEobAdjudicationsModal && activeEobForViewingAdjudications && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0c121e] border border-slate-700/80 rounded-xl max-w-3xl w-full p-5 space-y-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center space-x-2">
                  <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
                  <span>Claims Adjudicated Under EOB</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Canonical EOB: <strong className="text-slate-200 font-mono">{activeEobForViewingAdjudications.filename}</strong> • Payer: {activeEobForViewingAdjudications.payer_name} • Total: ${Number(activeEobForViewingAdjudications.total_payment_amount || 0).toFixed(2)}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowEobAdjudicationsModal(false);
                  setActiveEobForViewingAdjudications(null);
                }}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingEobAdjudications ? (
              <div className="p-8 text-center text-xs text-slate-400">Loading linked claim adjudications...</div>
            ) : eobAdjudicationsList.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400 space-y-2">
                <FileSpreadsheet className="w-8 h-8 text-slate-500 mx-auto" />
                <p className="font-semibold text-slate-300">No claims have been adjudicated under this EOB yet.</p>
                <button
                  onClick={() => {
                    setShowEobAdjudicationsModal(false);
                    handleOpenAdjudicateModal(activeEobForViewingAdjudications);
                  }}
                  className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold inline-flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Adjudicate a Claim Now</span>
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-slate-300">
                    {eobAdjudicationsList.length} Linked Claim Adjudication{eobAdjudicationsList.length !== 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={() => {
                      setShowEobAdjudicationsModal(false);
                      handleOpenAdjudicateModal(activeEobForViewingAdjudications);
                    }}
                    className="px-2.5 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold flex items-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>+ Adjudicate Another Claim</span>
                  </button>
                </div>

                <div className="divide-y divide-slate-800/80 rounded-lg border border-slate-800 overflow-hidden">
                  {eobAdjudicationsList.map((adj) => (
                    <div key={adj.id} className="p-3 bg-slate-900/40 hover:bg-slate-900/70 transition-colors space-y-2 text-xs">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              adj.adjudication_outcome === 'PAID'
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                : adj.adjudication_outcome === 'DENIED'
                                ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                                : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                            }`}
                          >
                            {adj.adjudication_outcome}
                          </span>
                          <span className="font-semibold text-slate-200">
                            Claim: {adj.claim_number}
                          </span>
                          <span className="text-slate-400">
                            ({adj.first_name} {adj.last_name} • AHCCCS: {adj.ahcccs_id || 'N/A'})
                          </span>
                        </div>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                          TPL: {adj.tpl_evidence_status}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[11px] font-mono">
                        <div>
                          <span className="text-slate-500">Billed: </span>
                          <span className="text-slate-200">${Number(adj.billed_amount || 0).toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Allowed: </span>
                          <span className="text-slate-300">${Number(adj.allowed_amount || 0).toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-emerald-400">Paid: </span>
                          <span className="text-emerald-400 font-bold">${Number(adj.paid_amount || 0).toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-amber-400">Adj: </span>
                          <span className="text-amber-300">${Number(adj.contractual_adjustment || 0).toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-sky-400">Pt Resp: </span>
                          <span className="text-sky-300">${Number(adj.patient_responsibility || 0).toFixed(2)}</span>
                        </div>
                      </div>

                      {adj.denial_reason && (
                        <div className="text-[11px] text-slate-400">
                          Reason: <span className="text-slate-200">{adj.denial_reason}</span>
                          {adj.carc_code && <span className="ml-2 font-mono text-amber-300">CARC: {adj.carc_code}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-3 border-t border-slate-800">
              <button
                onClick={() => {
                  setShowEobAdjudicationsModal(false);
                  setActiveEobForViewingAdjudications(null);
                }}
                className="px-4 py-2 rounded-lg bg-slate-800 text-slate-300 text-xs cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: SECONDARY COB PACKET PREVIEW & VALIDATION (PHASE 2B)               */}
      {/* ========================================================================= */}
      {showCobPreviewModal && (
        cobPreviewData ? (
          <SecondaryCobPreviewModal
            data={cobPreviewData}
            generating={generatingSecondaryServiceId === cobPreviewData.serviceId}
            generateError={secondaryGenerateError?.serviceId === cobPreviewData.serviceId ? secondaryGenerateError.message : null}
            onGenerate={() => handleGenerateDddSecondary(cobPreviewData.serviceId)}
            onClose={() => {
              setShowCobPreviewModal(false);
              setCobPreviewData(null);
            }}
          />
        ) : (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
            <div className="bg-[#0b121f] border border-slate-700/80 rounded-2xl max-w-md w-full p-6 space-y-4 shadow-2xl text-center text-slate-100">
              {loadingCobPreview ? (
                <div className="space-y-3 py-4">
                  <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto" />
                  <div className="text-sm font-semibold text-white">Generating Secondary COB Preview...</div>
                  <div className="text-xs text-slate-400">Verifying primary claim, canonical EOB, and adjudication facts...</div>
                </div>
              ) : cobPreviewError ? (
                <div className="space-y-3 py-2">
                  <AlertCircle className="w-8 h-8 text-rose-400 mx-auto" />
                  <div className="text-sm font-semibold text-rose-300">Preview Generation Error</div>
                  <div className="text-xs text-slate-300 bg-rose-950/40 p-3 rounded-lg border border-rose-500/30">
                    {cobPreviewError}
                  </div>
                  <button
                    onClick={() => {
                      setShowCobPreviewModal(false);
                      setCobPreviewError(null);
                    }}
                    className="px-4 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold cursor-pointer"
                  >
                    Dismiss
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        )
      )}
      {resolveServiceId && (
        <BillingHoldResolvePanel
          serviceId={resolveServiceId}
          userRole={userRole}
          onDismiss={() => setResolveServiceId(null)}
          onResolved={() => {
            setResolveServiceId(null);
            fetchData();
            onRefreshStats();
          }}
          onOpenDocument={(docId) => {
            setResolveServiceId(null);
            onNavigateTab?.('clinical', docId);
          }}
          onOpenClaims={() => {
            setResolveServiceId(null);
            setActiveTab('Claims');
            onNavigateTab?.('billing', 'claims');
          }}
        />
      )}
    </div>
  );
};
